import type { NrcDailyPowerSummary } from '../lib/nrcReactorStatus';

export type RagStatus = 'red' | 'amber' | 'green';

/**
 * Report-level headline status. Adds `withheld` to the three RAG colours for
 * the producers that can honestly need it: a Decision Guide dossier built from
 * a WITHHELD rollup, and a financial report whose deterministic engine declined
 * to band. Before this member existed (P1.4, 2026-09-05) every such producer
 * had to pick a false colour, and each picked a different one — amber on the
 * dossier's status, "Do Not Proceed" on its recommendation, red in the
 * conclusion callout — and `fromReport` then laundered the amber back into a
 * `conditional` semantic band, so a withheld verdict round-tripped as an
 * asserted CONDITIONAL. Category-level statuses stay `RagStatus`; the model
 * never emits `withheld`. Render it neutral — never as a colour.
 */
export type ReportOverallStatus = RagStatus | 'withheld';

/**
 * The headline recommendation enum. `Decision Pending` (P1.4) is the honest
 * value for a report assembled from a WITHHELD decision (an evidence hold or an
 * undeclared power strategy). It is not a directive: consumers render it
 * neutral, count it as neither a proceed nor a decline, and never infer a
 * band from it. It matches the rollup's own "Decision pending" label and
 * `DECISION_ANALYSIS_BAND_LABEL.withheld`.
 */
export type ConclusionRecommendation =
  | 'Proceed'
  | 'Proceed with Conditions'
  | 'Do Not Proceed'
  | 'Decision Pending';

export interface Location {
  lat: number;
  lng: number;
  address: string;
}

export interface RagMetric {
  id: string;
  label: string;
  status: RagStatus;
  score: number;
  value: string | number;
  unit?: string;
  explanation: string;
  /**
   * Site Score Movement SSM-3 — provenance of the SED slot the metric
   * read from. Mirrors `EvidenceSection.provenance` (RDX-1 widened the
   * mirror with the three `'report-*'` members). Optional for
   * back-compat — pre-SSM stamps leave it undefined; the Score
   * Provenance Panel (SSM-6) treats undefined as `'provider'`.
   */
  provenance?:
    | 'provider'
    | 'doc'
    | 'provider+doc'
    | 'doc-override'
    | 'report-derived'
    | 'report+provider'
    | 'report-overrides-provider'
    | 'auto-fill';
  /**
   * SSM-3 — doc ids whose extracted values were merged into this
   * metric (one entry per `'doc-override'` or `'provider+doc'` slot).
   * Drives the Score Provenance Panel's "Contributing docs" list.
   */
  contributingDocIds?: string[];
  /**
   * SSM-3 — provider value at gen-time (before any doc override).
   * Surfaced in the Score Provenance Panel as the "Provider value"
   * row alongside the doc-derived value. Optional / pre-SSM absent.
   */
  providerValue?: unknown;
  /**
   * SSM-3 — doc-derived value (only present when a doc influenced
   * this metric). The panel renders `provider → doc` side-by-side.
   */
  docValue?: unknown;
  /**
   * REG-SCORE #1 (scoring-engine audit, 2026-07-02) — true when the metric's
   * evidence is GENUINELY NOT SPECIFIED (e.g. no zoning designation anywhere
   * in the evidence). The scorer EXCLUDES missing metrics from the category
   * weighted mean (missing ≠ bad — never the silent categorical-default red)
   * and lists them as evidence gaps. `score`/`status` are stamped 50/'amber'
   * for display neutrality only; consumers must treat a `missing` metric as
   * "no evidence", not as a scored 50. Optional for back-compat — metrics
   * stamped before REG-SCORE leave it undefined (= scored normally).
   */
  missing?: boolean;
}

export interface RagCategory {
  id: string;
  label: string;
  status: RagStatus;
  score: number;
  weight: number;
  metrics: RagMetric[];
  explanation: string;
  /**
   * Site Score Movement SSM-3 — aggregated doc influence at the
   * category level. `'doc-override'` when at least one metric in this
   * category had a `'doc-override'` slot; `'provider+doc'` when at
   * least one had `'provider+doc'` but none had `'doc-override'`;
   * `'provider'` otherwise. Pre-SSM absent. Surfaces as a small chip
   * next to the category label in the Score Provenance Panel.
   *
   * RDX-1 mirrors the widened `EvidenceSection.provenance` union onto
   * this field so a future Wave-2 aggregator can lift a `'report-*'`
   * slot up to the category-level chip. Wave-1 never writes the new
   * members here.
   */
  provenance?:
    | 'provider'
    | 'doc'
    | 'provider+doc'
    | 'doc-override'
    | 'report-derived'
    | 'report+provider'
    | 'report-overrides-provider'
    | 'auto-fill';
  /**
   * SSM-3 — union of doc ids that influenced any metric in this
   * category. Drives the "X documents contributed to this category"
   * tooltip.
   */
  contributingDocIds?: string[];
}

export interface TimeSeriesData {
  month: string;
  avgTemp: number;
  peakTemp: number;
  minTemp?: number;
  wetBulbTemp: number;
  humidity?: number;
  precipitation?: number;
  coolingEfficiency: number;
}

/**
 * The pillar-routing category of a structured risk row.
 *
 * `RISK_CATEGORY_TO_PILLAR` (pillarRegistry.ts) maps each member to exactly ONE
 * decision pillar, so a finding moves one axis of the composite rather than
 * all ten. `water` was added on 2026-09-05 (P1.5): without it a disqualifying
 * water finding could only be filed as `environmental` or `regulatory`, and the
 * 0.10-weight water pillar could hold a perfect score beside it. (A risk on a
 * single-pillar report — the water report feeds only `water` — already reached
 * its pillar by construction via `soleDecisionPillarForReportType`; the
 * category makes the attribution explicit and lets a multi-pillar report say
 * "water" rather than borrow a neighbouring axis.)
 *
 * Additive: persisted rows carrying the four legacy members are untouched.
 */
export type RiskCategory =
  | 'regulatory'
  | 'environmental'
  | 'infrastructure'
  | 'execution'
  | 'water';

export interface RiskItem {
  id: string;
  category: RiskCategory;
  label: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  impact: string;
  mitigation: string;
  /**
   * P3-7 — risk provenance. All optional / additive (legacy risks omit them,
   * so existing risk payloads are byte-stable). Lets the UI explain WHY a
   * deterministic risk fired and trace it to its source:
   *   `triggeringValue` — the concrete input that crossed the threshold
   *                        (e.g. "firm-capacity worst verdict: infeasible",
   *                        "water rights: prior-appropriation, junior").
   *   `source`          — where the triggering value came from
   *                        ('intent' | 'scoring' | 'evidence' | 'provider').
   *   `contributingDocIds` — SED doc ids that informed the trigger.
   */
  triggeringValue?: string;
  source?: 'intent' | 'scoring' | 'evidence' | 'provider';
  contributingDocIds?: string[];
}

export interface Recommendation {
  type: 'power' | 'cooling' | 'site';
  title: string;
  description: string;
  rationale: string;
  architecture?: string; // e.g. "SMR + Thermal Storage"
  phase?: string;
  priority?: string;
  owner?: string;
  proposedResponsibleRole?: string;
  deliverable?: string;
  dependency?: string;
  decisionUnlocked?: string;
  status?: string;
}

export interface BtmOption {
  technology: string;
  riskLevel: 'Low' | 'Medium' | 'High';
  impactLevel: 'Green' | 'Amber' | 'Red';
  reason: string;
  action: string;
}

export interface DetailedRisk {
  category: string;
  riskLevel: 'Low' | 'Medium' | 'High';
  impactLevel: 'Green' | 'Amber' | 'Red';
  reason: string;
  action: string;
}

/**
 * OPTIONAL structured equipment-vs-COD gate for the BTM long-lead items —
 * lead time vs the in-service date with slip risk. Mirrors the Gemini
 * response schema (reportPrompts.ts:4403); ALL sub-fields optional + absent-
 * safe because the schema does NOT add `longLeadVsCod` (or any sub-field) to
 * `supplyChainAnalysis.required` — the model omits the whole object when no
 * long-lead BTM generation equipment is in scope.
 */
export interface LongLeadVsCod {
  /** Target in-service date (COD) the lead times are measured against. */
  codDate?: string;
  /** Per-item long-lead-vs-COD rows for the binding BTM equipment. */
  items?: {
    item?: string;
    leadTimeMonths?: string;
    orderByDate?: string;
    slackVsCod?: string;
    slipRisk?: string;
  }[];
  /** Binding-constraint item + recommended slot-reservation action. */
  bindingConstraint?: string;
}

/**
 * OPTIONAL consumptive-use / withdrawal PERMITTING gate — treats the right to
 * consumptively use / withdraw water as a binding development gate (especially
 * in prior-appropriation states: CO / AZ / TX). Mirrors the Gemini response
 * schema (reportPrompts.ts:4524); ALL sub-fields optional + absent-safe — the
 * schema does NOT add `consumptiveUse` (or any sub-field) to
 * `waterAnalysis.required`. The model omits the whole object when not
 * applicable. The four narrative gate fields render in WaterRightsSection;
 * `dryCoolingPueTradeoff` renders with cooling (CoolingByTechSection).
 */
export interface ConsumptiveUseGate {
  doctrineGate?: string;
  withdrawalPermit?: string;
  consumptiveVolume?: string;
  seniorRightCurtailment?: string;
  dryCoolingPueTradeoff?: string;
}

/** Supply Chain Risk v2 — section-aligned LLM narratives. */
export interface SupplyChainAnalysis {
  procurementOverview: string;
  criticalPathAssessment: string;
  supplyChainRiskSynthesis: string;
  supplierStrategy: string;
  quoteComparisonInsights: string;
  /**
   * OPTIONAL structured long-lead-equipment-vs-COD gate (the prompt also folds
   * this content into `criticalPathAssessment`). Rendered by
   * CriticalPathProcurementSection below the deterministic critical-path
   * register. Optional + absent-safe (not in the schema `required` set).
   */
  longLeadVsCod?: LongLeadVsCod;
}

/** Data source reference — tracks provenance of each data point. */
export interface DataSource {
  /** A synthetic source is attributed scenario input, never independent verification. */
  demonstrationDisclosure?: import('./demonstration').DemonstrationDisclosureV1;
  /** Optional explicit source identifier for direct ID binding. */
  id?: string;
  /** Optional explicit siteId binding for the reference row. */
  siteId?: string;
  field: string;       // Which report field this data supports
  source: string;      // Human-readable source name (e.g. "Google Maps Elevation API")
  url?: string;        // Source URL if available
  retrievedAt: string; // ISO timestamp
  /** Audited authority grade frozen from the SED section at report generation. */
  sourceGrade?: import('./siteEvidence').EvidenceSourceGrade;
  /** Underlying source vintage/effective period, distinct from retrieval time. */
  dataAsOf?: string;
  /** Exact evidence qualification; screening/estimated are never promoted to verified. */
  qualification?: import('./siteEvidence').EvidenceQualification;
  /** Optional explicit verification status ('verified' | 'verified-from-doc' | 'unavailable' | ...). */
  status?: string;
  /** Optional explicit availability / state / review / coverage status flags. */
  available?: boolean;
  missing?: boolean;
  state?: string;
  reviewState?: string;
  coverageStatus?: string;
  /** Optional descriptive / caveat / excerpt fields inspected by fail-closed negation & proxy guards. */
  notes?: string;
  summary?: string;
  description?: string;
  excerpt?: string;
  excerptPreview?: string;
  sourceQuote?: string;
  rationale?: string;
  evidenceQuotes?: string[];
  /** Optional stated provenance / project scope attribution for the source row. */
  attribution?: import('./siteEvidence').EvidenceAttribution;
  /** Exact professional document behind this row, when applicable. */
  documentId?: string;
  /** Optional page lineage for document-derived references. */
  pageNumber?: number;
  /** Optional structured topographic slope measurement/derivation support. */
  topographicSupport?: import('./siteEvidence').TopographicSurveySupportV1;
  /**
   * Optional sub-items for composite rows. Used by Content-Source
   * Fixes Prompt 7 (siteResearch) to collapse the previous 5-row
   * expansion into a single appendix row whose detail panel shows
   * one bullet per topic. Appendix renderer expands these as a
   * disclosure below the row's main label.
   */
  subItems?: { topic: string; detail: string }[];
  /**
   * Whether the rendered report body actually cites this row.
   *
   * Set during the post-LLM citation pass (Content-Source Fixes Prompt 4).
   * The appendix renders cited rows expanded and uncited rows under a
   * collapsed "Additional sources consulted" disclosure, instead of
   * shrinking the array (which used to make inline [Ref N] tokens point
   * past the end). Undefined on legacy SEDs / pre-Prompt-4 reports —
   * the renderer treats undefined as "cited" to preserve back-compat.
   *
   * Provider refs (indices below knowledgeRefStartIdx) are always
   * cited regardless of body content.
   */
  citedInThisReport?: boolean;
  /**
   * R-66 — set when the citation integrity gate could not establish
   * verifiable provenance (no https URL and no tier-1/SED source match)
   * for this reference, even after the optional regenerate-once retry.
   *
   * The gate gracefully degrades rather than hard-blocking: the inline
   * `[Ref N]` token in the body is rewritten to the `[unverified]`
   * reliability marker (rendered as a rose "unverified" badge), the
   * claim text is preserved, and this flag tells the appendix renderer
   * to surface the row under a "needs verification" treatment instead of
   * presenting it as a backed citation. Never used to fabricate a source.
   */
  needsVerification?: boolean;
}

export interface SiteDiligenceEvidenceBlock {
  id: 'topography' | 'geotechnical' | 'survey' | 'mineralResources' | 'culturalResources' | 'siteResources' | 'campusResourceAdvisory';
  label: string;
  qualification: import('./siteEvidence').EvidenceQualification;
  facts: string[];
  caveats: string[];
  sources: DataSource[];
}

/** Deterministic, persisted report snapshot; exporters never depend on LLM inclusion. */
export interface SiteDiligenceReportEvidence {
  blocks: SiteDiligenceEvidenceBlock[];
  /** Public-safe frozen view; original source witnesses remain in the SED. */
  resourceStatements?: import('./siteResourceEvidence').SiteResourceView;
  /** Immutable safe comparison; no live/read-time capacity calculation. */
  campusResourceAdvisory?: import('../lib/findings/campusResourceAdvisory').CampusResourceAdvisoryProjection;
}

/** Per-field confidence level — tracks whether data is from an authoritative source. */
export type ConfidenceLevel = 'verified' | 'ai-generated' | 'user-edited' | 'estimated';

export interface FieldConfidence {
  field: string;
  confidence: ConfidenceLevel;
  source: string; // e.g. "Open-Meteo API", "Datacrate AI (Gemini)", "Hardcoded template", "User edit"
}

/** Post-generation sanity check result. */
export interface SanityCheck {
  field: string;
  passed: boolean;
  message: string;
  severity: 'info' | 'warning' | 'error';
}

/** Per-Gemini-call telemetry row. One row per attempt (success or failure). */
export interface CallTelemetry {
  stage: 'fetchSiteData' | 'analyzeEvidence' | 'generateReportContent' | 'reviewReport' | 'finalSanityCheck' | 'rerunSection' | 'quickSummarize' | 'generateEnvironmentalReportContent' | 'generatePowerReportContent' | 'generateWaterReportContent' | 'generateTestFitContent' | 'generateInterconnectionContent' | 'generateSupplyChainReportContent' | 'generatePublicSentimentReportContent' | 'generateCustomBriefReportContent' | 'generateExecutiveSummaryReportContent' | 'generateEditorialNarrative' | 'generateLayoutPlan';
  model: string;
  promptTokens: number;
  /** Visible response tokens (`candidatesTokenCount`); EXCLUDES thinking. */
  completionTokens: number;
  /**
   * Thinking tokens (`thoughtsTokenCount`), billed at the output rate and
   * included in estimatedCostUsd. Undefined on calls recorded before
   * 2026-09-30, when thinking was neither read nor priced.
   */
  thoughtsTokens?: number;
  totalTokens: number;
  latencyMs: number;
  success: boolean;
  attempt: number;
  /** Whether this call used the user's personal Gemini key ('user') or the
   *  org Secret Manager key ('org'). Undefined for legacy reports recorded
   *  before personal-key support shipped. */
  keySource?: 'user' | 'org';
  /**
   * Correlation id shared by every attempt of ONE logical Gemini call (all
   * withRetry attempts of it), unique within the generating process. Lets
   * deriveModelFallbacks pair a failed 3.x attempt with the 2.5 success of the
   * SAME call even when calls sharing a stage overlap. Absent on rows recorded
   * before it shipped; those pair by stage, as before.
   */
  callId?: string;
  /** Optional editorial retry attribution; absent on legacy/noneditorial rows. */
  failureReason?: 'capacity' | 'timeout';
}

/** Aggregated AI cost telemetry attached to each generated report. */
export interface GenerationTelemetry {
  calls: CallTelemetry[];
  totalPromptTokens: number;
  /** Sum of visible response tokens; EXCLUDES thinking (see totalThoughtsTokens). */
  totalCompletionTokens: number;
  /**
   * Sum of thinking tokens. Billed output = totalCompletionTokens +
   * totalThoughtsTokens. Undefined on reports generated before 2026-09-30,
   * whose estimatedCostUsd did not include thinking.
   */
  totalThoughtsTokens?: number;
  totalTokens: number;
  totalLatencyMs: number;
  /** Per-call cost on the published tiered rates, thinking billed as output. */
  estimatedCostUsd: number;
  modelMix: Record<string, number>;
  promptVersion: string;
  /** How many recorded Gemini calls used 'user' vs 'org' keys.
   *  Undefined for reports generated before personal-key support shipped. */
  keySourceMix?: { user: number; org: number; unknown: number };
  /**
   * Calls a gemini-3.x configuration served from a non-3.x model (withRetry
   * capacity fallback, 404 chain). Present only when at least one happened.
   */
  modelFallbacks?: import('../config/geminiCapacityFallback').ModelFallbackSummary;
  /**
   * Stage-2 narrative retry telemetry. Reports generated before the
   * retry-on-empty path shipped leave these undefined; new reports
   * populate them per generation. Drives the post-generation toast
   * branch (success / info / error) and the per-report-type
   * first-attempt failure dashboard.
   */
  attempts?: number;
  narrativeRetried?: boolean;
  finalAttemptEmpty?: boolean;
  /** Classified Stage-2 failure kind for admin dashboards + toast routing. */
  narrativeFailureKind?: import('../lib/classifyNarrativeFailure').NarrativeFailureKind;
  /** Typed Stage-1 evidence-analysis outcome (F2). */
  evidenceAnalysisStage?: import('../lib/reportGeneration/evidenceAnalysisRetry').EvidenceAnalysisStageOutcome;
}

/** Convergence trace for the Phase 5 review loop. Lets us see whether the
 *  loop hit the cap, exited early on score, or plateaued. */
export interface ReviewTrace {
  iterationsRun: number;
  iterationsCappedAt: number;
  finalScore: number;
  scoresPerIteration: number[];
  earlyExited: boolean;
  /** Review stopped with time reserved for the unchanged final correctness gates. */
  deadlineReserved?: boolean;
  /** Set when a non-final iteration had the highest score. */
  bestOfSelected?: boolean;
  /** 1-based iteration index returned to callers (when bestOfSelected). */
  selectedIteration?: number;
}

/** Deterministic audit of [Ref N] usage across the narrative. Populated in Phase 4b. */
export interface CitationAudit {
  totalRefs: number;
  totalCitations: number;
  uniqueCitations: number;
  outOfBoundsCount: number;
  orphanedCount: number;
  orphanedRefs: number[];
}

/**
 * Report P4 / SED D0.4 — per-report SED ingestion-completeness flag.
 * Stamped onto the report from the upstream relevance-ranked SED excerpt
 * so a reader can tell whether the generator saw the WHOLE SED or only the
 * highest-relevance subset that fit the prompt budget.
 */
export interface SedIngestionInfo {
  /** True ⇒ the entire SED reached the generator (no-op for small SEDs);
   *  false ⇒ relevance-ranking dropped the named lower-priority sections. */
  fitsWhole: boolean;
  /** Section titles dropped to fit the budget (document order). Empty when
   *  `fitsWhole`. Names ONLY genuine SED `##` sections. */
  omittedTitles: string[];
  /** `omittedTitles.length` — convenience for the panel / telemetry. */
  droppedSectionCount: number;
  /** Raw-SED chars NOT carried into the prompt (UTF-16 .length, non-ASCII
   *  safe). Approximate floor: excludes the appended omitted-index line. */
  droppedChars: number;
  /** The char budget the SED body was ranked against (50k / exec 30k). */
  budget: number;
}

/**
 * The directive a user attaches when they resolve a sanity finding as
 * `apply`. It is the exact text + evidence that the next rerun's
 * narrative builders are instructed to incorporate into the body —
 * snapshotted at resolution time so a later edit to the finding can't
 * silently change what gets applied. `section` scopes which report
 * section the rewrite targets.
 */
export interface ApplyDirective {
  /** The suggestion text to apply — the user's edit if they edited it,
   *  otherwise the AI's original `suggestion`. */
  suggestionAtResolution: string;
  /** The evidence the AI cited for the finding, carried forward so the
   *  narrative builder can ground the rewrite. */
  evidenceAtResolution?: string;
  /** The report section the directive targets (e.g. "Grid Study"). */
  section?: string;
  /** Optional directive scope / target aliases used by structured correction directives. */
  scope?: string;
  targetSection?: string;
  userInstruction?: string;
  anchorFactPaths?: string[];
  findingCategory?: string;
}

/** AI final sanity check finding — user-actionable. */
export interface SanityFinding {
  id: string;
  category: 'inconsistency' | 'false-positive' | 'misleading' | 'unverified' | 'document-conflict' | 'risk-verified' | 'strength-verified' | 'citations';
  section: string;              // Which report section
  finding: string;              // What the AI found
  suggestion: string;           // How to fix or clarify
  severity: 'high' | 'medium' | 'low';
  verificationStatus?: 'confirmed' | 'partially-confirmed' | 'contradicted' | 'not-applicable';
  evidence?: string;            // SED data points supporting/contradicting the finding
  /**
   * Optional traceable source/SED field values backing this finding's evidence
   * (e.g. `{ 'power.phase1Mw': 706, 'power.phase2AddMw': 290 }`). Used to
   * detect genuine source/field changes rather than free-text prose wording
   * differences when evaluating whether new evidence supersedes a prior
   * approved factual correction.
   */
  evidenceSourceFields?: Record<string, string | number | boolean | null>;
  /** Optional traceable source document / SED section identifiers. */
  evidenceSourceIds?: string[];
  /** Optional traceable source/SED revision identifier. */
  evidenceSourceRevision?: string | number;
  /**
   * Explicit conflict decision recorded when traceable source/field changes
   * contradict a previously approved factual correction. Free-text prose
   * inequality in `evidence` never supersedes a correction on its own.
   */
  evidenceConflictDecision?: {
    conflictsWithPriorCorrection: boolean;
    reason?: string;
    changedFields?: string[];
    sourceIds?: string[];
  };
  /**
   * Two-state resolution (Section D — Apply-on-Rerun pack):
   *   - `'apply'`  → queue the suggestion (or the user's edit of it) as a
   *     directive the next rerun's narrative builders incorporate.
   *   - `'ignore'` → acknowledge; suppress re-flagging on the next rerun.
   * Legacy reports persisted `'approved' | 'changed' | 'discarded'`;
   * `migrateSanityResolution` collapses those on first load.
   */
  resolution?: 'apply' | 'ignore';
  /** The edited suggestion text when the user chose "Edit suggestion"
   *  before applying. Empty/undefined means they applied the original. */
  userNote?: string;
  /** Snapshot of what to apply — populated only when `resolution === 'apply'`. */
  applyDirective?: ApplyDirective;
  resolvedAt?: string;
  resolvedByUid?: string;
  source?: string;
  /**
   * Rerun lineage populated when a prior report's `resolution === 'apply'`
   * directive was consumed by this rerun and the finding re-fired during
   * post-generation audit reevaluation. Instead of leaving `resolution: 'apply'`
   * (`Queued` / "Will apply on rerun"), the finding stays unresolved/active
   * (`resolution: undefined`) while carrying the applied directive and
   * remaining failure reason.
   */
  correctionLineage?: {
    status: 'applied-still-failing' | 'applied-resolved' | 'superseded-by-evidence' | 'withdrawn';
    originReportId?: string;
    appliedDirective?: string;
    userNote?: string;
    appliedAt: string;
    fromReportId?: string;
    remainingReason?: string;
    supersededByEvidence?: string;
    changedSourceFields?: string[];
    conflictReason?: string;
  };
  /**
   * Recorded when an authoritative or confirmed historical finding states an
   * earlier verdict (e.g. 'Conditional Go') that conflicts with the report's
   * canonical `conclusion.recommendation` (e.g. 'Do Not Proceed'). Preserves
   * `finding`, `evidence`, `suggestion`, `verificationStatus`, and `category`
   * verbatim while flagging the conflict and providing a non-destructive
   * display-reconciliation note.
   */
  verdictConflict?: {
    status: 'conflict-with-canonical-conclusion';
    historicalStatedVerdict: string;
    canonicalConclusionVerdict: string;
    authoritativeDecisionPreserved: true;
    reconciledDisplaySummary: string;
    conflictNote: string;
  };
}

/** Record of a user edit correction on a report. */
export interface EditRecord {
  id: string;
  reportId: string;
  field: string;          // Field path that was edited
  originalValue: string;  // JSON-serialized original
  editedValue: string;    // JSON-serialized edited
  editedAt: string;       // ISO timestamp
  address: string;        // Geographic context for similarity matching
  useCase: string;        // Use case profile for context
}

/** Typed dataset structure — no more `any`. */
export interface ClimateData {
  avgTemp: number;        // °C annual average
  peakTemp: number;       // °C peak summer
  avgWetBulb: number;     // °C average wet bulb
  waterStress: 'Low' | 'Moderate' | 'High';
  humidity: number;       // % annual average RH
}

export interface GridData {
  substationDist: number; // km to nearest substation
  capacity: 'High' | 'Limited' | 'Constrained';
  complexity: 'Low' | 'Medium' | 'High';
  voltage: string;        // e.g. "115kV"
  // Extended grid study data
  nearestSubstationName?: string;
  substationsWithin50km?: number;
  transmissionLines?: { voltage: string; distKm: number; owner?: string }[];
  nearestTransmissionKm?: number;
  nearestTransmissionVoltage?: string;
  estimatedInterconnectionCost?: string; // e.g. "$2-5M"
  estimatedInterconnectionCostBasis?: import('./siteEvidence').GridEvidence['estimatedInterconnectionCostBasis'];
  estimatedQueueTime?: string;           // e.g. "18-36 months"
  estimatedQueueTimeBasis?: import('./siteEvidence').QueueDurationBasis;
  isoRegion?: string;                    // e.g. "MISO", "ERCOT", "PJM"
}

/** Grid study section in the report — AI-generated analysis. */
export interface GridStudySection {
  interconnectionAssessment: string;     // Overall assessment narrative
  transmissionAccess: string;            // Transmission line access analysis
  capacityAnalysis: string;              // Available capacity assessment
  costEstimate: string;                  // Cost estimate for interconnection
  timelineEstimate: string;              // Expected timeline
  recommendations: string[];             // Specific next steps
  risks: import('../lib/findingClaim').FindingClaimEntry[]; // Key grid-related risks/open questions
}

/** Transport & logistics study section. */
export interface TransportStudySection {
  overview: string;                      // Overall transport assessment
  roadAccess: string;                    // Road access analysis (interstate, highway, load-bearing)
  railAccess: string;                    // Rail infrastructure analysis
  portAccess: string;                    // Port/waterway access
  heavyHaulRoute: string;               // Equipment delivery feasibility
  recommendations: string[];
  risks: import('../lib/findingClaim').FindingClaimEntry[];
}

/** Connectivity & fiber study section. */
export interface ConnectivityStudySection {
  overview: string;                      // Overall connectivity assessment
  fiberInfrastructure: string;           // Fiber density, dark fiber routes
  internetExchange: string;              // IX proximity, peering options
  datacenters: string;                   // Nearby DC facilities, cloud on-ramps
  latencyAnalysis: string;               // Network latency to major markets
  recommendations: string[];
  risks: import('../lib/findingClaim').FindingClaimEntry[];
}

export interface LandData {
  /** Gross geometry/parcel area, never usable area or proof the proposed
   * development fits. Zero denotes unavailable in legacy reconstruction. */
  acres: number;
  elevation: number;      // m above sea level (mean across the parcel when available)
  /** Optional min/max elevation across the user-drawn boundary. Populated
   *  by the SED-from-siteProfile reconstruction path; absent when only a
   *  single-point elevation is known (legacy reports + fetchElevation
   *  fallback). */
  elevationRange?: { min: number; max: number };
  slope: number;          // % grade
  floodRisk: 'Low' | 'Low-Medium' | 'Medium' | 'High' | 'Unknown';
  floodCoverage?: import('./floodScreening').FloodScreeningCoverage;
}

export interface InfrastructureData {
  /** Unknown means fiber density is unverified. DC facility counts alone
   * cannot establish fiber density, including when the count is zero. */
  fiberDensity: 'High' | 'Medium' | 'Low' | 'Unknown';
  /** Geographic IX latency heuristic in ms, not measured route latency.
   * Unknown is NaN in the legacy in-memory SED reconstruction and null after
   * JSON persistence. Neither denotes a zero-latency observation. */
  latency: number | null;
  roadAccess: 'Interstate' | 'Highway' | 'Local';
}

export interface RegulatoryData {
  nuclearStance: 'Favorable' | 'Neutral' | 'Restrictive';
  /** Missing means zoning was not established; never infer a Mixed designation. */
  zoning?: 'Industrial' | 'Mixed' | 'Residential' | 'Undeveloped';
  envConstraints: 'Low' | 'Moderate' | 'High';
}

export interface SeismicData {
  pga: number;
  sds: number;
  sd1: number;
  designCategory: string;
  riskCategory: string;
}

export interface SolarData {
  ghi: number;
  dni: number;
}

export interface EnvironmentalData {
  protectedAreas?: import('./siteEvidence').EnvironmentalProtectedAreas;
  /** Mapped final OR proposed habitat; undefined means the source screen is incomplete. */
  hasCriticalHabitat?: boolean;
  criticalHabitatFindings?: import('./refdata').CriticalHabitatFinding[];
  criticalHabitatCoverage?: import('./refdata').CriticalHabitatCoverage;
  species: string[];
  hasWetlands?: boolean;
  wetlandTypes: string[];
  wetlandAcreage?: number;
  wetlandAcreageBasis?: 'source-feature-total';
  wetlandSourceAcres?: Record<string, number>;
  wetlandSourceAcreageConflicts?: import('./refdata').NwiSourceAcreageConflict[];
  wetlandAcreageIssues?: import('./refdata').NwiAcreageIssue[];
  wetlandMappingCoverage?: import('./refdata').NwiMappingCoverage;
}

export interface UserApiKeys {
  geminiKey?: string;
  nrelKey?: string;
  eiaKey?: string;
  airnowKey?: string;
  /** Regrid (paid). Required when VITE_REGRID_ENABLED is true and the
   *  user wants Regrid-sourced parcel data. */
  regridApiKey?: string;
  // Legacy fields — kept so old localStorage / Firestore docs still type-check.
  nrel?: string;
  eia?: string;
  airnow?: string;
  [key: string]: string | undefined;
}

export interface WindData {
  windSpeed100m: number;
  windSpeed80m: number;
  capacityFactor: number;
}

export interface ElectricityData {
  retailRate: number;
  state: string;
  utility?: string;
  year?: number;
  /** EIA state averages are screening context, not applicable utility tariffs. */
  basis?: 'state-industrial-average';
  sector?: 'IND';
  units?: 'cents per kilowatt-hour';
}

export interface AirQualityData {
  aqi: number;
  category: string;
  primaryPollutant: string;
  pm25?: number;
}

export interface ConnectivityData {
  nearestIxName: string;
  nearestIxCity: string;
  nearestIxDistKm: number;
  ixCount50km: number;
  facilityCount50km: number;
  /**
   * CONNECTIVITY-ADVISORY — what the `nearestIx*` fields actually describe.
   * PeeringDB's IX list can fail independently of its facility list, and the
   * provider then falls back to the nearest COLO FACILITY
   * (`peeringDb.ts` — the `usedIx` branch). Before this field the fallback was
   * disclosed only in the human-readable source string, so the payload
   * presented a colo as if it were the IX. 'colo-fallback' readings are
   * display-only: the scorer treats them as missing data, never as an IX
   * distance. `undefined` on legacy payloads ⇒ treated as 'ixp'
   * (pre-existing behavior; only explicitly-labeled fallbacks are excluded).
   */
  nearestIxKind?: 'ixp' | 'colo-fallback';
}

export interface ParcelData {
  parcelId: string;
  address: string;
  owner: string;
  acreage: number;
  zoning: string;
  landUse: string;
  assessedValue?: number;
}

/** Project intent questionnaire — drives database recommendations. */
/** User-uploaded document for site analysis context. */
/**
 * P3 — one topical segment of a document's body, from the content index
 * (`segmentDocumentTopics`). Offsets are absolute into the body.
 */
export interface DocumentTopicSegment {
  id: string;
  charStart: number;
  charEnd: number;
  /** Page the segment starts on, when the page map knows it. */
  page?: number;
  topic: string;
  /** Keeps the concrete figures, units, dates and names of the passage. */
  summary: string;
  sedSections: import('./siteEvidence').SedSectionId[];
  requirementIds: string[];
  confidence: number;
  /** Source-quoted facts kept even when no dedicated SED scalar exists. */
  facts?: { label: string; value: string; unit?: string; sourceQuote: string; charStart: number; charEnd: number }[];
  /** Literal source support for project scope; never an approval or verification stamp. */
  projectScopeEvidence?: { sourceQuote: string; charStart: number; charEnd: number };
  /** Attribution stated in the text (seed for provenance-carrying facts, P4). */
  attribution?: {
    statedBy?: string | null;
    statedByRole?: string | null;
    organisation?: string | null;
    statedOn?: string | null;
    appliesToProject?: 'this-site' | 'other-project' | 'unknown';
  };
}

/** P3 — compact union of the content index, kept on the site record for routing. */
export interface DocumentTopics {
  /** Source-bound receipts permit retrying failed windows without repeating successful calls. */
  coverage?: { bodyHash: string; windows: { start: number; end: number; status: 'complete' | 'failed' }[]; segmentsTruncated?: boolean };
  sedSections: import('./siteEvidence').SedSectionId[];
  requirementIds: string[];
  topics: string[];
  segmentCount: number;
  segmentedAt: string;
  version: string;
}

export interface DocumentVisualReading {
  /** Verbatim readable text from scanned forms/pages, separate from screening interpretation. */
  transcription?: string;
  transcriptionIncomplete?: boolean;
  kind: 'firm-panel' | 'map' | 'utility-diagram' | 'document-image' | 'site-photo' | 'other';
  confidence: number;
  narrative: string;
  legend: string[];
  features: string[];
  siteRelation: string;
  missingMetadata: string[];
  page?: number;
  channel: 'vision';
}

export interface SiteDocument {
  id: string;
  /** Stored extracted-source hash, separate from original-file `contentHash`. */
  demonstrationProvenance?: import('./demonstration').DemonstrationDocumentProvenanceV1;
  fileName: string;
  visualReadings?: DocumentVisualReading[];
  visualReadingCount?: number;
  pdfCoverage?: { totalPages: number; textPagesRead: number };
  visualCoverage?: { candidatePages: number[]; attemptedPages: number[]; unreadPages: number[]; limit: number };
  evidenceDigest?: import('../lib/sed/documentEvidenceDigest').DocumentEvidenceDigest;
  /** Email attachment lineage; a child is an independently citable document. */
  parentDocId?: string;
  attachedFromEmail?: { subject?: string; date?: string; from?: string; fileName: string };
  /** Visible attachment outcomes so unsupported/failed parts are never silently lost. */
  emailAttachments?: { fileName: string; docId?: string; status: 'ingested' | 'skipped' | 'failed'; reason?: string }[];
  mimeType: string;
  sizeBytes: number;
  summary: string;           // AI-extracted summary
  tags: string[];            // Auto-extracted topic tags
  textContent: string;       // Extracted text (truncated to 10K chars)
  /** R-131-RAG-B — full extracted body for chunking (not embedded in site entity). */
  fullExtractedText?: string;
  /**
   * R-70 — page-level char offsets into `textContent` (PDF uploads).
   * Back-compat: absent ⇒ treat as a single page spanning full textContent.
   */
  pages?: { page: number; charStart: number; charEnd: number; text: string }[];
  /**
   * Total characters the extractor produced BEFORE the
   * `EXTRACTED_TEXT_MAX_CHARS` cap (src/lib/docExtractionLimits.ts) was
   * applied. Equals `textContent.length` when nothing was cut. Absent on
   * uploads that predate the stamp.
   */
  extractedCharsTotal?: number;
  /**
   * Characters of the body actually captured (`fullExtractedText.length`).
   * Absent on uploads made before P1.2, where only the 10k preview survived.
   */
  extractedCharsKept?: number;
  /**
   * True when text was lost: before P1.2 the 10k preview was all that was
   * kept; since P1.2 only a document longer than
   * `EXTRACTED_TEXT_HARD_MAX_CHARS` is cut. Drives the Documents tab
   * TRUNCATED chip. Omitted (never `false`) when nothing was cut.
   */
  truncated?: boolean;
  /**
   * P3 — content index of the whole body. Lives in the GCS JSON and in the
   * upload session; stripped from the site record (see `topics`).
   */
  topicSegments?: DocumentTopicSegment[];
  /** P3 — compact union of the content index; drives routing and matching. */
  topics?: DocumentTopics;
  uploadedAt: string;        // ISO timestamp
  /** Extraction/content refresh time; keeps SED cache freshness independent of original upload. */
  updatedAt?: string;
  storagePath?: string;      // Firebase Storage path (if stored)
  /**
   * GCS path to the raw uploaded bytes — distinct from `storagePath`
   * which holds the JSON metadata. Drives the Documents tab Download
   * button (and a future inline viewer). Optional: pre-feature
   * uploads have no raw path; the Download button stays disabled
   * for those rows with a "re-upload to enable download" tooltip.
   */
  rawStoragePath?: string;
  /** True only when the original file bytes were durably stored and verified (byte size + SHA-256). */
  rawBytesVerified?: boolean;
  /** Verified byte size of the durably stored original file. */
  rawStorageSizeBytes?: number;
  /** Verified SHA-256 hex digest of the durably stored original file. */
  rawStorageSha256?: string;
  /**
   * Optional enrichment lifecycle status. When optional summarization or topic
   * indexing times out or fails after raw bytes are preserved, `enrichmentStatus`
   * is stamped `'deferred'` with `enrichmentDeferredStages` so deferred
   * processing is explicit and never mistaken for a lost or failed original file.
   */
  enrichmentStatus?: 'complete' | 'deferred' | 'failed';
  enrichmentDeferredStages?: ('summarization' | 'topic-indexing' | 'classification')[];
  /**
   * Office_Preview — server-side LibreOffice → PDF derived-preview state
   * for formats with no client-side inline renderer (PowerPoint .ppt/.pptx).
   * The `office-preview` Cloud Run Job converts the raw upload to a PDF and
   * stamps this field; the OfficePreviewViewer renders the derived PDF by
   * reusing PdfViewer once `status === 'ready'`.
   *   - `none`    — convertible type, no preview generated yet (set at upload).
   *   - `pending` — a conversion job has been triggered and is in flight.
   *   - `ready`   — `pdfPath` points at the derived PDF in GCS.
   *   - `failed`  — conversion failed; `error` carries the reason.
   * Absent (undefined) for non-convertible types — those keep their existing
   * viewer routing untouched.
   */
  preview?: {
    status: 'none' | 'pending' | 'ready' | 'failed';
    /** GCS path to the derived PDF (users/{uid}/documents/{docId}/derived/preview.pdf). */
    pdfPath?: string;
    /** Failure reason when status === 'failed'. */
    error?: string;
    /** ISO timestamp of the last preview-state transition. */
    updatedAt?: string;
  };
  priority?: boolean;        // NB: marked for deeper AI analysis
  folder?: string;           // User-assigned folder (overrides auto-categorization)
  /**
   * DMF-1 — epoch-ms timestamp of the last folder move (set by
   * `moveSiteDocument`). Drives "recently moved" / audit surfaces and
   * lets the union dedupe (which is last-wins on `updatedAt`) pick the
   * freshest folder when the same doc lives in multiple report copies.
   * Absent on pre-DMF docs.
   */
  movedAt?: number;
  includeInReport?: boolean; // For images: include in the generated report/DOCX export

  /**
   * AI-classified per-report-type relevance. 0..1. Populated by the
   * classifier in src/services/knowledge/relevanceClassifier.ts at upload
   * time, refreshed by the admin "Reclassify all documents" action when
   * a new report type is added to the registry. Documents_On_Site Prompt 2.
   */
  relevance?: Partial<Record<import('../config/reportTypes').ReportTypeId, number>>;
  /** SED sections this document is most useful for (e.g. ['climate', 'grid']). */
  sedSectionsHelpful?: import('./siteEvidence').SedSectionId[];
  /** ISO timestamp of the last classifier run. */
  relevanceUpdatedAt?: string;
  /** Stamp of the classifier prompt/model version that produced these scores. */
  relevanceModelVersion?: string;
  /**
   * SHA-256 hex digest of the raw file bytes computed at upload time.
   * Used by the cross-site reuse detector (Documents_On_Site Prompt 7-C)
   * to surface "this document also lives on Site Beta — share or copy?"
   * Optional: pre-Prompt-7 uploads have no hash and are silently skipped
   * by the reuse helper.
   */
  contentHash?: string;
  /**
   * Hash-bound Generation & Impact screening facts. The model identifies raw
   * facts only; deterministic validation owns canonical units, eligibility,
   * and verified page/excerpt lineage. Optional for all legacy documents.
   */
  generationScreeningExtraction?: import('./generationImpact').GenerationScreeningDocumentExtraction;
  /** R-131-RAG-A — true once chunk embeddings are persisted and queryable. */
  embeddingsReady?: boolean;
  /** R-131-RAG-A — whole-doc embedding when chunk subcollection not yet populated. */
  vectorEmbedding?: number[];
  /**
   * ISO timestamp of the last content-based auto-classification pass
   * (Doc_Classification_And_Evidence_Labels AC1). When set, the doc has
   * had `tags` augmented from its `textContent` via the lightweight
   * Gemini classifier. Drives idempotency: the on-upload hook and the
   * `npm run backfill:doc-tags` helper both skip docs that already carry
   * this stamp. Absent on pre-AC1 uploads — those recover when the
   * admin "Backfill document tags" action runs.
   */
  autoTaggedAt?: string;
  /** Prompt/version stamp of the auto-classifier that produced the content tags. */
  autoTagModelVersion?: string;
  /**
   * ISO timestamp of the last PDF-text re-extraction pass
   * (PDF_Text_Extraction_Fix PE4). Set by `npm run backfill:pdf-text` /
   * the AdminPanel "Reprocess broken PDF text" action after the doc's
   * `textContent` was re-derived through the fixed (legacy-build) extractor.
   * Drives idempotency: a doc that already carries this stamp *and* whose
   * `textContent` is now clean is skipped on re-runs. Absent on docs that
   * never needed reprocessing (text was always clean) and on pre-PE4 data.
   */
  pdfTextReprocessedAt?: string;
  /**
   * Document_Extraction_Hardening DE6 — flag for PDFs whose text extraction
   * returned a `[PDF: … scanned/image-based …]` placeholder (i.e. pdfjs
   * found zero text on every page → almost certainly an image-only scan).
   * Set at upload time and refreshed by the PE4 reprocess backfill. The
   * actual OCR pass (Tesseract.js / Cloud Vision) is a future pack;
   * persisting the flag now means we can backfill OCR later without
   * re-scanning every PDF, and the AdminPanel filter ("Documents needing
   * OCR") shows the team how many sites are blocked on it. Absent / false
   * = no OCR needed (text extraction succeeded, or the doc isn't a PDF).
   */
  needsOcr?: boolean;
  /**
   * ISO timestamp of the last office/image re-extraction pass
   * (Document_Extraction_Hardening DE7). Set by the AdminPanel
   * "Reprocess broken Office / image docs" button + the
   * `npm run backfill:office-image` CLI after the doc's `textContent` was
   * re-derived through the new (DE1-DE5) extractors. Drives idempotency: a
   * doc that already carries this stamp AND whose `textContent` is no
   * longer a placeholder is skipped on re-runs. Absent on docs that never
   * needed reprocessing.
   */
  officeReprocessedAt?: string;
  /**
   * Supply Chain Management (SCM pack — SCM8). IDs of `BomItem`s this
   * document covers — typically vendor quote PDFs linked to one or
   * more BoM line items. The reverse pointer (`bomItem ← document`) is
   * carried by `Quote.documentId` so the SupplyChainPanel can render
   * the quote alongside the BoM row. Undefined = legacy / non-quote
   * documents.
   */
  linkedBomItemIds?: string[];
  /**
   * KSD3 — When `false`, the bootstrap pipeline skips this doc
   * entirely: no SED extraction, no doc-aware boost, no
   * `applyDocAwareConfidence` participation. Defaults to `true` for
   * legacy / pre-KSD3 docs (absent ≡ included).
   *
   * Flipped via the Document Library tile and the per-doc detail
   * drawer. Composes with the UD pack — the value is a user decision
   * preserved across SED rebuilds via the standard
   * `mergePreservedUserDecisions` keyed-on-docId pattern.
   */
  includeInSed?: boolean;

  // ── UPE pack — Upload Provenance Evidence ────────────────────────
  /**
   * UPE-1 — per-doc extraction status. Set at the end of each per-doc
   * loop in `applyDocAwareConfidence` (sedBootstrap.ts) once we know
   * which slots got populated, so the Documents tab can render a
   * status chip next to the filename. Absent on legacy / pre-UPE
   * docs (rendered as `'pending'` until the next SED rebuild).
   */
  extractionStatus?: ExtractionStatus;
  /** UPE-1 — ISO timestamp when `extractionStatus` was last stamped. */
  extractionStatusAt?: number;
  /** UPE-1 — short error string when `extractionStatus` is one of the
   *  `failed-*` variants. Surfaced in the chip tooltip. */
  extractionStatusError?: string;
  /** Retained field mappings plus legacy linked sections without separately
   *  counted fields, deduplicated per section/field. Exact field and section
   *  counts are separate in extractionOutcome; neither is fulfillment. */
  populatedSlotCount?: number;
  /** Latest source-bound mapping pass; process outcomes are not evidence fulfillment. */
  extractionOutcome?: DocumentExtractionOutcomeV1;
  /** UPE-1 — checklist-requirement ids the doc fulfilled, per
   *  `computeRequirementStatus` (Tier 2 — slot-aware) on the latest
   *  pass. Drives Documents-tab chips that show "fulfils: utility,
   *  zoning" alongside the doc row. */
  fulfilledRequirementIds?: string[];

  // ── URE pack — Upload Requirement Engagement ─────────────────────
  /**
   * URE-1 — Coverage Matrix requirement ids the user intended this
   * doc to fulfill at upload time (e.g. clicking
   * `coverage-matrix-upload-utility-letter` stamps
   * `['utility-letter']`). Drives:
   *   (a) extraction priority — `extractFromDocument` consults
   *       `REQUIREMENT_TO_SECTION_MAP` to run the matching sections
   *       FIRST.
   *   (b) Coverage Matrix slot filling — even if the DCT score is
   *       borderline, an explicitly intended doc is preferred as the
   *       fallback for the intended requirement's sections.
   *   (c) `DOC_OVERRIDE_RULES` — secondary trigger alongside the
   *       existing tag-match trigger.
   * Optional / additive — back-compat with pre-URE docs (undefined).
   */
  intendedRequirementIds?: string[];
  /** URE-1 — epoch ms when `intendedRequirementIds` was last set
   *  (audit). */
  intentSetAt?: number;
  /** URE-1 — uid/email of the user that set the intent (audit). */
  intentSetBy?: string;

  // ── DAT-FOLLOWUP-PERSIST-IMPORTANCE ───────────────────────────────
  /**
   * Persisted importance score (0..1). Computed by `computeDocImportance`
   * (DAT-4) and cached here so the Documents tab's importance-sort sort
   * is O(1) at render time instead of recomputing on every mount.
   *
   * Lifecycle:
   *   - Written by `DocumentLibrary`'s backfill effect when the doc is
   *     missing a score (first mount post-deploy + every newly-uploaded
   *     doc on its first render).
   *   - Routed through `patchSiteDocument` so the DDL-3 cross-array
   *     parity carries the score onto every report copy of the doc.
   *
   * Invalidation:
   *   - Any patch that touches an input the importance formula reads
   *     (`relevance`, `populatedSlotCount`, `fulfilledRequirementIds`,
   *     `priority`) clears `importanceScore` so the next render
   *     recomputes from scratch. The clear happens inside
   *     `patchSiteDocument` — callers don't need to remember.
   *
   * Absent ⇒ "not yet computed". Backfill is gated on
   * `importanceScore === undefined` so re-runs are idempotent and
   * write-storms can't happen on a site with N legacy docs.
   */
  importanceScore?: number;
  /**
   * Epoch ms when `importanceScore` was last persisted. Surfaced for
   * audit / debugging only — readers consume `importanceScore` directly.
   */
  importanceScoreAt?: number;

  // ── CMU pack — Coverage Matrix Upload (replace + supporting) ─────
  /**
   * CMU-2 — id of the document this upload replaces (a newer revision).
   * Set at upload time when the user landed in the Document Library
   * via the Coverage Matrix `[Replace]` affordance: the old doc gets
   * soft-archived (see `archived` / `archivedReason` below) and the
   * new doc carries this back-pointer so the dedupe / lineage helpers
   * can reason about the version chain.
   *
   * Composes with the existing canonical-name dedupe in
   * `src/lib/documentDedupe.ts` — `replacesDocId` is the EXPLICIT chain
   * (user-asserted), the canonical-name grouping is the implicit one.
   * Both feed the "+N earlier revisions" disclosure.
   *
   * Absent on free-drop uploads (the canonical-name grouping still
   * collapses repeats — same behaviour as pre-CMU).
   */
  replacesDocId?: string;
  /**
   * CMU-2 — soft-archive flag. Set on the OLD doc when a replacement
   * lands. Archived docs are hidden from the default Documents-tab
   * view + Coverage Matrix DCT pool, but kept in storage so the audit
   * trail is preserved and an admin "restore" action remains viable.
   *
   * We deliberately DO NOT hard-delete: the report references / SED
   * lineage may still point at the old `docId` (the SED rebuild after
   * a replace re-extracts from the new doc, but the historical
   * sources stay valid for prior-report reproducibility — same
   * principle that keeps `unresolvedConflicts` snapshots immutable).
   *
   * Absent / false ≡ live document.
   */
  archived?: boolean;
  /** CMU-2 — epoch ms when the doc was archived. */
  archivedAt?: number;
  /** CMU-2 — uid/email of the user that triggered the archive. */
  archivedBy?: string;

  // ── DDL-FOLLOWUP-CONFIRM-DIALOG / DDL-FOLLOWUP-RESTORE ───────────
  /**
   * Soft-delete tombstone on the SiteDocument itself. Distinct from the
   * separate `users/{uid}/deletedDocuments/{docId}` tombstone collection
   * (DLB-3), which exists to block re-ingest of the same canonical-name
   * doc for 30 days. THIS field is the in-place tombstone for the doc
   * record — readers in the Document Library filter `deletedAt != null`
   * out of the default view; the admin Ghost Docs panel surfaces the
   * soft-deleted set with a Restore action that nulls the field again.
   *
   * Lifecycle:
   *   - `null` / absent  ⇒ live document (the explicit "not deleted" state
   *                         on Firestore is `null`; absent is treated as
   *                         the same by `isSoftDeleted`).
   *   - ISO timestamp     ⇒ soft-deleted at this instant. The doc is hidden
   *                         from the default Documents tab but kept in
   *                         storage for `SOFT_DELETE_TTL_DAYS` (7) so an
   *                         admin restore round-trip is viable.
   *   - After 7 days the doc is eligible for purge by the cron / a one-line
   *     filter in the read path (see `src/lib/documentSoftDelete.ts`).
   *
   * Schema additivity: optional. Pre-DDL-FOLLOWUP docs have it undefined
   * and are treated as live everywhere. `deletedAt: null` is the EXPLICIT
   * "not deleted" state — `stripUndefined()` preserves it on writes.
   */
  deletedAt?: string | null;
  /** UID of the user that soft-deleted the doc. `null` clears (restore). */
  deletedBy?: string | null;
  /**
   * CMU-2 — short machine-readable reason. Common values:
   *   - `'replaced-by-<newDocId>'` — superseded via the Coverage
   *     Matrix Replace flow.
   *   - `'admin-archive'`          — manual admin action.
   * Open string so future archive triggers (e.g. classifier reject,
   * tombstoned site) can extend without a union migration.
   */
  archivedReason?: string;

  // ── IIA pack — Image Intelligence Analyzer ───────────────────────
  /**
   * IIA-2 / IIA-4 — output of the IIA pipeline when the doc is an
   * image upload (`mimeType.startsWith('image/')`). Populated by
   * `processUploadedFile` at upload time; consumed by:
   *   - the Coverage Matrix resolver (tier-1 doc) to stamp
   *     `coverage[reqId].extractionModality = 'vision'`, gating the
   *     `📷 Image (vision)` chip;
   *   - the SED bootstrap (`sedBootstrap.ts`) to surface free-text
   *     observations into `siteContext.claims[]` when the classifier
   *     returns `'other'` (kind === 'other') or extraction failed.
   *
   * Absent ⇒ non-image upload OR pre-IIA legacy doc.
   *
   * Shape mirrors `IiaPipelineResult` in `imageExtractors.ts`; kept as
   * `unknown` here to avoid a cross-cutting type import from a
   * type-only module (the runtime guard in
   * `applyIiaResultToSiteContextClaims` re-narrows).
   */
  iiaResult?: {
    /** Classified kind + classifier confidence (always present). */
    classification: {
      kind: import('../services/knowledge/analyzeImageEvidence').ImageKind;
      confidence: number;
    };
    /** Per-kind structured extraction result. Only populated when the
     *  classifier was confident enough (>= 0.5) and the extractor
     *  succeeded. Shape varies per kind — see `imageExtractors.ts`. */
    extracted?: Record<string, unknown>;
    /** Free-text observation written when no structured fields could
     *  be extracted (IIA-3 fallback). `userApproved: false` until the
     *  user accepts via the Documents tab affordance. `confidence` is
     *  the IIA-3 / IIA-5 free-text cap (≤ 0.55) — never inflate above
     *  this without an explicit user amend. */
    observation?: {
      observationText: string;
      relevantSections?: readonly string[];
      /** IIA-3 / IIA-5 free-text confidence (capped at
       *  `IIA_VISION_FREE_TEXT_CONFIDENCE_CAP` = 0.55). Absent on
       *  pre-IIA-3 results (rendered as the classifier confidence
       *  fallback by the IIA-5 card). */
      confidence?: number;
      userApproved?: boolean;
    };
    /** Requirement ids this extraction is hinting at populating.
     *  Mirrors `intendedRequirementIds` but derived from the kind, not
     *  the upload-time user click. */
    targetRequirementIds?: readonly string[];
    /** ISO timestamp of the pipeline run. */
    runAt: string;
    /** Result outcome the audit dashboard reads. */
    attemptResult: 'success' | 'empty' | 'classifier-uncertain' | 'extraction-failed';
  };
}

/**
 * UPE-1 — per-doc extraction status surfaced on the Documents tab row
 * and consulted by `computeRequirementStatus` (UPE-3) when deciding
 * whether a Coverage Matrix cell reads `strong` (real extraction
 * landed) vs `tentative` (DCT score alone with no slot population).
 *
 *   - `'pending'`         — never run / not yet scanned.
 *   - `'extracting'`      — in-flight (a SED rebuild is currently
 *                           working through this doc).
 *   - `'success-full'`    — all expected slots for this doc's
 *                           intended/helpful sections populated.
 *   - `'success-partial'` — some slots populated, others returned
 *                           null from the extractor.
 *   - `'failed-quota'`    — Gemini quota / 429 from
 *                           `extractFromDocument`.
 *   - `'failed-parse'`    — JSON parse / schema validation failed.
 *   - `'failed-timeout'`  — 30s per-section ceiling exceeded.
 *   - `'failed-unknown'`  — other error.
 *   - `'failed-no-text'`  — SPO-1: the doc carries only a placeholder
 *                           (scanned/image-based, OCR yielded nothing) —
 *                           there is no machine-readable text to extract
 *                           from. Terminal + non-retryable until the
 *                           underlying text extraction is repaired
 *                           (re-upload / OCR backfill). Replaces the
 *                           misleading `'success-full'` these docs used
 *                           to receive from zero-attempt forced scans.
 */
export type ExtractionStatus =
  | 'pending'
  | 'extracting'
  | 'success-full'
  | 'success-partial'
  | 'failed-quota'
  | 'failed-parse'
  | 'failed-timeout'
  | 'failed-unknown'
  | 'failed-no-text';

/** Metadata only. Empty or unsupported targets never imply a populated requirement. */
export interface DocumentExtractionOutcomeV1 {
  version: 1;
  at: number;
  retainedFieldCount: number;
  linkedSectionCount: number;
  targets: Array<{
    channel: 'section' | 'requirement';
    targetId: string;
    status: 'extracted' | 'empty' | 'unsupported' | 'rejected' | 'failed' | 'deferred';
    /** Extraction invocations, including cached reads; not paid model-call telemetry. */
    attempts: number;
    reason?: string;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Project Phasing (PP pack — PP1).
//
// Hyperscaler / IPP / sovereign datacenter builds rarely ship as a single
// one-shot build. Phased rollouts (50 MW grid → 50 MW gas → 100 MW SMR …)
// are the norm. `ProjectPhase[]` makes phasing a first-class concept on
// `ProjectIntent` so every downstream surface (LCOE, SCM, critical path,
// risk derivation, action plan) can reason per phase instead of treating
// a multi-phase $5B build as a single $5B event.
//
// All phasing fields are OPTIONAL — sites that ship one MW / one COD
// keep working unchanged via `effectivePhases()` (src/lib/projectIntent.ts)
// which collapses absent / empty phases into a single legacy view.
// ─────────────────────────────────────────────────────────────────────────────

export type ProjectPhaseStatus =
  | 'planned'
  | 'permitting'
  | 'construction'
  | 'commissioned'
  | 'decommissioned';

export type PowerSourceKey =
  | 'grid'              // utility-supplied grid
  | 'gas-cc'            // combined cycle gas
  | 'gas-sc'            // simple cycle / peaker
  | 'smr'               // small modular reactor
  | 'large-nuclear'     // utility-scale nuclear
  | 'solar-bess'        // solar PV + BESS
  | 'wind-bess'         // wind + BESS
  | 'hybrid'            // multiple co-located on this phase
  | 'undecided';

/** Granular generation/storage components selectable per phase. */
export type PhasePowerComponentKey =
  | 'recip'
  | 'gas-turbine'
  | 'fuel-cell'
  | 'battery'
  | 'grid'
  | 'solar-bess'
  | 'wind-bess'
  | 'smr'
  | 'large-nuclear';

export type GridConnectionMode = 'islanded' | 'grid-connected' | 'undecided';

/**
 * BoP (balance-of-plant) heat-rejection / cold-end method — the COOLING-SYSTEM
 * side, distinct from the IT-side `coolingStrategy` (air / liquid / …).
 *
 * `coolingStrategy` describes how heat leaves the SERVERS (air vs liquid loops);
 * this describes how the facility ultimately REJECTS that heat (dry coolers, wet
 * towers, once-through, or thermally — absorption / CCHP). The distinction
 * matters for the config-derived PUE estimate and the BTM waste-heat credit:
 *
 *   - `absorption-cchp` — heat is reused to drive absorption chillers / CCHP, so
 *     cooling becomes a THERMAL (not electrical) load. With on-site thermal
 *     generation (gas / SMR / nuclear) this offloads roughly half the
 *     cooling-electrical demand (Araner / Clarke Energy; >20% primary-energy
 *     saving) → a lower effective electrical PUE.
 *   - `dry-cooler` / `wet-cooling-tower` / `hybrid` / `once-through` /
 *     `free-cooling` — conventional electrical heat rejection. Any of these
 *     (except a purely electrical immersion loop) CAN incorporate absorption
 *     when paired with on-site thermal generation, so they remain
 *     waste-heat-credit-eligible.
 *   - `unknown` — not yet specified. `resolveConfigPue` uses the conservative
 *     base and the resulting PUE is flagged a config estimate (not a credit).
 *
 * ADDITIVE + optional — does NOT replace or alter `coolingStrategy` and is not
 * part of `normaliseCooling`'s vocabulary (that remains IT-side only).
 */
export type BopHeatRejection =
  | 'dry-cooler'
  | 'wet-cooling-tower'
  | 'hybrid'
  | 'once-through'
  | 'absorption-cchp'
  | 'free-cooling'
  | 'unknown';

/**
 * BTM cooling-GENERATION machine axis — HOW the facility's cooling duty is
 * produced (the chiller / cold-production technology), as opposed to:
 *   - `coolingStrategy` — the RACK / IT-side heat-capture loop (air vs liquid), and
 *   - `bopHeatRejection` — the cold-END / heat-rejection sink (dry / wet / etc.).
 *
 * This axis is ORTHOGONAL to both of the above: a single site can pair any
 * rack-side `coolingStrategy` with any cold-end `bopHeatRejection` and any of
 * these cooling-generation machines. It distinguishes the BTM-generation
 * implications:
 *
 *   - `absorption-thermal` — absorption chiller driven by waste / thermal heat
 *     (no significant cooling-electrical load; pairs with on-site thermal gen).
 *   - `vapour-compression-electric` — conventional electric vapour-compression
 *     chiller (an electrical cooling load).
 *   - `steam-driven-mechanical` — steam-turbine-driven mechanical chiller
 *     (a thermal / mechanical drive rather than an electrical one).
 *   - `free-cooling-economiser` — ambient free-cooling / economiser (minimal
 *     mechanical cooling generation).
 *   - `none` — no active cooling-generation machine on this axis.
 *   - `unknown` — not yet specified.
 *
 * ADDITIVE + optional. Does NOT replace or alter `coolingStrategy` or
 * `bopHeatRejection`, and is not part of `normaliseCooling`'s vocabulary
 * (that remains IT-side only). Downstream readers should derive it from a
 * stored intent via `projectCoolingGeneration()` (the non-destructive
 * back-compat boundary projector) rather than assuming the field is set.
 */
export type CoolingGeneration =
  | 'absorption-thermal'
  | 'vapour-compression-electric'
  | 'steam-driven-mechanical'
  | 'free-cooling-economiser'
  | 'none'
  | 'unknown';

/**
 * Non-destructive boundary projector — derives the BTM cooling-GENERATION
 * machine axis from a stored intent WITHOUT re-keying persisted data.
 *
 * Resolution order (back-compat):
 *   1. If `intent.coolingGeneration` is explicitly set, return it as-is.
 *   2. Else map the legacy `bopHeatRejection` cold-end value:
 *        - `'absorption-cchp'`  → `'absorption-thermal'`
 *        - `'free-cooling'`     → `'free-cooling-economiser'`
 *   3. Else `'unknown'`.
 *
 * Pure + side-effect-free. Downstream cooling groups call THIS rather than
 * reading the raw field, so legacy SEDs (which only carry `bopHeatRejection`)
 * still project a sensible cooling-generation value. Accepts a structural
 * subset so it can be called with sparse / partial intent views.
 */
export function projectCoolingGeneration(
  intent: Pick<ProjectIntent, 'coolingGeneration' | 'bopHeatRejection'>,
): CoolingGeneration {
  if (intent.coolingGeneration) return intent.coolingGeneration;
  if (intent.bopHeatRejection === 'absorption-cchp') return 'absorption-thermal';
  if (intent.bopHeatRejection === 'free-cooling') return 'free-cooling-economiser';
  return 'unknown';
}

// ─────────────────────────────────────────────────────────────────────────────
// COOLING-AXES CONSISTENCY (collapse the overlapping absorption / free-cooling
// axis). The two fields below are genuinely DISTINCT axes:
//
//   - `bopHeatRejection`  — the cold-END SINK (where rejected heat goes:
//     dry / wet / once-through / hybrid …).
//   - `coolingGeneration` — the cold-PRODUCTION MACHINE (how cold is made:
//     electric vapour-compression / absorption-thermal / steam-driven …).
//
// They are NOT one-derivable-from-the-other in general — a dry-cooler SINK + an
// electric chiller MACHINE is a valid, common pair a naive merge would destroy.
// But ONE concept (the thermal absorption / free-cooling machine) was historically
// duplicated across BOTH enums (`bopHeatRejection === 'absorption-cchp'` smuggled
// the absorption MACHINE into the sink). With nothing validating the pair, a
// physically-impossible combination (thermal sink + electric machine, or vice
// versa) was allowed and resolved inconsistently downstream.
//
// These helpers are the ONE derivation point that keeps the duplicated absorption
// /free-cooling concept consistent across both fields, so the contradiction is
// structurally impossible. They derive ONLY the paired value for the overlapping
// concept; the genuinely-distinct sinks/machines are left untouched.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The MACHINE values that are THERMALLY DRIVEN — they require an on-site heat /
 * steam stream and therefore imply a thermal cold-end choice on the SINK axis.
 * Mirrors `coolingValidity.ts` THERMALLY_DRIVEN_GENERATION (kept in lockstep).
 */
const THERMAL_GENERATION_MACHINES: ReadonlySet<CoolingGeneration> = new Set([
  'absorption-thermal',
  'steam-driven-mechanical',
]);

/**
 * Derive the consistent `coolingGeneration` MACHINE for a chosen heat-rejection
 * SINK — the single source of the paired value when the user edits the SINK axis.
 *
 * Only the OVERLAPPING (historically-duplicated) sink values map to a machine:
 *   - `'absorption-cchp'` → `'absorption-thermal'`  (thermal machine)
 *   - `'free-cooling'`    → `'free-cooling-economiser'`
 * Every genuinely-distinct sink (dry / wet / once-through / hybrid) returns
 * `undefined` — it does NOT constrain the machine axis, so the caller leaves the
 * existing machine choice alone.
 */
export function coolingGenerationForSink(
  sink: BopHeatRejection | undefined,
): CoolingGeneration | undefined {
  if (sink === 'absorption-cchp') return 'absorption-thermal';
  if (sink === 'free-cooling') return 'free-cooling-economiser';
  return undefined;
}

/**
 * Reconcile the `bopHeatRejection` SINK for a chosen cooling-GENERATION MACHINE
 * — the single source of the paired SINK value when the user edits the MACHINE
 * axis. Returns the SINK the caller should WRITE, or `undefined` to leave the
 * existing sink choice unchanged.
 *
 * The SINK and MACHINE axes are genuinely distinct, so most machine choices do
 * NOT constrain the sink (an `absorption-thermal` chiller can legitimately reject
 * heat through a dry cooler — powerStack still credits it). The ONLY rule is the
 * narrow overlap that made the contradiction possible: the legacy
 * `'absorption-cchp'` sink IS the thermal machine smuggled into the sink enum, so
 * it can ONLY coexist with a thermal machine. When the user picks a NON-thermal
 * machine while the sink is the contradictory `'absorption-cchp'`, clear the sink
 * (→ `'unknown'`) so the impossible pair can never be persisted.
 *
 *   - `current === 'absorption-cchp'` + a non-thermal/electric/none machine
 *       → `'unknown'`  (clear the contradictory thermal sink).
 *   - everything else → `undefined` (leave the sink as the user set it).
 */
export function reconcileSinkForCoolingGeneration(
  gen: CoolingGeneration | undefined,
  current: BopHeatRejection | undefined,
): BopHeatRejection | undefined {
  if (current !== 'absorption-cchp') return undefined; // only the overlap rule
  const genIsThermal = !!gen && THERMAL_GENERATION_MACHINES.has(gen);
  return genIsThermal ? undefined : 'unknown';
}

/** TRUE when a SINK + MACHINE pair is physically contradictory — i.e. the
 *  thermal `absorption-cchp` SINK (the absorption machine in disguise) paired
 *  with an electric / non-thermal generation machine. Used by both the form (to
 *  clear the conflicting field) and the cross-field viability gate so the
 *  combination can never be persisted. NOTE: a thermal MACHINE on a conventional
 *  (dry / wet / hybrid) sink is NOT a contradiction — absorption can reject heat
 *  through any conventional sink (powerStack credits it). */
export function isCoolingPairContradictory(
  sink: BopHeatRejection | undefined,
  gen: CoolingGeneration | undefined,
): boolean {
  if (!sink || !gen) return false; // an unset axis carries no contradiction
  // The only structural contradiction: the absorption-cchp SINK (thermal) with
  // a non-thermal MACHINE (electric / free-cooling / none).
  if (sink === 'absorption-cchp' && !THERMAL_GENERATION_MACHINES.has(gen)) {
    return true;
  }
  return false;
}

/**
 * Resolve a CONSISTENT (sink, machine) pair from an intent / phase view —
 * defensive normalizer for READERS (report-gen, SED) so a legacy-persisted
 * contradictory pair never surfaces as conflicting prose. The MACHINE is the
 * source of truth (it is what the PUE credit gates on via
 * `projectCoolingGeneration`), so when the resolved sink is the thermal
 * `absorption-cchp` but the resolved machine is NON-thermal, the sink is demoted
 * to `'unknown'` (drop the contradictory thermal claim) rather than printing both.
 *
 * Byte-stable for every consistent pair (the overwhelming common case): it only
 * rewrites the genuinely-contradictory `absorption-cchp` + non-thermal-machine
 * combination, which the write paths now prevent from being created at all.
 */
export function resolveCoolingPair(
  view: Pick<ProjectIntent, 'coolingGeneration' | 'bopHeatRejection'>,
): { coolingGeneration: CoolingGeneration; bopHeatRejection: BopHeatRejection | undefined } {
  const machine = projectCoolingGeneration(view);
  let sink = view.bopHeatRejection;
  if (isCoolingPairContradictory(sink, machine)) {
    sink = 'unknown';
  }
  return { coolingGeneration: machine, bopHeatRejection: sink };
}

export type FuelCellChemistry =
  | 'sofc'
  | 'pafc'
  | 'pem'
  | 'mcfc'
  | 'compare-all-suitable'
  | 'unspecified';

export type FuelCellPrimaryFuel =
  | 'natural-gas'
  | 'rng-biogas'
  | 'hydrogen'
  | 'hydrogen-ng-blend'
  | 'unspecified';

export type FuelCellAuxiliaryLoadKey =
  | 'skid-blowers'
  | 'internal-reformer'
  | 'desulfurization'
  | 'fuel-booster-compressor'
  | 'dry-cooler-fans'
  | 'step-up-transformer';

/**
 * Rating boundary specification for stationary fuel-cell packages (Finding 8).
 * Prevents double-derating vendor net-AC skid ratings (e.g., Bloom Energy Server 6.5
 * 325 kW net AC cumulative) by recording which parasitics and degradation are already
 * included inside the published rating boundary.
 */
export interface RatingBoundarySpec {
  powerBoundary:
    | 'gross-dc-stack'
    | 'inverter-ac-terminals'
    | 'net-ac-skid-boundary'
    | 'net-ac-pcc';
  efficiencyAveragingBasis:
    | 'bol-new-and-clean'
    | 'first-year-average'
    | 'cumulative-contract-term'
    | 'eol-minimum';
  outputGuaranteeMode:
    | 'constant-net-ac-via-fuel-trim'
    | 'degrading-net-ac-over-time';
  includedAuxiliaries: readonly FuelCellAuxiliaryLoadKey[];
  isoReferenceConditions: {
    ambientTempC: number;
    elevationM: number;
    relativeHumidityPct?: number;
  };
  claimStatus:
    | 'oem-guaranteed'
    | 'oem-indicative'
    | 'independent-test'
    | 'model-assumption';
}

/**
 * Explicit dimensional bases for fuel-cell stack degradation (Finding 11).
 * Never conflates per-1000-operating-hours with per-calendar-year.
 */
export interface FuelCellDegradationSpec {
  degradationDriver: 'operating-hours' | 'calendar-time' | 'thermal-start-cycles';
  outputDeclineRate?: {
    pctPer1000Hours?: number;
    pctPerYear?: number;
    pctPerStartCycle?: number;
  };
  heatRateGrowthRate?: {
    pctPer1000Hours?: number;
    pctPerYear?: number;
  };
  stackReplacementIntervalHours?: number;
  stackReplacementIntervalYears?: number;
}

export type StackCostAccountingMode =
  | 'ltsa-levelized-opex'
  | 'discrete-refurbishment-events';

/**
 * Per-phase stationary fuel-cell configuration (Findings 2, 7, 8, 9, 11, 12, 13, 14).
 * When `chemistry` or `primaryFuel` is `'unspecified'`, the resolver MUST keep it
 * unresolved (`'fuel-cell-unspecified'`) and never silently default to `'fuel-cell-sofc'`.
 */
export interface FuelCellPhaseConfig {
  chemistry: FuelCellChemistry;
  primaryFuel?: FuelCellPrimaryFuel;
  /** Optional curated OEM package ID (e.g., `'bloom-energy-server-6.5-ng'`). */
  oemPackageId?: string;
  /** Optional useful CHP heat recovery fraction (% of fuel input, 0 when electrical-only). */
  chpUsefulHeatRecoveryPct?: number;
  /** Whether CHP heat recovery is actively coupled to absorption cooling. */
  chpCoupledToCooling?: boolean;
  /** Optional explicit rating boundary override or package spec. */
  ratingBoundary?: RatingBoundarySpec;
  /** Efficiency fraction on LHV basis in (0, 1) — values >= 1 are rejected as unit errors. */
  electricalEfficiencyFractionLhv?: number;
  /** Optional directly supplied OEM net-AC HHV heat rate (Btu/kWh). */
  suppliedHeatRateHhvBtuPerKwh?: number;
  /** Required minimum fuel inlet pressure at skid boundary. */
  minInletPressure?: {
    value: number;
    unit: 'psig' | 'in-wc';
  };
  /** Hydrogen purity requirement when `primaryFuel === 'hydrogen'` (ISO 14687:2025). */
  hydrogenPurityRequirement?: {
    standard: 'ISO-14687' | 'SAE-J2719' | 'oem-custom';
    edition: '2025' | '2019' | string;
    gradeOrCategory: 'Grade-D' | 'Grade-E-Cat1' | 'Grade-E-Cat2' | 'Grade-E-Cat3' | string;
    maxCarbonMonoxidePpmv?: number;
    maxTotalSulfurPpmv?: number;
    maxAmmoniaPpmv?: number;
  };
  degradation?: FuelCellDegradationSpec;
  /** Mutual exclusion guard between LTSA opex and discrete stack capex spikes (Finding 7). */
  stackCostAccountingMode?: StackCostAccountingMode;
  ltsaPerKwYearUsd?: number;
  discreteRefurbishmentEvents?: ReadonlyArray<{
    yearFromCod: number;
    usdPerKw: number;
    description: string;
  }>;
  /** External parasitics outside `ratingBoundary.includedAuxiliaries` (fraction of net AC). */
  externalParasitics?: {
    boosterCompressorFraction?: number;
    mvStepUpTransformerLossFraction?: number;
    externalDryCoolerFraction?: number;
  };
  /** Islanding and step-load transient envelope parameters (Finding 13). */
  islandReadiness?: {
    gridFormingInverterVerified?: boolean;
    blackStartAuxiliaryVerified?: boolean;
    hotStandbyRampRateMwPerMin?: number;
    requiredStepLoadMw?: number;
    pairedBessStepBufferMw?: number;
    pairedBessUsableMwh?: number;
    pairedBessMinReservedSocPct?: number;
    singleGasMainCommonModeMitigated?: boolean;
  };
  /** Tax credit policy posture (Finding 14 — base case is always unsubsidized 0%). */
  taxCreditScenario?:
    | 'unsubsidized-base'
    | 'irc-48e-j-qualified-fuel-cell-subject-to-tax-review'
    | 'irc-48e-45y-zero-ghg-subject-to-tax-review';
}

/** Hybrid power source configuration for a single phase. */
export interface PhasePowerSourceConfig {
  /** Selected co-located components (e.g. recips + turbines + battery). */
  components: PhasePowerComponentKey[];
  /** Optional stationary fuel-cell configuration when `'fuel-cell'` is in `components`. */
  fuelCellConfig?: FuelCellPhaseConfig;
  /** Islanded microgrid vs utility grid when applicable. */
  gridConnection: GridConnectionMode;
  /** Utility name when grid-connected (e.g. "Entergy"). */
  gridUtilityName?: string;
  /**
   * Whether a grid-connected phase may export power. OPTIONAL and additive;
   * meaningful only with `gridConnection: 'grid-connected'` (an islanded or
   * undecided phase ignores it). Behind-the-meter generation can run
   * grid-parallel without export: zero export does not by itself remove
   * utility interconnection review. Absent reads as unknown. An explicit
   * "Unknown" answer is recorded with the
   * `fieldSource['phases.<id>.powerSourceConfig.exportMode'] = 'user-unknown'`
   * marker. `GridConnectionMode` is deliberately NOT widened.
   */
  exportMode?: 'non-export' | 'export' | 'unknown';
  /**
   * How the selected `components` relate to one another. OPTIONAL and
   * back-compatible — absent is treated as `'or'` (today's behaviour).
   *
   *   - `'or'`  — the sources are mutually-exclusive ALTERNATIVES the user is
   *     still choosing between (no combined-capacity feasibility check).
   *   - `'and'` — the sources COMBINE on this phase (a real co-located mix);
   *     `assessPhaseFeasibility` then checks whether the combined nameplate ×
   *     capacity factor covers the phase load. See `phaseFeasibility.ts`.
   */
  mixMode?: 'or' | 'and';
  /**
   * Per-source generation nameplate MW, or contracted/expected customer draw
   * for grid import, plus capacity factor (%). Feeder hardware ratings remain
   * in the engineering scenario and do not establish a customer allocation.
   * Used in AND mode to size the combined mix against the phase load.
   * OPTIONAL — missing entries fall
   * back to 0 MW and the documented `DEFAULT_CAPACITY_FACTOR_PCT` for that key.
   *
   * CAPACITY DUALITY (exact ⊕ range) — mirrors the phase-level
   * `capacityMW ⊕ capacityBucket` contract:
   *   - `capacityMW` is the EXACT per-source cumulative nameplate. Exact WINS:
   *     when it carries a finite figure, any stored range edges are inert and
   *     the sanctioned writers (`withPhaseSourceCapacityMW` /
   *     `withPhaseSourceCapacityRange` in lib/projectIntent.ts) clear the
   *     other mode on write.
   *   - `capacityMinMW`/`capacityMaxMW` are a FREE per-source cumulative
   *     capacity RANGE (MW) — not a bucket; per-source figures are
   *     equipment-scale numbers. The range speaks only when BOTH edges are
   *     finite with 0 ≤ min ≤ max and max > 0; a half-set / inverted pair is
   *     a drafting state ("unsized"). Presence-gated back-compat: absent
   *     fields read as "no range".
   *   - ALL capacity figures here are CUMULATIVE at phase completion (the
   *     carry-forward convention) — never per-phase increments.
   *   - Never read these fields raw — resolve via
   *     `effectivePhaseSourceCapacity(phase, key)`.
   */
  componentParams?: Partial<
    Record<
      PhasePowerComponentKey,
      {
        capacityMW?: number;
        capacityFactorPct?: number;
        capacityMinMW?: number;
        capacityMaxMW?: number;
        /**
         * 'phase-power-plan' when `capacityMW` was written by applying the
         * per-phase power recommendation (mirrors `ProjectPhase.capacitySource`).
         * The sanctioned writer drops it on any manual capacity edit — the
         * marker describes history, never authority.
         */
        capacitySource?: 'phase-power-plan';
      }
    >
  >;
}

/**
 * Canonical component order — render order in the mix editor AND the
 * deterministic tie-break order for the phase power recommendation's merit
 * allocation. ONE list so UI and engine can never disagree.
 */
export const PHASE_POWER_COMPONENT_KEYS: PhasePowerComponentKey[] = [
  'recip', 'gas-turbine', 'fuel-cell', 'battery', 'grid', 'solar-bess', 'wind-bess', 'smr', 'large-nuclear',
];

/** Coarse COD bucket for a phase when exact date is unknown. */
export type PhaseCodTimeline = 'under-2yr' | '2-5yr' | 'over-5yr' | 'undecided';

/**
 * A single phase of a phased rollout. When a project has
 * `intent.phases = [...]` with at least one entry, downstream code
 * resolves the project as multi-phase via `effectivePhases(intent)`.
 *
 * Per-phase overrides (`coolingStrategy`, `workloadProfile`) supersede
 * the top-level intent for THIS phase only — left undefined to inherit.
 *
 * Capacity and date may be null while the user is still drafting; the
 * resolver treats `null` as "TBD" and skips that phase when deriving the
 * ultimate figure (it doesn't break the rollup). `capacityMW` is
 * CUMULATIVE (total at phase completion, site-owner decision 2026-06-08)
 * — the ultimate campus figure is the FINAL phase's value, never a sum.
 */
export interface ProjectPhase {
  /** Stable id (UUID). Used by `dependencies` cross-refs and React keys. */
  id: string;
  /** Free-text label — "Phase 1 — Bridge", "SMR pilot", etc. */
  label: string;
  /** 1-based sort order. The form keeps this contiguous on add/remove/reorder. */
  sequence: number;
  /**
   * CUMULATIVE site capacity (MW) at THIS phase's completion — NOT the
   * increment added this phase (`phaseIncrementMW()` derives that). Null
   * while the user is still drafting, or when the phase carries a
   * `capacityBucket` range instead of an exact figure.
   */
  capacityMW: number | null;
  /**
   * Coarse capacity RANGE when `capacityMW` is absent — the per-phase
   * analogue of the top-level `ProjectIntent.powerRequirement` bucket.
   * Mirrors the COD pair (`targetInServiceDate` / `codTimeline`): writers
   * must keep the two capacity fields consistent via
   * `withPhaseCapacityMW()` / `withPhaseCapacityBucket()` /
   * `withPhaseCapacityMax()` (src/lib/projectIntent.ts). When the FINAL
   * phase carries a bucket, the campus total resolves as a BAND (exact
   * scalar null) — a range never collapses into a scalar.
   */
  capacityBucket?:
    | 'under-10mw'
    | '10-50mw'
    | '50-200mw'
    | '200-500mw'
    | '500-1000mw'
    | '1000-1500mw'
    | 'over-1500mw'
    | 'over-200mw' // legacy back-compat only — resolvable, never offered as a new choice
    | 'undecided';
  /**
   * Provenance of `capacityMW` when it was DERIVED rather than typed —
   * 'site-max-power-study' means the max-power stamp wrote this phase's
   * cumulative capacity from the study's facility MW. Dropped by the
   * capacity writers on any manual edit, so a stale marker can never
   * describe a hand-entered figure. Absent = user-declared.
   */
  capacitySource?: 'site-max-power-study';
  /** Target commercial operation date (COD), ISO YYYY-MM-DD. Null = TBD. */
  targetInServiceDate: string | null;
  /**
   * Coarse COD bucket when `targetInServiceDate` is absent. Mirrors the
   * top-level `ProjectIntent.timeline` pair — writers must keep the two
   * fields consistent via `withPhaseInServiceDate()` /
   * `withPhaseTimelineBucket()` (src/lib/projectIntent.ts).
   *
   * DERIVED-ONLY since the intent-input audit: the form's COD "Range" mode was
   * removed (a bucket-only phase resolves to a null in-service date, so no
   * deterministic consumer ever read it). The form now writes this only as the
   * bucket derived from the exact date, or `'undecided'` when the date is
   * cleared. A legacy phase may still carry a standalone bucket — it renders
   * read-only in the form and in the phasing prompt block.
   */
  codTimeline?: PhaseCodTimeline;
  /** Primary power source for this phase. Auto-synced from `powerSourceConfig`. */
  powerSource: PowerSourceKey;
  /**
   * Optional hybrid power mix for this phase. When set with components,
   * supersedes the coarse `powerSource` dropdown for display and prompts.
   */
  powerSourceConfig?: PhasePowerSourceConfig;
  /**
   * Intended operating duty of this phase's on-site generation, for the
   * regulatory screen's air-permit question. OPTIONAL and additive; absent
   * reads as unknown and is never inferred from a capacity factor, operating
   * hours or an equipment rating. Emergency approval is not prime-power
   * permission. An explicit "Unknown" answer is recorded with the
   * `fieldSource['phases.<id>.generationDuty'] = 'user-unknown'` marker.
   */
  generationDuty?: 'emergency-standby' | 'peaking' | 'prime-continuous' | 'mixed' | 'unknown';
  /**
   * DEPRECATED — per-phase IT-side cooling override.
   *
   * The per-phase cooling (override) dropdown was removed from the Project
   * Intent form: the IT-side cooling loop (`coolingStrategy`) is now a
   * SITE-LEVEL decision only (`intent.coolingStrategy`). This optional field
   * is retained solely so legacy persisted phases keep type-checking; nothing
   * in the form writes it any more and all resolvers read the site-level
   * `intent.coolingStrategy` directly. Do NOT add new readers.
   */
  coolingStrategy?: 'air' | 'liquid' | 'hybrid' | 'undecided';
  /**
   * Per-phase override of `intent.bopHeatRejection` (BoP heat-rejection /
   * cold-end). ADDITIVE + optional — see `ProjectIntent.bopHeatRejection`.
   * When set, takes priority over the intent-level value for THIS phase's
   * `resolveConfigPue` estimate. Absent → inherit the intent-level value.
   */
  bopHeatRejection?: BopHeatRejection;
  /**
   * Per-phase override of `intent.coolingGeneration` (BTM cooling-GENERATION
   * machine axis). ADDITIVE + optional — see `ProjectIntent.coolingGeneration`
   * and `CoolingGeneration`. When set, takes priority over the intent-level
   * value for THIS phase only. Absent → inherit the intent-level value.
   */
  coolingGeneration?: CoolingGeneration;
  /**
   * Adopted simulation PUE for THIS phase (dimensionless, facility ÷ IT).
   *
   * DEFERRED WRITE: the EO engineering simulation that computes a
   * site-specific PUE for this phase is a later deep-sim follow-up — nothing
   * writes this field yet. When present AND `adoptSimulatedPue === true`, the
   * power-stack resolver treats it as the AUTHORITATIVE PUE for this phase
   * (pueSource = 'simulation'), overriding the config estimate / target /
   * user value. Optional, back-compatible: absent → resolver falls through to
   * the user / target / config-estimate / default chain.
   */
  simulatedPueValue?: number;
  /**
   * User toggle to ADOPT the simulated PUE (`simulatedPueValue`) for this
   * phase. Explicit + user-controlled (never silent): the simulation result
   * is only used when the user opts in. Absent / false → the simulated value
   * is ignored even if present. Optional, back-compatible.
   */
  adoptSimulatedPue?: boolean;
  /** Optional override — when set, takes priority over `intent.workloadProfile`. */
  workloadProfile?: 'ai-training' | 'ai-inference' | 'hpc' | 'enterprise' | 'colocation' | 'edge' | 'mixed';
  /**
   * @deprecated Free-text per-phase offtaker. The form input was removed
   * (intent-input audit — prompt-only, no deterministic report / SED consumer).
   * Retained as optional only so legacy persisted phases keep type-checking;
   * nothing writes or reads it any more. Do NOT add new readers.
   */
  customerOrOfftaker?: string;
  /** Phase ids this phase depends on — drives sequencing risk derivation. */
  dependencies?: string[];
  /**
   * Current execution state. DEPRECATED + OPTIONAL — the per-phase STATUS
   * picker was removed from the Project Intent form (every entered phase is
   * now treated as ACTIVE). Retained as optional only so legacy persisted
   * intents that still carry a `status` keep type-checking; nothing in the
   * form writes it any more and downstream readers no longer filter on it
   * (a phase's presence implies it is active, not decommissioned).
   */
  status?: ProjectPhaseStatus;
  /**
   * @deprecated Free-text per-phase notes. The form input was removed
   * (intent-input audit — no report-content or SED consumer) and the report
   * prompt no longer renders it. Retained as optional for legacy persisted
   * phases: the ONLY remaining readers are the two relax-only post-generation
   * audit helpers (`intentDeclaredVoltagesKV`, `buildIntentDeclarationsForJudge`)
   * so already-stored declarations keep suppressing false publish blocks
   * (Redhead RCA). Do NOT add new readers.
   */
  notes?: string;
  /** Optional phase-scoped footprint in acres (KMZ / boundary import). */
  areaAcres?: number;
  /**
   * Multi-Parcel Site Boundary (MP pack) — Phase 2 cross-pack scaffold.
   *
   * Optional id of the parcel this phase is scoped to. References
   * `SiteEvidenceDocument.parcels[].id` (MP4). When set, downstream
   * consumers can render "Phase 1 on main parcel, Phase 2 on substation
   * parcel" without re-resolving the parcel registry.
   *
   * Absent = phase is not parcel-scoped (legacy single-parcel project or
   * a phase that spans every parcel). No runtime logic is wired against
   * this field yet — it is forward scaffolding only; UI / resolver
   * changes land in MP Phase 3.
   */
  parcelId?: string;
  /** Audit metadata. */
  updatedAt?: string;
  updatedBy?: string;
}

/** Display label for a power source — single source of truth. */
export const POWER_SOURCE_LABEL: Record<PowerSourceKey, string> = {
  'grid':          'Grid',
  'gas-cc':        'Gas CC',
  'gas-sc':        'Gas SC',
  'smr':           'SMR',
  'large-nuclear': 'Large nuclear',
  'solar-bess':    'Solar + BESS',
  'wind-bess':     'Wind + BESS',
  'hybrid':        'Hybrid',
  'undecided':     'Undecided',
};

export const PHASE_POWER_COMPONENT_LABEL: Record<PhasePowerComponentKey, string> = {
  'recip':           'Recip engines',
  'gas-turbine':     'Gas turbines',
  'fuel-cell':       'Fuel cells',
  'battery':         'Battery (BESS)',
  'grid':            'Grid import',
  'solar-bess':      'Solar + BESS',
  'wind-bess':       'Wind + BESS',
  'smr':             'SMR',
  'large-nuclear':   'Large nuclear',
};

export const GRID_CONNECTION_LABEL: Record<GridConnectionMode, string> = {
  'islanded':        'Islanded',
  'grid-connected':  'Grid-connected',
  'undecided':       'Undecided',
};

/**
 * Annual average DATA-CENTER DEMAND divided by the declared IT/facility load.
 * Values are percentages in (0, 100], and `low <= high`. Equal endpoints are
 * an exact operator value. This is intentionally unrelated to generation
 * capacity factor (`powerSourceConfig.componentParams.*.capacityFactorPct`).
 */
export interface AnnualDemandUtilizationRangePct {
  low: number;
  high: number;
}

/**
 * A single water-supply kind a project can draw from. `'none'` is intentionally
 * NOT a member — an absent / empty selection is the "undecided" state (see
 * `ProjectIntent.waterSource`).
 */
export type WaterSourceKind = 'municipal' | 'groundwater' | 'surface' | 'reuse';

export interface ProjectIntent {
  /** Screening workflow choice only; never a capacity or evidence assertion.
   * Absent preserves the legacy proposal-review flow. */
  screeningMode?: 'recommend' | 'evaluate-proposal';
  /** Explicitly promoted Generation & Impact scenario; drafts never set this. */
  promotedGenerationScenarioId?: string;
  /** Integrity stamps for the immutable promoted scenario snapshot. */
  promotedGenerationInputHash?: string;
  promotedGenerationResultHash?: string;
  /** ISO timestamp of the promotion decision. */
  promotedGenerationPromotedAt?: string;
  /**
   * The candidate set the promoted scenario was chosen FROM, in the order the
   * engineer compared them. Always contains the promoted id when present.
   *
   * Promotion is a funnel: exactly one scenario becomes authority. Without this,
   * the fact that a comparison happened at all is destroyed at that boundary —
   * the report, the SED and the go/no-go record only the winner, and the
   * rationale ("we also screened these three and here is why they lost") is
   * unrecoverable. These ids are EVIDENCE OF METHOD, never a second authority:
   * nothing downstream may read a compared scenario as a duty basis.
   */
  comparedGenerationScenarioIds?: string[];

  /**
   * How the power figure was established.
   *
   * ABSENT on every legacy intent ⇒ read as 'declared-load' (the user typed a
   * number or picked a bucket). 'site-max-power' means the MW was DERIVED by the
   * max-power envelope study rather than declared — `powerRequirementMW` is
   * still written so every downstream consumer keeps working unchanged, and
   * `fieldSource.powerRequirementMW` records the provenance.
   */
  powerSizingMode?: 'declared-load' | 'site-max-power';

  /**
   * Stamped max-power envelope result. Same provenance discipline as the four
   * promoted-scenario fields above: enough hashes to DETECT staleness, plus a
   * denormalised headline so intent readers need no study body.
   *
   * The land ceiling is LAYOUT-CONDITIONAL, so the layout and parcel hashes are
   * part of the staleness contract — not decoration.
   */
  maxPowerStudy?: {
    scenarioId: string;
    scenarioInputHash: string;
    scenarioResultHash: string;
    studySchemaVersion: number;
    studyModelVersion: string;
    /** Hash of the frozen study body stored at `site.maxPowerStudySnapshot`. */
    studyHash: string;
    layoutPublicationId?: string;
    layoutVersion?: number;
    layoutHash?: string;
    parcelId?: string;
    /** Denormalised headline — the campus the envelope supports. */
    firmNetMW: number;
    facilityMW: number;
    itLoadMW: number;
    bindingConstraintIds: string[];
    /** NEVER 'exact'; mirrors the study's own bound-quality discipline. */
    boundQuality:
      | 'bounded-by-documented-constraints'
      | 'upper-bound-undocumented-constraints'
      | 'search-ceiling-reached';
    stampedAt: string;
    stampedBy?: string;
  };
  /**
   * PHASE POWER PLAN stamp — provenance + staleness record for an APPLIED
   * per-phase power recommendation (recommendPhasePowerPlan → apply). Lives
   * on the INTENT (one campus-coherent plan; phases are interdependent
   * through cumulative floors), mirroring the `maxPowerStudy` stamp pattern:
   * the applied values live in `phases[].powerSourceConfig.componentParams`
   * (every consumer keeps working blind); this block only records what was
   * applied and the input hashes that dirty it. Staleness resolution:
   * `phasePowerPlanStalenessReason` (lib/powerAvailability). ADDITIVE +
   * presence-gated — absent on every intent until a plan is applied.
   */
  phasePowerPlan?: {
    /** Engine model version — bump on algorithm changes. */
    planVersion: string;
    /** Hash over the full canonical input slice (intent ⊕ availability ⊕ envelope). */
    inputsHash: string;
    /** Per-input hashes so the staleness reason can name WHAT changed. */
    subHashes: { intentSlice: string; availability: string; envelope: string };
    appliedAt: string;
    appliedBy?: string;
    /** What was applied, per phase (cumulative MW per source). */
    appliedPhases: Array<{
      phaseId: string;
      recommendedLoadMW: number;
      perSource: Array<{ key: PhasePowerComponentKey; mw: number }>;
    }>;
  };
  /**
   * Authoritative workload classification. New code reads/writes this field.
   * The legacy `projectType` is preserved on existing records for back-compat
   * but no new code should write to it — readers go through
   * `effectiveWorkloadProfile(intent)` (src/lib/workloadProfile.ts) which
   * falls back to `projectType` when `workloadProfile` isn't set yet.
   */
  workloadProfile?: 'ai-training' | 'ai-inference' | 'hpc' | 'enterprise' | 'colocation' | 'edge' | 'mixed';
  /** @deprecated Read via `effectiveWorkloadProfile(intent)`; kept for legacy data only. */
  projectType?: 'ai-datacenter' | 'hpc' | 'edge' | 'colocation' | 'enterprise' | 'undecided';
  /**
   * Bucket — kept for back-compat; ALWAYS populated. When the user enters
   * an exact MW via `powerRequirementMW`, this bucket is auto-derived from
   * it via `bucketFromMw()` so scoring / tier-binning logic that switches
   * on the categorical value keeps working unchanged.
   */
  powerRequirement:
    | 'under-10mw'
    | '10-50mw'
    | '50-200mw'
    | '200-500mw'
    | '500-1000mw'
    | '1000-1500mw'
    | 'over-1500mw'
    | 'over-200mw' // legacy back-compat only — never emitted by bucketFromMw()
    | 'undecided';
  /**
   * Exact MW — preferred when the user knows the number precisely. Optional.
   * When set, `powerRequirement` is auto-derived from it via `bucketFromMw()`
   * so downstream readers can resolve the exact value via `effectivePower()`
   * (src/lib/projectIntent.ts) while bucket-based consumers keep working.
   */
  powerRequirementMW?: number;
  // ─────── Power-semantics basis (R-FIN-POWER-STACK) ───────
  // These OPTIONAL, back-compatible fields declare what `powerRequirementMW`
  // (and each `phase.capacityMW`) MEAN — they do NOT change the entered figure.
  // The resolver in `src/lib/powerStack.ts` reads them to convert between the
  // IT / facility / grid / apparent / generation layers of the power stack.
  /**
   * What basis the entered MW figures represent.
   *   - 'facility' — total facility / grid demand (IT load × PUE, incl. cooling
   *     + electrical losses). This is the DEFAULT when the field is absent.
   *   - 'it-load'  — server / critical IT load only (excludes cooling overhead).
   * Default applied in `resolvePowerStack` (see DEFAULT in powerStack.ts).
   */
  powerBasis?: 'it-load' | 'facility';
  /**
   * Power Usage Effectiveness — facility power ÷ IT power. Converts between the
   * IT-load and facility bases (facility = IT × PUE). Default applied in the
   * resolver (`DEFAULT_PUE`) when absent.
   */
  pueValue?: number;
  /**
   * Power factor (real power ÷ apparent power). Converts real MW to apparent
   * MVA (MVA = MW ÷ PF) — the rating basis for transformers / switchgear.
   * Default applied in the resolver (`DEFAULT_POWER_FACTOR`) when absent.
   */
  powerFactor?: number;
  /**
   * Expected annual DATA-CENTER DEMAND utilization / load-factor range (%).
   * This is the average served IT/facility demand divided by the declared MW
   * ceiling, used to tighten annual resource screening. It MUST NOT be filled
   * from a generator, grid, solar, wind, gas, or nuclear capacity factor.
   *
   * Absent = not recorded. A deliberate "Unknown / not yet decided" remains
   * absent and is distinguished by
   * `fieldSource.annualDemandUtilizationRangePct === 'user-unknown'`.
   */
  annualDemandUtilizationRangePct?: AnnualDemandUtilizationRangePct;
  /**
   * Legacy cooling vocabulary. Writers continue to write the literal
   * strings here (back-compat). New readers should route through
   * `normaliseCooling()` (src/types/cooling.ts) which projects this
   * + the two other legacy vocabularies (`coolingPreference`,
   * `coolingType`) onto the canonical `CoolingType` — see IC2.
   */
  coolingStrategy: 'air' | 'liquid' | 'hybrid' | 'undecided';
  /**
   * BoP (balance-of-plant) heat-rejection / cold-end method — the
   * COOLING-SYSTEM side, distinct from the IT-side `coolingStrategy`. ADDITIVE
   * + optional; it refines the config-derived PUE estimate (`resolveConfigPue`)
   * and determines whether the facility's cooling is heat-usable (and thus
   * eligible for the BTM waste-heat credit when on-site thermal generation is
   * present). Absent / `'unknown'` → the estimate uses the conservative base
   * and is flagged an estimate. See `BopHeatRejection` and
   * `src/lib/powerStack.ts:resolveConfigPue`. A phase may override this via
   * `ProjectPhase.bopHeatRejection`.
   */
  bopHeatRejection?: BopHeatRejection;
  /**
   * BTM cooling-GENERATION machine axis — HOW cooling is produced (absorption /
   * vapour-compression-electric / steam-driven / free-cooling). ADDITIVE +
   * optional and ORTHOGONAL to both the rack-side `coolingStrategy` and the
   * cold-end `bopHeatRejection`. Estimate-flaggable like `bopHeatRejection`.
   * Downstream readers should derive it via `projectCoolingGeneration()` rather
   * than assume it is set. A phase may override this via
   * `ProjectPhase.coolingGeneration`. See `CoolingGeneration`.
   */
  coolingGeneration?: CoolingGeneration;
  nuclearInterest: 'primary' | 'considering' | 'no' | 'undecided';
  /**
   * Site-level generic reactor design class (v1). Shown when nuclearInterest
   * is primary or considering. Vendor-specific catalog deferred to v2.
   */
  reactorDesignClass?: import('../config/reactorDesignCatalog').ReactorDesignClassId;
  /** Optional vendor-specific design (v2) — must match parentClass of reactorDesignClass. */
  reactorVendorId?: string;
  /**
   * Bucket — kept for back-compat; ALWAYS populated. When the user enters
   * an exact in-service date via `inServiceDate`, this bucket is auto-derived
   * from it via `bucketFromInServiceDate()` so scoring / tier-binning logic
   * that switches on the categorical value keeps working unchanged.
   */
  timeline: 'under-2yr' | '2-5yr' | 'over-5yr' | 'undecided';
  /**
   * Exact target in-service date (ISO 8601 yyyy-mm-dd) — preferred when the
   * user knows the COD precisely (e.g. "2028-04-01"). Optional. When set,
   * `timeline` is auto-derived from it via `bucketFromInServiceDate()` so
   * downstream readers can resolve the exact horizon (months-to-COD)
   * via `effectiveTimeline()` (src/lib/projectIntent.ts) while
   * bucket-based consumers keep working.
   */
  inServiceDate?: string;
  priority: 'cost' | 'speed' | 'sustainability' | 'resilience' | 'undecided';
  /** Custom scoring weights — overrides the use-case preset. Values should sum to ~1.0. */
  customWeights?: {
    grid: number;
    cooling: number;
    site: number;
    regulatory: number;
    logistics: number;
  };
  /**
   * Intent Prefill IP4 — provenance of each ProjectIntent field. Populated
   * when the user accepted a `suggestProjectIntent` chip (the source tier
   * is stamped on the field they accepted). Manual edit surfaces may stamp
   * `user`; an explicit "Unknown / not yet decided" answer stamps
   * `user-unknown`. That additive marker is important: the underlying field
   * remains `undefined` / `undecided` / `unknown` (and therefore is NEVER
   * treated as a known fact), while persistence, Path to GO and reports can
   * distinguish a deliberate unknown from a field the user never touched.
   *
   * Stored as a sparse partial keyed by ProjectIntent field name (or a dotted
   * nested path such as `offtake.priceBasis`); values
   * are the `IntentSuggestionSource` enum from
   * `src/lib/suggestProjectIntent.ts` ('prior-report' / 'document-extracted'
   * / 'site-context' / 'archetype-default' / 'dam-default' / 'fallback', plus
   * the authoring states 'user' / 'accepted' / 'manual' / 'user-unknown').
   * Typed as `string` here to avoid a circular import; readers should
   * widen via `as IntentSuggestionSource`.
   */
  fieldSource?: Partial<Record<keyof Omit<ProjectIntent, 'fieldSource' | 'customWeights'>, string>>
    & Partial<Record<string, string>>;

  // ─────── Project Phasing (PP1) ───────
  /**
   * Optional phased rollout. When present and non-empty, downstream
   * surfaces treat the project as multi-phase:
   *   - Total/ultimate capacity = the FINAL phase's CUMULATIVE `capacityMW`
   *     (each phase stores total-at-completion, never an increment — the
   *     ultimate is NOT a sum; see `phaseIncrementMW()` for additive math)
   *   - LCOE blends across phases (weighted by capacity × years operated)
   *   - Action plan timeline shows per-phase Gantt
   *   - SCM critical path computes per-phase
   *   - Financial CAPEX/OPEX schedule per phase
   * When absent or empty, the existing single-phase model applies.
   * Read via `effectivePhases(intent)` so callers don't branch.
   */
  phases?: ProjectPhase[];

  // ─────── Expanded Project Intent (PP1) — 10 new optional fields ───────
  /**
   * Site sponsor — who is building / paying. Drives the report's framing
   * (hyperscaler vs IPP vs sovereign vs enterprise) and the financing
   * structure assumptions in FA narrative. All optional.
   */
  siteSponsor?: {
    name: string;
    type: 'hyperscaler' | 'ipp' | 'sovereign' | 'enterprise' | 'consortium' | 'unknown';
  };
  /**
   * @deprecated Primary offtaker / customer (free-text). The form input was
   * removed (intent-input audit): despite the original intent it never drove
   * PPA risk or LCOE — it was a prompt-only pass-through. Retained as optional
   * only so legacy persisted intents keep type-checking; nothing reads it any
   * more. Do NOT add new readers.
   */
  counterparty?: string;
  /** Site selection constraints — US-only for now; expand to ISO countries later. */
  geographicConstraints?: {
    preferredStates?: string[];
    preferredCountries?: string[];
    avoidStates?: string[];
    avoidCountries?: string[];
  };
  /**
   * Carbon commitment — RE100 / 24/7-CFE / net-zero gates power-source
   * choices. PP9 auto-derives a risk when a phase uses gas under RE100.
   */
  carbonCommitment?: 'none' | 're100' | '24-7-cfe' | 'net-zero-2030' | 'net-zero-2050' | 'custom';
  /** Free-text rationale when `carbonCommitment === 'custom'`. */
  carbonCommitmentNote?: string;
  /** Uptime Institute tier target — Tier IV is 2-3x CAPEX vs Tier III. */
  datacenterTierTarget?: 'I' | 'II' | 'III' | 'IV';
  /** Redundancy class — drives transformer / UPS sizing in BoM (SCM2). */
  redundancyClass?: 'N' | 'N+1' | '2N' | '2N+1';
  /**
   * @deprecated Target PUE. The form input was removed (intent-input audit): it
   * fed the SAME resolved PUE as `pueValue`, one precedence step lower, and let
   * a stored target defeat a recorded-UNKNOWN `pueValue`. Retained as optional
   * for legacy persisted intents ONLY — `resolvePueWithSource` still falls back
   * to it (source `'design-target'`) so existing sites' power stack, water
   * screening and SED `cooling.resolvedPue` do not move. The form shows it in
   * the single PUE box and drops it on the first edit; marking PUE unknown
   * drops it too. New code must write `pueValue`.
   */
  pueTarget?: number;
  /** Template flag — "build 1 of 12" hyperscaler campuses. Surfaces replicability commentary. */
  modularReplicableFlag?: boolean;
  /** Labor pool preference — affects construction schedule + cost regionally. */
  laborPoolPreference?: 'union' | 'non-union' | 'either';
  /** Decommissioning plan provided? NRC requires for SMR projects (compliance flag). */
  decommissioningPlanProvided?: boolean;

  // ─────── BTM-feasibility development inputs (P1 — field-architecture audit §3.6/§4) ───────
  // SIX net-new canonical BTM intent fields. All OPTIONAL with an
  // 'undecided' / 'unknown' / 'none' default so they NEVER hard-gate
  // generation. ADDITIVE — legacy intents pass through unchanged. Each
  // surfaces in the (now-ungated) report-gen review, is captured into
  // `projectIntentAtGen`, and is injected into the relevant report-type
  // prompt only when set (no fabricated values). See audit §4 for the
  // per-report-type consumer set.

  /**
   * Grid interconnection request state — where the project sits in the
   * ISO/RTO / utility interconnection process. Consumers:
   * power-availability, interconnection, btm-feasibility. Flat enum + two
   * optional refinements (queueId text, studyPhase) mirroring the
   * `siteSponsor` object idiom. `queueStatus: 'none'` / absent → undecided.
   */
  interconnectionStatus?: {
    /** Current utility-service or generator-interconnection milestone.
     * Executed load service is an accepted customer service agreement, not
     * generator LGIA approval, energization or completed commissioning. */
    queueStatus: 'none' | 'pre-application' | 'queued' | 'study' | 'executed-LGIA' | 'executed-load-service';
    /** Optional ISO/utility queue position identifier (free-text). */
    queueId?: string;
    /** Current or last accepted study phase; retain it after agreement acceptance. */
    studyPhase?: 'feasibility' | 'system-impact' | 'facilities';
  };

  /**
   * Natural-gas / fuel-supply access for gas-driven BTM. Consumers:
   * btm-feasibility, power-availability (feeds the gas fuel-supply
   * diligence). `supply: 'none'` / absent → undecided. Optional lateral
   * distance refines the deliverability assessment.
   */
  fuelAccess?: {
    /** Gas supply state at the site. */
    supply: 'none' | 'near-pipeline' | 'lateral-required' | 'firm-contracted';
    /** Optional distance to the nearest usable pipeline (km). */
    distanceKm?: number;
  };

  /**
   * Water source(s) + rights for the cooling-water gate. Consumers:
   * water-resource, btm-feasibility. The `coolingVsWater` reasoning reads
   * declared water availability from this field.
   *
   * A project may draw from MORE THAN ONE supply, so the canonical field is
   * `sources` (multi-select). Empty / absent `sources` (and absent object) →
   * undecided. `rightsStatus` defaults to 'none'.
   *
   * ALWAYS read the effective sources through `waterSourceKinds()` in
   * `lib/intent/waterSource.ts` — never read `.source` / `.sources` directly.
   * That helper resolves the canonical `sources` array AND the legacy single
   * `source` scalar, so persisted sites written before the multi-select
   * migration keep resolving correctly. Do not remove `source`: it is a live
   * back-compat read path for stored data + single-value context ingestion.
   */
  waterSource?: {
    /**
     * @deprecated Legacy single-source scalar. New writes populate `sources`;
     * this remains only so persisted intents and single-value context-fill
     * still resolve through `waterSourceKinds()`. Never read directly.
     */
    source?: WaterSourceKind | 'none';
    /** The project's water source(s) (multi-select). Empty / absent → undecided. */
    sources?: WaterSourceKind[];
    /** Water-rights / permit status. */
    rightsStatus?: 'none' | 'applied' | 'permitted';
  };

  /**
   * @deprecated Site control / land tenure. The form + Path-to-GO inputs were
   * removed (intent-input audit): the documented "site-control risk dimension"
   * was never implemented — the value only reached LLM prompt text. Retained
   * as optional for legacy persisted intents; the ONLY remaining reader is the
   * relax-only post-generation audit helper `buildIntentDeclarationsForJudge`
   * (already-stored declarations keep suppressing false absence flags). Do NOT
   * add new readers.
   */
  landControl?: 'owned' | 'under-option' | 'LOI' | 'greenfield-uncontrolled' | 'undecided';

  /**
   * Offtake / PPA structure. Consumers: btm-feasibility, power-availability.
   * `offtake.priceBasis` / the optional `pricePerMwhUsd` connect to
   * `financialAssumptions.ppaPricePerMwhUsd` (the P0 test-fit revenue
   * wiring) when available. `structure: 'merchant'` is a valid declared
   * state; absent → undecided.
   */
  offtake?: {
    /** PPA / revenue structure. */
    structure: 'merchant' | 'contracted' | 'hybrid';
    /** Contract tenor in years (optional). */
    tenorYears?: number;
    /**
     * @deprecated Price basis. Form input removed (intent-input audit — a
     * prompt-only parenthetical). Legacy persisted values only; nothing reads it.
     */
    priceBasis?: 'fixed' | 'indexed' | 'unknown';
    /** Declared PPA price ($/MWh) — feeds financialAssumptions.ppaPricePerMwhUsd when set. */
    pricePerMwhUsd?: number;
  };

  /**
   * Capacity-credit / firm-capacity (ELCC) accreditation expectation.
   * Consumers: power-availability, btm-feasibility. The ELCC intent flows
   * to SED / reports (audit §4). `target: 'none'` is a valid state;
   * absent → undecided.
   */
  capacityCreditIntent?: {
    /** Firm-capacity accreditation target. */
    target: 'none' | 'partial' | 'firm';
    /** Optional firm-capacity / accreditation target (MW). */
    targetMW?: number;
    /** Optional free-text accreditation note (market, methodology). */
    accreditationNote?: string;
  };

  /**
   * P3-6 — declared on-site WASTE-HEAT OFFTAKE. A confirmed waste-heat
   * offtaker (district heating, greenhouse, industrial host, CCHP loop) is the
   * thermal-load signal that makes thermally-driven cooling (`absorption-thermal`
   * / `steam-driven-mechanical`) and the `absorption-cchp` heat-rejection sink
   * VIABLE even when the predicate over the power mix is ambiguous, and it
   * unlocks the waste-heat utilisation credit. Consumers: btm-feasibility,
   * water-resource, environmental (CWA 316(a) thermal discharge).
   *
   *   `status`         — declared maturity of the offtake arrangement.
   *   `offtaker`       — optional free-text host / counterparty.
   *   `thermalLoadMwth`— optional usable thermal load the offtaker absorbs (MWth).
   *   `heatUsableFraction` — optional 0-1 fraction of rejected heat actually
   *                          usable by the offtaker (drives the utilisation credit).
   *
   * `status: 'none'` is a valid declared state (no offtake — pure rejection);
   * absent → undecided (the common case, byte-stable for legacy intents).
   */
  wasteHeatOfftake?: {
    status: 'none' | 'exploring' | 'LOI' | 'contracted';
    offtaker?: string;
    thermalLoadMwth?: number;
    heatUsableFraction?: number;
  };

  /**
   * Who supplies the data center's electricity, for the regulatory screen's
   * ownership / electricity-sale question. OPTIONAL and additive; absent →
   * unknown. Distinct from `siteSponsor.type` (sponsor archetype) and
   * `offtake.structure` (PPA revenue structure), neither of which is ever
   * mapped to this field.
   *
   *   `own-use`                — the operator generates for its own use;
   *   `third-party-onsite-sale`— a separate company sells on-site power to
   *                              the operator (legality depends on state law
   *                              and utility service-territory rights);
   *   `utility-supplied`       — the serving utility supplies the load;
   *   `unknown`                — not yet decided.
   *
   * `sellerName` is optional free text for the selling company.
   */
  electricitySupply?: {
    arrangement: 'own-use' | 'third-party-onsite-sale' | 'utility-supplied' | 'unknown';
    sellerName?: string;
  };

  /**
   * R-131-EPISTEMIC-GROUNDING-E — operator pre-attestation of factual states
   * known to the sponsor but absent from uploaded documents. Report generation
   * maps `no` → confirmed absence (operator attestation evidence); `unknown` /
   * unset → open question. Extensible via index signature for domain-specific keys.
   */
  attestations?: ProjectIntentAttestations;
}

/** R-131-EPISTEMIC-GROUNDING-E */
export type AttestationState =
  | 'yes'
  | 'no'
  | 'in-discussion'
  | 'in-progress'
  | 'unknown';

/** R-131-EPISTEMIC-GROUNDING-E */
export interface ProjectIntentAttestations {
  ppaSigned?: AttestationState;
  speFormed?: AttestationState;
  epcSigned?: AttestationState;
  gasSupplyFirm?: AttestationState;
  slotReservationBinding?: AttestationState;
  [key: string]: AttestationState | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-type preferences captured at preflight (Workflow Prompt 0).
// All fields optional so reports created before preferences existed keep loading.
// ─────────────────────────────────────────────────────────────────────────────

export interface FeasibilityPreferences {
  // SSOT — projectSizeMW, customerCategory, inServiceDate, riskTolerance,
  // powerSourcePreference, coolingPreference DELETED. Live canonically on
  // SiteEntity.projectIntent (powerRequirementMW, workloadProfile,
  // inServiceDate, priority, nuclearInterest, coolingStrategy respectively).
  // CI sentinel scripts/ci/checkNoSharedFieldsInPrefs.ts prevents
  // reintroduction. Historical reports may still carry the deleted keys on
  // their typedPreferencesAtGen snapshots — that's expected; nothing reads
  // them post-SSOT.
  kpiWeightOverrides?: Partial<Record<'grid' | 'cooling' | 'site' | 'regulatory' | 'logistics', number>>;
}

export interface EnvironmentalPreferences {
  expectedNepaPathway?: 'catex' | 'ea' | 'eis' | 'unknown';
  federalNexus?: boolean;
  speciesConcerns?: string[];
  tribalLandProximityKnown?: boolean;
  airBasinOverride?: 'attainment' | 'non-attainment-marginal' | 'non-attainment-serious' | 'auto';
  dischargeWaterTarget?: 'npdes' | 'zld' | 'reuse' | 'unknown';
}

export interface TestFitPreferences {
  targetFootprintAcres?: number;
  phasing?: 'single' | 'phased';
  phaseCount?: number;
  setbackOverrideFt?: number;
  parkingRatioPerEmployee?: number;
  stormwaterDetentionGal?: number;
}

export interface PowerAvailabilityPreferences {
  // SSOT — powerRequirementMW DELETED (lives canonically on
  // SiteEntity.projectIntent.powerRequirementMW). Sentinel
  // scripts/ci/checkNoSharedFieldsInPrefs.ts prevents reintroduction.
  timeToPowerCriticalDate?: string;
  renewablesPreference?: 'required' | 'preferred' | 'agnostic' | 'avoided';
  btmStrategy?: 'grid-only' | 'btm-only' | 'hybrid' | 'islanded';
  maxSubstationDistanceMiAmber?: number;
  maxSubstationDistanceMiRed?: number;
  curtailmentRiskTolerance?: 'low' | 'medium' | 'high';
}

export interface WaterResourcePreferences {
  // SSOT — coolingType DELETED (lives canonically on
  // SiteEntity.projectIntent.coolingStrategy, projected to the
  // 5-bucket CoolingType via normaliseCooling()). Sentinel
  // scripts/ci/checkNoSharedFieldsInPrefs.ts prevents reintroduction.
  waterRightsKnown?: boolean;
  droughtScenario?: '1-in-10' | '1-in-100' | 'both';
  wastewaterTarget?: 'npdes' | 'zld' | 'reuse';
}

export interface InterconnectionPreferences {
  latencyTargetsMs?: { aws?: number; azure?: number; gcp?: number };
  carrierDiversityMin?: 1 | 2 | 3;
  bandwidthGbps?: number;
  ixPeeringRequired?: boolean;
  submarineCableProximityImportant?: boolean;
}

/**
 * PSR-1 (row 0.99290) — Public Sentiment Report preferences.
 *
 * Scaffold-only field set; PSR-2 will fold in the SENTIMENT INDEX
 * weighting overrides (theme-weighting, geographic-radius caps, etc.).
 * Empty is a valid state — the orchestrator falls through to defaults.
 */
/** Ordered diligence question in a user-defined Custom Brief. */
export interface CustomBriefQuestion {
  id: string;
  text: string;
}

/**
 * Custom Brief — user-defined report type. Captured at wizard preflight
 * and stamped on the report at generation time.
 */
export interface CustomBriefPreferences {
  briefName: string;
  objective?: string;
  questions: CustomBriefQuestion[];
  contextNotes?: string;
  counterparties?: string;
  keyDates?: string;
}

export type CustomBriefOverallVerdict = 'PROCEED' | 'CAUTION' | 'STOP' | 'INCONCLUSIVE';

/** R-131-EPISTEMIC-GROUNDING-A — structured finding with evidence state. */
export type {
  FindingClaim,
  FindingClaimEvidence,
  FindingClaimEntry,
  FindingEvidenceState,
  OpenQuestionAnswerValue,
  PartitionedFindingClaims,
} from '../lib/findingClaim';

/** Structured executive summary for Custom Brief reports (v2 schema). */
export interface CustomBriefExecutiveSummary {
  overallVerdict: CustomBriefOverallVerdict;
  keyFindings: import('../lib/findingClaim').FindingClaim[];
  materialRisks: import('../lib/findingClaim').FindingClaim[];
  recommendedDecision: string;
}

/** Project context block — site framing + intent restatement. */
export interface CustomBriefProjectContext {
  siteSummary: string;
  intentRestatement: string;
}

export type CustomBriefNextActionPriority = 'immediate' | 'pre-FID' | 'pre-close';

export interface CustomBriefRecommendedNextAction {
  priority: CustomBriefNextActionPriority;
  action: string;
  rationale: string;
}

/** Structured conclusion for Custom Brief reports (v2 schema). */
export interface CustomBriefConclusion {
  topStrengths: string[];
  keyRisks: import('../lib/findingClaim').FindingClaim[];
  recommendedNextActions: CustomBriefRecommendedNextAction[];
}

/** One answered question persisted on a generated Custom Brief report. */
export interface CustomBriefAnswer {
  questionId: string;
  question: string;
  questionNumber?: number;
  questionSlug?: string;
  findings: string;
  evidence: string;
  redFlags: import('../lib/findingClaim').FindingClaimEntry[];
  confidence: 'high' | 'medium' | 'low';
}

/** Wizard preferences for the Executive Summary synthesis report. */
export interface ExecutiveSummaryPreferences {
  /** Report ids at this site to synthesize (non–executive-summary types). */
  sourceReportIds: string[];
  /** Optional user focus notes (deal stage, audience, emphasis). */
  synthesisFocus?: string;
}

/** One domain block in the cross-report synthesis section. */
export interface CrossReportDomainBlock {
  domain: string;
  synthesis: string;
  sourceReportTypes: string[];
}

/** Snapshot of a source report stamped at executive-summary generation. */
export interface ExecutiveSummarySourceUsed {
  reportId: string;
  reportType: import('../config/reportTypes').ReportTypeId;
  title: string;
  generatedAt?: string;
  verdictTier?: string;
}

/**
 * FIN-1 (wf w7nuyquwd) — compact deterministic Financial Viability stamp.
 *
 * Persisted on `SiteRagReport.financialAnalysis` at create / recompute (NO
 * LLM). Holds the headline metrics, Monte-Carlo bands, binding constraints,
 * and the banded viability score so aggregators read it cheaply. Built from
 * the deterministic engines (`projectFinance.recompute`, `finDecision/*`); this
 * struct NEVER re-derives the math — it is a serialized snapshot of it.
 */
export interface FinancialAnalysisStamp {
  /** Absent on legacy generation/PPA snapshots. */
  modelKind?: 'generation-ppa' | 'data-center-capacity-fee';
  /** Calculated fictional economics, explicitly ineligible for real-world
   * investment decisions. Absent from legacy and real manual-model stamps. */
  decisionScope?: {
    kind: 'hypothetical-scenario';
    scenarioId: string;
    calculated: true;
    realWorldEligible: false;
  };
  /** Render-only canonical public snapshot; no owner, documents, quotes or model authority. */
  dataCenterPublic?: { schemaVersion: 1; sections: Record<string, string> };
  dataCenter?: {
    model: import('./dataCenterFinancial').DataCenterFinancialModelV1;
    result: import('./dataCenterFinancial').DataCenterFinancialResult;
    lcoeApplicability: 'not-applicable';
  };
  /** Headline metrics from `projectFinance.recompute(resolveFinancialAssumptions(...))`. */
  headline: {
    lcoeUsdPerMwh: number | null;
    /**
     * WHICH levelised cost `lcoeUsdPerMwh` is (audit Q6 / 4.4). Four different
     * definitions print under one label across the app — this project's own
     * project-finance figure, its phased blend, a benchmark technology option,
     * and a simulated-yield levelisation. They answer different questions and
     * legitimately differ, so every surface must say which one it is showing.
     * Absent on reports stamped before the basis existed.
     */
    lcoeBasis?: import('../lib/lcoeBasis').LcoeBasis;
    /**
     * The same project in CONSTANT (real) dollars.
     *
     * `lcoeUsdPerMwh` above is NOMINAL — it is the flat PPA price at which this
     * project's NPV is zero, which is the right number for pricing a contract.
     * It is the wrong number for comparison: the benchmark pathways printed
     * beside it, and every published figure a reader will have in mind (NREL
     * ATB, Lazard, IEA), are REAL. Nominal sits above real by the inflation
     * wedge over the asset life, so putting them side by side without this
     * field manufactures a discrepancy that is purely a change of basis.
     *
     * Absent on reports stamped before the two bases were split (2026-08-20).
     */
    lcoeRealUsdPerMwh?: number | null;
    npvUsd: number | null;
    irrPct: number | null;
    /** Hurdle / target IRR the verdict bands IRR against (override → WACC). */
    hurdleRatePct: number | null;
    paybackYears: number | null;
    projectLifeYears: number | null;
    capexPerKwUsd: number | null;
    annualMwh: number | null;
    waccPct: number | null;
  };
  /**
   * Monte-Carlo NPV bands + probability of a positive NPV.
   *
   * BASIS WARNING (review 2026-08-19): these come from the PATHWAY comparison
   * engine, which prices benchmark technology options against a flat
   * value-of-served-load credit — NOT from this project's declared financial
   * model. `basis` names that explicitly; renderers MUST label the band and must
   * never present it as an uncertainty band around `headline.npvUsd`.
   */
  monteCarlo: {
    npvP10: number | null;
    /** The MC MEDIAN of the anchor pathway's NPV (not a deterministic point). */
    npvP50: number | null;
    npvP90: number | null;
    /** P(NPV>0) in [0,1] for the anchor pathway, on the pathway basis. */
    pPositiveNpv: number | null;
    /**
     * Seed + run count that ACTUALLY produced these bands (absent when no
     * pathway comparison ran). Methodology disclosure — must never be a
     * constant advertised independently of the run set.
     */
    seed?: number;
    runs?: number;
    /** What the band describes. `'pathway'` = benchmark technology economics. */
    basis?: 'pathway' | null;
    /** The pathway the band was measured on (the comparison's ranked leader). */
    anchorPathwayId?: string;
    anchorPathwayName?: string;
  };
  /**
   * FA-BASIS SENSITIVITY — a tornado over THIS project's own model.
   *
   * Distinct from `monteCarlo`, which is the benchmark-pathway band. The report
   * used to relabel those pathway percentiles as "Scenario NPV — Downside /
   * Base / Upside", so a reader saw a technology comparison presented as their
   * project's own range. This is the real thing: each driver perturbed ±`deltaPct`
   * around the canonical case, recomputed through the engine.
   *
   * Persisted so the section renders a snapshot rather than recomputing at view
   * time (which would silently drift from the verdict it sits beside).
   */
  sensitivity?: {
    /** Which metric the swings are measured on. */
    metric: 'lcoe' | 'irr' | 'npv';
    /** Symmetric perturbation, percent of base (20 ⇒ ±20%). */
    deltaPct: number;
    /** The metric at the unperturbed canonical case. */
    metricBase: number | null;
    /** One row per driver, already sorted by swing magnitude (descending). */
    rows: Array<{
      field: string;
      /** Human label for the driver. */
      label: string;
      baseValue: number | null;
      metricLow: number | null;
      metricHigh: number | null;
      swingAbs: number | null;
      /**
       * Set when a physical limit held this driver back from the full
       * ±`deltaPct` (a 90% capacity factor cannot go to 108%). The bar is then
       * NOT comparable with the others, and — because the tornado sorts by
       * swing — a clamped driver under-ranks. Rendered as a marker.
       */
      clampedBound?: 'low' | 'high' | 'both';
    }>;
  };
  /**
   * The project's OWN named scenarios (Base / Upside / Downside …), each
   * recomputed through the engine. Empty when the model defines none — in which
   * case the report must say so rather than borrowing the pathway band.
   */
  scenarioMetrics?: Array<{
    id: string;
    label: string;
    isCanonical: boolean;
    /** What the scenario changes, in relative terms ("capexTotalUsd −10%"). */
    summary: string;
    lcoeUsdPerMwh: number | null;
    npvUsd: number | null;
    irrPct: number | null;
    paybackYears: number | null;
  }>;
  /**
   * Why the pathway side is populated — or is not. `'none'` = the site has no
   * pinned comparison; `'load-failed'` = one exists but could not be read (the
   * report then carries FA-only economics and says so, instead of rendering an
   * empty Monte-Carlo section that looks like missing evidence).
   */
  pathwayStatus?: 'loaded' | 'none' | 'load-failed';
  /**
   * The engine's ACTUAL annual cashflow vector — one entry per project year,
   * index 0 = `baseYear`. Persisted so the cash-position exhibit plots the
   * model instead of interpolating a curve between NPV and payback.
   * `basis` names which engine produced it (and therefore the headline).
   */
  cashflow?: {
    basis: 'single-phase' | 'phased' | 'data-center-annual';
    /** Calendar year of index 0 (COD year, or the first drawdown year). */
    baseYear: number | null;
    /** Net cashflow per year in whole USD (negative = outflow). */
    flowsUsd: number[];
    /** Running cumulative position in whole USD. */
    cumulativeUsd: number[];
  };
  /** Recommended pathway id (from `recommendPathwayComparison`) when present. */
  recommendedPathwayId?: string;
  /** Pathway-comparison win-probability leader, when a comparison exists. */
  pWinLeaderPathwayId?: string;
  /** The auditable reasons the band landed where it did. */
  rationale: string[];
  /** Binding constraints that held the band down (empty when Viable). */
  bindingConstraints: string[];
  /** Banded verdict — null when inputs insufficient (tier resolves to 'none'). */
  band: 'green' | 'amber' | 'red' | null;
  /**
   * WHY a null band is null. `'inputs-insufficient'` = the engine could not
   * compute an NPV; `'inputs-incoherent'` = it could, but the CAPEX basis and
   * the revenue basis describe different plant (or the outputs breached a
   * plausibility ceiling), so banding them would mislead. Absent when a band
   * exists. Consumers must render a withheld verdict as WITHHELD — never as a
   * Conditional.
   */
  verdictWithheldReason?: 'inputs-insufficient' | 'inputs-incoherent' | 'scope-limited';
  /**
   * The deterministic action plan (owner + deadline tokens). Stamped ALWAYS,
   * including for a withheld verdict, so the action-plan section and the
   * roadmap can render guidance without the report having to carry a
   * fabricated `conclusion.recommendation` enum value.
   */
  actionPlan?: {
    /** One-line headline (verdict summary or the withheld explanation). */
    recommendation: string;
    /** Owner/deadline-token actions the roadmap parser extracts. */
    actions: string[];
  };
  /** 0-100 deterministic viability score (mirrors report.overallScore). */
  viabilityScore: number | null;
  /**
   * Two-engine LCOE divergence note — the project-finance LCOE vs the
   * report-corrected pathway LCOE. Surfaced (not hidden) in the methodology.
   */
  lcoeEngineDiffNote?: string;
  /**
   * ILLUSTRATIVE-ASSUMPTIONS BANNER — **LEGACY REPORTS ONLY. ZERO WRITERS.**
   *
   * Historically set when a site had NO entered financialAssumptions and the
   * engine was seeded from industry baselines (NREL ATB / Lazard / EIA) so the
   * report showed clearly-labelled illustrative economics instead of an empty
   * stub. That seeding path was DELETED in the 2026-08-19 audit (Phase 3.5):
   * banding a site on a 250-MW baseline it never declared published a verdict
   * about a project that does not exist. A non-computable model now yields the
   * engine's WITHHELD verdict plus an action plan naming the missing inputs.
   *
   * The field stays READ-ONLY so reports persisted before that change keep
   * rendering their banner (and stay honest about what they were built from).
   * Nothing writes it; do not add a writer without re-reading the audit row.
   */
  illustrativeAssumptionsNote?: string;
  /** ISO timestamp the stamp was computed. */
  computedAt: string;
  /**
   * Set when this stamp was RE-COMPUTED after generation because the financial
   * model changed under a mutable legacy generation report. Immutable
   * revisions and data-center annual models require normal regeneration.
   * Absent ⇒ the figures are exactly what generation produced.
   *
   * WHY IT MATTERS TO THE READER. A re-stamp updates the deterministic figures
   * — headline, cashflow, sensitivity, score, band — but it CANNOT rewrite the
   * written analysis, which is an LLM narrative. So a re-stamped report holds
   * current numbers next to prose that describes the earlier model, and the
   * staleness banner has to say WHICH half is stale. Without this marker it
   * would have to claim both are, which is false as soon as a re-stamp lands.
   *
   * `financialAssumptionsAtGen` is deliberately NOT advanced by a re-stamp: it
   * is the narrative's provenance, and moving it would clear the banner while
   * the prose is still stale.
   */
  restampedAt?: string;
}

export interface PublicSentimentPreferences {
  /**
   * Per-source aggregation window in days. Defaults to 365 (1y back) when
   * unset. Bounded [30, 1825] at the validator boundary. Surfaces in the
   * Trend Analysis section.
   */
  trendWindowDays?: number;
  /**
   * Comparable-site benchmark cohort size — how many similar BTM sites to
   * pull into the comparison band. Default 5; bounded [3, 12].
   */
  benchmarkCohortSize?: number;
  /**
   * Include comments / minutes from federally-recognised tribal
   * governments alongside municipal council records. Off by default
   * because tribal records have separate redaction guidance under
   * EO 13175; opt-in surfaces them explicitly.
   */
  includeTribalRecords?: boolean;
}

/**
 * FIN-1 (wf w7nuyquwd) — Financial Viability Report preferences.
 *
 * The financial body is computed LIVE from the deterministic project-finance
 * engine (projectFinance.recompute + finDecision/*) — these knobs only frame
 * the verdict / Monte-Carlo presentation, not the engine math (which is driven
 * by SiteEntity.financialAssumptions, the SSOT). All optional; empty falls
 * through to defaults. NO power-mix / sizing / cooling fields here (those live
 * canonically on projectIntent — checkNoSharedFieldsInPrefs sentinel).
 */
export interface FinancialPreferences {
  /**
   * Hurdle / target IRR (fractional, e.g. 0.12 = 12%) the verdict bands IRR
   * against. When unset, `scoreSiteFinancial` falls back to the engine's
   * resolved discount rate / WACC as the hurdle.
   */
  hurdleRateOverride?: number;
  /** Which pinned pathway-comparison case the report headlines (id). */
  pinnedPathwayComparisonId?: string;
  /** Surface IRA ITC/PTC tax credits as a financial adjustment (default on). */
  includeTaxCredits?: boolean;
  /** Surface a contracted PPA as a revenue adjustment when present. */
  includePpaAdjustment?: boolean;
}

export interface TypedPreferencesByType {
  feasibility?: FeasibilityPreferences;
  environmental?: EnvironmentalPreferences;
  'test-fit'?: TestFitPreferences;
  'power-availability'?: PowerAvailabilityPreferences;
  'water-resource'?: WaterResourcePreferences;
  interconnection?: InterconnectionPreferences;
  'supply-chain-risk'?: FeasibilityPreferences;
  'public-sentiment'?: PublicSentimentPreferences;
  'custom-brief'?: CustomBriefPreferences;
  financial?: FinancialPreferences;
  'executive-summary'?: ExecutiveSummaryPreferences;
}

/**
 * Lightweight metadata-only projection of a report — the shape that the
 * Firestore-fallback path (`store.repairIncompleteReports`,
 * `siteRagService` metadata-only save) writes when the full report's
 * `datasets`/`insights` payload couldn't be persisted (GCS upload
 * failed, legacy migration, list-page denormalised reads, etc.).
 *
 * Production_Hardening_Prompts.md PH-A2 items 8 + 9: the typed
 * contract previously claimed `datasets` and `insights` were
 * always-present, which let consumers do `report.datasets.X` and
 * crash at runtime on Firestore-fallback records. The new contract
 * makes those two fields optional on `SiteRagReport`; this helper
 * type names the metadata-only shape explicitly so dashboard / list
 * views can declare their intent ("I only need metadata") rather than
 * lying about the full shape.
 *
 * Consumers that need the full record (datasets, insights, recommendations,
 * btmOptions, etc.) should accept `SiteRagReport`. Dashboard /
 * list / picker views that only need the headline metadata should
 * accept `ReportListItem` and let TS reject `.datasets.X` accesses
 * at the type boundary.
 */
export interface ReportListItem {
  id: string;
  siteId?: string;
  reportType?: import('../config/reportTypes').ReportTypeId;
  siteName: string;
  address: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  overallScore: number;
  overallStatus: ReportOverallStatus;
  /** Headline verdict — surfaced on the dashboard list row + report card. */
  conclusion?: { recommendation: string };
  createdAt: string;
  version?: number;
  minorVersion?: number;
  /** True when only metadata was successfully persisted — the full
   *  `datasets`/`insights`/etc. payload is unavailable. Set by the
   *  Firestore-fallback path in `siteRagService`; consumers can
   *  surface a "metadata only — open to refresh" affordance. */
  _metadataOnly?: boolean;
}

export type ContextualFindingCategory =
  | 'intentAlignment'
  | 'projectDetails'
  | 'outcomeInfluence';

export interface ContextualKeyFinding {
  title: string;
  category: ContextualFindingCategory;
  detail: string;
  intentContext: string;
  influences: string;
  whyKey: string;
  validationWarning: string;
  updateFieldPath?: string;
  updateFieldLabel?: string;
  impact: 'positive' | 'negative' | 'neutral';
  source?: string;
  relevanceGate?: {
    appliesWhen: string;
    notApplicableWhen?: string;
  };
}

/**
 * C0-GEN — one section's editorial-narrative layer (Option B).
 *
 * EVERY field is optional: a layer may carry only a headline, or only a
 * narrative, or be omitted entirely. The render resolves missing fields
 * through `composeEditorialFallback`, so a partial / absent layer always
 * degrades to the deterministic Option-A strings.
 *
 * North-Star contract: this is a re-voicing of ALREADY-VERIFIED content,
 * never a new fact source. Every number is a verbatim figure already in the
 * verified section content; every quantitative claim carries a resolvable
 * `[Ref N]`. The grounding validators (`editorialGrounding.ts`) enforce this
 * before any layer is merged onto the report (Wave 2).
 */
export interface EditorialSectionLayer {
  /** Conclusion-as-title, ≤~90 chars, asserts the finding (never the topic). */
  editorialHeadline?: string;
  /** One-sentence conclusion-first thesis with 1-3 decisive numbers + [Ref N]. */
  editorialThesis?: string;
  /** Flowing judgement prose; restates ONLY verified facts; [Ref N] on numbers. */
  editorialNarrative?: string;
  /**
   * Optional pull-quote. `text` is a verbatim lift from the grounded prose;
   * `attribution` is composed DETERMINISTICALLY in code (report type + date),
   * never model-authored (a free-text attribution can fabricate a source).
   */
  pullQuote?: { text: string; attribution: string };
  /**
   * C0-GEN Wave 2 — per-section claims the editorial generator emits for the
   * re-voiced prose. Same `{ text, sources, sectionId }` shape as
   * {@link ReportClaim}; the Wave-2 hook merges the SURVIVING sections'
   * `editorialClaims[]` into `report.claims[]` so the citation-density KPI and
   * the R-66 citation-integrity verifier see the editorial prose's claims.
   * Present only on a generated layer; the deterministic fallback omits it.
   */
  editorialClaims?: import('./citation').ReportClaim[];
}

export const WATER_SCREENING_VIEW_SCHEMA_VERSION = 1 as const;

/** One scenario row of the saved "Modeled Water Use" table, pre-formatted. */
export interface WaterScreeningViewRow {
  scenarioId: string;
  label: string;
  /** Carries "benchmark", "screening" or "modeled" (the row's qualifier). */
  applicability: string;
  boundary: string;
  flowBasis: string;
  /** Formatted bands, unit once (`0.19–1.72 MGD`); null when not printed. */
  averageDay: string | null;
  annual: string | null;
  fullLoad: string | null;
  /**
   * Replaces the figures when the quantity is conditional (unverified dry
   * capacity): `value` (`potentially 0 gal/day`) and the condition it holds under.
   */
  conditional: { value: string; qualifier: string } | null;
  /** The adiabatic makeup on the dry path, laid out as a sub-row under this row. */
  assist: WaterScreeningViewAssist | null;
  /** Supporting sub-results: hourly weather basis, dry-cooling temperature screen. */
  details: string[];
}

/** The hourly adiabatic-assist estimate under a dry row, pre-formatted. */
export interface WaterScreeningViewAssist {
  label: string;
  /** The sub-row's qualifier (`modeled screening from hourly weather`). */
  basis: string;
  /** Annual-average daily makeup band. */
  averageDay: string;
  annual: string;
  activeHours: string;
  peakHour: string;
}

/**
 * The formatted deterministic water screen as the reader lays it out. Every
 * string is copied from the builder that writes `SiteRagReport.waterScreening`,
 * so the reader never formats a number of its own.
 */
export interface WaterScreeningView {
  schemaVersion: typeof WATER_SCREENING_VIEW_SCHEMA_VERSION;
  /** Recorded MW scope and basis (`500–1,000 MW facility load`). */
  scope: string;
  catalogVersion: string;
  /** Deterministic, code-written lead; no quantity. */
  lead: string;
  basis: string[];
  rows: WaterScreeningViewRow[];
  caveats: string[];
  notes: string[];
  /** Cited sources; the reader resolves each report reference by URL. */
  sources: Array<{ citation: string; url: string }>;
}

/**
 * Full report record. Use `ReportListItem` for metadata-only consumers
 * (dashboard, list views, pickers); `SiteRagReport` is the full record
 * with datasets, insights, recommendations, etc.
 *
 * PH-A2 8+9: `datasets` and `insights` are optional. They are absent
 * on Firestore-fallback records (when GCS save failed or the report
 * was loaded denormalised). Every consumer that reaches into
 * `.datasets.X` or `.insights.X` MUST guard the parent first.
 */
export interface SiteRagReport {
  /** Exact private source-bound static assessment frozen before source hashing. */
  gridLedStandbyPlanningAtGen?: import('./gridLedStandbyPlanning').GridLedStandbyPlanningSnapshot;
  /** Private server-built applicability decision; canonical Intent history is unchanged. */
  inactiveGenerationImpactAuthorityAtGen?: import('./gridLedStandbyPlanning').InactiveGenerationImpactAuthority;
  /** Safe optional assessment projection; no owner/document handles or private quotations. */
  gridLedStandbyPlanningProjection?: import('./gridLedStandbyPlanningProjection').GridLedStandbyPlanningReportProjection;
  /** Exact selected document basis frozen before canonical report/source hashing. */
  demonstrationProvenance?: import('./demonstration').FrozenDemonstrationBasisV1;
  /** Independently frozen SED source subset, even when direct report selection is empty. */
  demonstrationSedProvenance?: import('./demonstration').FrozenDemonstrationBasisV1;
  /** Public render-only projections retain this safe subset instead of private source handles. */
  demonstrationDisclosure?: import('./demonstration').DemonstrationDisclosureV1;
  /** Exact private reference-data source basis; retained in semantic identity. */
  refdataAcceptance?: import('./refdata').RefdataAcceptanceSourceBinding;
  id: string;
  /** True only when a pre-userState-doc flat body may contain decisions. */
  legacyUserStateBodyAvailable?: boolean;
  reportType?: import('../config/reportTypes').ReportTypeId;
  siteName: string;
  address: string;
  location: {
    lat: number;
    lng: number;
    address: string;
  };
  overallScore: number;
  overallStatus: ReportOverallStatus;
  /**
   * REG-SCORE #2 (scoring-engine audit, William's decision 2026-07-02) — the
   * LLM narrative's suggested score adjustment, kept SEPARATE from the
   * deterministic score. Pre-fix, `conclusion.insightModifier` silently
   * shifted `overallScore` by up to ±15 at gen time; `overallScore` is now
   * the PURE deterministic weighted mean and this labelled field carries the
   * AI's adjustment + rationale for display ("AI assessment adjustment: −4 —
   * <rationale>") wherever the overall score renders. Consumers MUST NOT
   * fold `delta` back into `overallScore` — the deterministic number is the
   * headline; this is visible context. Absent on legacy reports (whose
   * persisted overallScore may already include the modifier) and when the
   * modifier is 0/absent. `delta` is clamped to ±15 (the legacy bound).
   */
  aiScoreAdjustment?: { delta: number; rationale?: string };
  /**
   * EO-1 — Energy Orchestration simulation evidence snapshot.
   */
  eoSimulationEvidence?: import('../lib/sim/eo/eoReportSection').EoReportSection;
  /** Explicit supplier-only or selected-simulation source choice frozen at generation. */
  engineeringSourceBasisAtGen?: import('./simulationAuthority').EngineeringSourceBasisV1;
  /** Exact selected EO → annual-yield → finance-LCOE lineage frozen at generation. */
  simulationEvidenceChainAtGen?: import('./simulationAuthority').SimulationEvidenceChainV1;
  /**
   * Exact presentation-neutral upstream identities consumed by the semantic
   * decision analysis. Values are content hashes/revision ids only; the source
   * payloads remain in their authoritative stores.
   */
  decisionAnalysisUpstreamAtGen?: import('./decisionAnalysis').DecisionAnalysisUpstreamReferenceV1[];
  /** Historic BoP execution references remain audit-readable but require regeneration. */
  requiresRegeneration?: {
    reason: 'legacy-simulation-reference-retired';
    detectedAt: string;
  };
  /** Screening topography frozen from the SED used for this generation. */
  topographyAtGen?: import('./topography').TopographyEvidence;
  /**
   * Promoted workbench scenario plus deterministic free-field results.
   * Historical reports used the compact `{ scenario, result, capturedAt }`
   * shape; retain that optional read contract while all new reports write the
   * canonical `{ scenario, noise, capturedAt }` wrapper. Neither shape is ever
   * re-solved against live site state when a report is read.
   */
  generationImpactAtGen?:
    | import('./generationImpact').GenerationImpactSnapshot
    | {
        scenario: import('./generationImpact').GenerationNoiseScenario;
        result: import('./generationImpact').GenerationNoiseImpactResult;
        capturedAt: string;
      };
  /**
   * Frozen A-01 generation test-fit exhibit. This is independent from the
   * legacy generationImpactAtGen union so old reports remain readable while
   * new test-fit reports render without consulting live site/Zustand state.
   */
  generationLayoutExhibitAtGen?: import('./generationImpact').GenerationLayoutExhibitSnapshot;
  /**
   * Public, coordinate-free projection of the frozen generation exhibit.
   * This field exists only on the render-only share projection and must never
   * be promoted back into a private report revision.
   */
  generationLayoutExhibitPublicAtGen?: import('./generationImpact').PublicGenerationLayoutExhibitProjection;
  /**
   * R3-C1 — Simulation & Resilience appendix exhibits (reliability N+1,
   * ride-through transients, and B7-gated optimisation studies). Rendered as a
   * data-gated subsection inside the Appendix & References section across all
   * four surfaces (view / PDF / DOCX / PPTX). Populated post-generation by the
   * evidence orchestrator (`assembleSimEvidenceExhibits`); absent/empty ⇒ no
   * section (dormant-safe). B7 governance (citable vs exploratory watermark) is
   * carried on each exhibit — see `simEvidenceExhibit.ts`.
   */
  simEvidenceExhibits?: import('../lib/reportGeneration/evidence/simEvidenceExhibit').SimEvidenceExhibit[];
  /** Deterministic category scores. Undefined when the report type's
   *  ReportPromptConfig.usesScoring is false (e.g. test-fit) — Workflow Prompt 4. */
  categories?: RagCategory[];
  summary: string;

  /**
   * PH-A2 #8+9: stamped `true` by `fetchRagReports` on Firestore-fallback
   * records (no `storagePath`, or GCS load failed). When `true`, the
   * record carries metadata only — `datasets`, `insights`, and the
   * other heavy fields are absent. Consumers can branch on this flag
   * to render a "metadata only — open to refresh" affordance without
   * having to re-derive the predicate from `!datasets`.
   */
  _metadataOnly?: boolean;

  // Advanced Features
  timeSeries: TimeSeriesData[];
  risks: RiskItem[];
  recommendations: Recommendation[];
  /**
   * PH-A2 #9: previously typed required, but absent on Firestore-fallback
   * records (metadata-only saves). Consumers must guard
   * (`report.insights?.powerStrategy ?? ''`) — TS will enforce.
   */
  insights?: {
    powerStrategy: string;
    coolingStrategy: string;
    regulatoryOutlook: string;
    commercialViability: string;
  };

  /**
   * Set when AI narrative generation failed and the report was assembled from
   * deterministic data only (scoring + SED summary + tables). The report-view
   * surfaces a banner with a "Refresh narrative" CTA.
   */
  degradedMode?: boolean;
  /** Reason for the degraded mode, surfaced to the user. */
  degradedReason?: string;
  /** Typed Stage-1 evidence-analysis outcome (F2). */
  evidenceAnalysisStage?: import('../lib/reportGeneration/evidenceAnalysisRetry').EvidenceAnalysisStageOutcome;

  /**
   * Cost P3 — tier this report was generated at.
   *   - 'investment-grade' (default): today's full path — ≤4 review
   *     passes on Pro, full LLM-judge audits, full final sanity check.
   *   - 'draft': cheaper opt-in iteration tier — Flash review, fewer
   *     review iterations, qualitative LLM-judge audits OFF. The
   *     deterministic correctness gates (numeric fact-check, citation
   *     density floor, provider precedence) ALWAYS run regardless.
   * Undefined on reports generated before P3 — treated as
   * 'investment-grade' (exportable) by `resolveGenerationTier`.
   */
  generationTier?: import('../config/generationTier').GenerationTier;
  /**
   * Cost P3 — convenience boolean mirror of `generationTier === 'draft'`.
   * Stamped alongside `generationTier`. Drafts are for iteration, not
   * delivery: `assertExportableTier` BARS PDF/DOCX/PPTX export on a
   * draft until it is promoted to investment-grade (re-run at that tier).
   */
  isDraft?: boolean;
  /**
   * V2 delivery contract — an explicit generation quality hold. This used to
   * exist only on an intersection type inside the runner, which let viewers
   * and exporters silently miss it. Optional for legacy reports.
   */
  qualityHold?: 'needs-revision';
  /** Bounded human-readable reasons for {@link qualityHold}. */
  qualityHoldFindings?: string[];
  /**
   * Cost P5 — snapshot of the SED's per-section input fingerprints at the time
   * this report was generated. On a later rerun, `computeSedDirtySetSinceReport`
   * diffs the live SED's current fingerprints against this snapshot to find
   * which SED sections changed since this report, which then drives the
   * report-section stale set (`computeReportSectionStaleSet`) and the
   * carry-forward of unchanged sections. Absent on pre-P5 reports → a rerun
   * cannot scope and falls back to a full regen (safe). Stamped on EVERY
   * generation going forward so the NEXT rerun can go incremental.
   */
  sedSectionFingerprintsAtGen?: Partial<Record<
    import('./siteEvidence').SedSectionId,
    import('./siteEvidence').SectionFingerprint
  >>;
  /** Content hashes of shared source ledgers consumed by every report prompt. */
  sedSharedDocumentHashesAtGen?: Record<string, string>;
  /** Worker-collected public-source research; it never publishes a SED revision. */
  publicSentimentResearch?: import('../lib/sentiment/groundedSentimentResearch').GroundedSentimentResearch;
  /**
   * Cost P5 — telemetry: the report sections that were REGENERATED on this run
   * (the stale set). Absent / empty on a full regen. Used by the post-regen
   * DiffReviewDialog to label "regenerated vs carried-forward" and by cost
   * attribution. Carry-forward (reused) sections are the complement against the
   * canonical section list.
   */
  regeneratedSections?: string[];
  /**
   * Site Score Movement SSM-2 — epoch-ms timestamp at which the
   * `degradedMode` was stamped. Used by the banner copy to render
   * "AI narrative unavailable as of <relative time>" and by the
   * post-mortem dashboard to spot clusters of LLM-empty events.
   * Optional for back-compat — reports stamped before SSM-2 leave it
   * undefined; the banner falls back to its previous copy.
   */
  degradedAt?: number;

  /**
   * RN-2 (RTB-FOLLOWUP-AUTO-FILL-NEXT-REGEN, 2026-05-19) — per-section
   * one-shot prompt-strengthening hint. Stamped at render-time by
   * `SiteRagReportView` when a section's render collapses to a
   * SectionPlaceholder for `reason === 'data-too-thin'`; read by
   * `store.regenerateNarrativeOnly` on the NEXT rerun to prepend the
   * `STRENGTHENED_FILL_PREFIX` to that section's prompt. The hint is
   * cleared on successful consumption so a follow-up rerun without a
   * new stamp falls back to the default prompt.
   *
   * Shape: `{ [sectionId]: 'auto-fill-thin' }`. The union is reserved
   * (only one hint kind today) so future kinds widen additively.
   *
   * Absent on pre-RN-2 reports; treat undefined as "no hints" — UI
   * must NOT crash when reading. Tests pin both code paths.
   */
  sectionRegenHints?: Record<string, 'auto-fill-thin'>;

  /**
   * Site Score Movement SSM-5 — telemetry summarising which doc-evidence
   * tiers influenced which category scores at gen-time. Allows
   * post-hoc analysis from the Score Provenance Panel ("why didn't my
   * Cultural Survey move Regulatory?") without re-running the
   * resolver. Stamped once at gen-time by `siteRagService`; immutable
   * after — historical reports keep their original snapshot.
   *
   *  - `categoriesAffectedByDocs`         — category ids whose at
   *                                         least one metric ingested
   *                                         a `'doc-override'` or
   *                                         `'provider+doc'` slot.
   *  - `docsInfluencingPerCategory`       — per-category doc id list.
   *                                         An id appears under every
   *                                         category whose slot it
   *                                         influenced.
   *  - `categoriesUnaffectedByDocs`       — complement; the categories
   *                                         that scored on pure
   *                                         provider data.
   *
   * Absent on pre-SSM reports — UI must guard.
   */
  scoringInfluence?: {
    categoriesAffectedByDocs: string[];
    docsInfluencingPerCategory: Record<string, string[]>;
    categoriesUnaffectedByDocs: string[];
    /**
     * RNH-4 — extra source groups surfaced alongside doc influence so
     * the Score Provenance panel can render the same three-group
     * structure the Appendix already shows (KNOWLEDGE BASE / Provider
     * data / GOOGLE API DATA ACCESSED).
     *
     * `providers` — datasets the report grounded scoring on (HIFLD,
     *   FEMA, NREL, USGS, EIA, …). Sourced from `report.references[]`
     *   entries whose `kind === 'provider'` at gen-time.
     *
     * `damRefs` — DAM curated knowledge-base entries (CFR-52, NRC-Reg,
     *   SMR, Grid, …). Sourced from `report.references[]` entries
     *   whose `label` starts with `Kb ` at gen-time.
     *
     * Both optional — pre-RNH reports leave them undefined. The panel
     * falls back to the doc-only view in that case.
     */
    providers?: import('./scoringInfluence').ContributingProvider[];
    damRefs?: import('./scoringInfluence').ContributingDamRef[];
  };

  /**
   * Per-provider status snapshot from the orchestration step. Persisted on the
   * report so the Report Quality card can render the actual failed-provider
   * list (with remediation classification) rather than a truncated string
   * baked into a sanity-check message.
   */
  providerStatuses?: import('../services/dataProviders').ProviderStatus[];

  /**
   * A1 — publish-time hygiene telemetry and other generation audit fields.
   * Stamped at persistence when post-render strips fire.
   */
  metadata?: {
    /** Original Stage 1 interpretations corrected before narrative and finding seeding. */
    sourceFindingQualifications?: import('../lib/reportGeneration/qualifyStructuredSourceFindings').StructuredSourceQualification[];
    /** Original model risks withheld from scoring because their cited source cannot establish the asserted fact. */
    scoringSourceQualifications?: import('../services/scoring/riskSourceQualification').RiskSourceQualification[];
    metaInstructionStripped?: number;
    metaInstructionStripDetails?: Array<{
      path: string;
      matches: Array<{ pattern: string; matched: string }>;
    }>;
    sectionDedupFindings?: Array<{
      sectionA: string;
      sectionB: string;
      similarity: number;
      contentSample: string;
    }>;
    caveatHonestyFailures?: Array<{
      kind: 'false-honesty' | 'undisclosed-provider-failure';
      severity: 'block' | 'warn';
      detail: string;
      providerKind?: string;
    }>;
    /** D3 — exec summary verdict stronger than weakest source report. */
    execVerdictConflict?: {
      execVerdict: string;
      execRank: number;
      sourceVerdicts: Record<string, string>;
      conflictingReports: string[];
      weakestSourceVerdict: string;
      severity: 'block' | 'warn';
    };
    [key: string]: unknown;
  };

  /** R-78 / R-67 — post-generation QA findings (provider precedence, internal numbers, etc.). */
  qaFindings?: {
    reliabilityMarkerCorrections?: import('../lib/audit/reliabilityMarkerPolicy').ReliabilityMarkerCorrectionReceipt[];
    /** Exact bounded draft corrections and their independently checked admission. */
    boundedGroundingRepairs?: import('../lib/grounding/boundedGroundingRepair').BoundedGroundingRepairReceipt[];
    /** Private exact-ordinal source omissions; attempted sources are not evidence. */
    sourceEvidenceOmissions?: { version: 'report-source-omissions-v1'; omissions: Array<{ index: number; reason: import('../lib/reportGeneration/reportEvidenceSourceAdmission').SourceOmissionReason }> };
    /** Completed report grounding check, including advisory findings. */
    grounding?: {
      mode: 'warn' | 'on';
      /** `GROUNDING_VALIDATOR_VERSION` that produced this verdict; absent ⇒ pre-v2 binding rules. */
      validatorVersion?: string;
      /** Deterministic rollback of editorial layers whose content alone held the report. */
      editorialRollback?: import('../lib/grounding/editorialGroundingAdmission').EditorialGroundingRollbackReceipt;
    } & Pick<import('../lib/grounding/groundingValidator').GroundingResult, 'clean' | 'violations' | 'stats'>;
    /** R-71 — provider coverage snapshot at dispatch time. */
    providerCoverage?: import('../lib/audit/providerCoverageGate').ProviderCoverageReport;
    providerPrecedenceViolations?: Array<{
      fact: string;
      reportedValue: string | number;
      tier1Value: string | number;
      tier1Source: string;
      severity: 'block' | 'warn';
      suggestedFix: string;
      excerpt?: string;
    }>;
    /** R-67 — within-report same-fact multi-value inconsistencies. */
    internalNumberFindings?: Array<{
      canonicalKey: string;
      label: string;
      severity: 'block' | 'warn';
      mentions: Array<{
        value: string | number;
        normalizedValue: string | number;
        units?: string;
        sectionId?: string;
        lineNumber?: number;
        excerpt?: string;
        tier1Cited?: boolean;
        rawText?: string;
      }>;
      conflictingValues: Array<string | number>;
      resolution?: {
        canonicalValue: string | number;
        source: 'tier1' | 'majority' | 'provenance';
        tier1Source?: string;
        provenance?: string;
      };
      autocorrectBlocked?: boolean;
    }>;
    /** R-66 — citation integrity gate (dangling refs, provenance gaps, URL health). */
    citationIntegrityViolations?: Array<{
      kind: string;
      severity: 'block' | 'warn' | 'info';
      index?: number;
      lineStart: number;
      lineEnd: number;
      message: string;
      detail?: string;
    }>;
    /**
     * R-66 — count of citations whose `[Ref N]` token was downgraded to a
     * `[unverified]` marker because the gate could not establish verifiable
     * provenance. Informational only — drives a non-blocking "N citations
     * marked for verification" note, not a blocking banner.
     */
    citationsMarkedForVerification?: number;
    /** R-68 — major constraints buried vs Key Findings elevation gaps. */
    constraintElevationFindings?: Array<{
      type: string;
      canonicalKey: string;
      value: string | number;
      displayValue: string;
      constraintSeverity: 'block' | 'warn' | 'info';
      severity: 'block' | 'warn';
      kind: 'absent_from_key_findings' | 'buried_in_exec_only' | 'buried_order';
      inKeyFindings: boolean;
      inExecutiveSummary: boolean;
      keyFindingsIndex?: number;
      excerpt?: string;
      sectionId?: string;
      lineNumber?: number;
      source?: 'provider' | 'regex';
      provider?: string;
      citationRefs?: number[];
      suggestedBullet: string;
      message: string;
      /** True when the auto-elevation patch resolved this gap in the same
       *  pass — the finding is downgraded to 'warn' (elevated ⇒ resolved). */
      autoElevated?: boolean;
    }>;
    /** R-68 — reversible Key Findings elevation patch metadata. */
    elevationPatch?: {
      original: unknown;
      appliedAt?: string;
      appliedCount?: number;
      target?: 'structuredFindings' | 'keyFindings' | 'markdown';
    };
    /** R-69 — semantic action deduplication clusters (intra- or cross-report). */
    actionClusters?: Array<{
      clusterId: string;
      canonicalText: string;
      canonicalSectionId: string;
      memberCount: number;
      members: Array<{
        text: string;
        sectionId: string;
        sourcePath: string;
        owner?: string;
        deadline?: string;
        citationRefs?: number[];
      }>;
      ownerConflict?: boolean;
      conflictingOwners?: string[];
      mergedCitationRefs?: number[];
      crossReport?: boolean;
      htmlComment?: string;
    }>;
    /** R-69 — summary stats for action dedup pass. */
    actionDedupMeta?: {
      originalCount: number;
      dedupedCount: number;
      reductionRatio: number;
      threshold: number;
      applied?: boolean;
      crossReport?: boolean;
    };
    /** R-62/R-63/R-64 — per-section topical drift findings. */
    sectionDriftFindings?: Array<{
      detector: 'keyword' | 'embedding' | 'llm-judge';
      reportTypeId: string;
      sectionId: string;
      severity: 'block' | 'warn';
      message: string;
      overlap?: number;
      matchedKeywords?: string[];
      missingKeywords?: string[];
      prohibitedHits?: string[];
      similarity?: number;
      score?: number;
      rationale?: string;
    }>;
    /**
     * R-73 — publish-time section depth failures (thin sections, missing
     * bullets, stub-template repeats). Stamped by `runSectionDepthGate` on the
     * persisted artifact; block-severity entries feed the QA-1 quality hold
     * exactly like `distribution` / `sectionDriftFindings`.
     */
    sectionDepth?: Array<import('../lib/sectionDepthGate').SectionDepthFailure>;
    /**
     * Explicit section omissions stamped at generation time when a section's
     * evidence domain PROVABLY has nothing (e.g. every attempted tier-1
     * sentiment source returned no facts). Depth auditors demote findings on
     * these sections to warn — "regenerate" cannot fix an unevidenced
     * section; adding sources can. See `lib/reportGeneration/sectionOmissions`.
     */
    sectionOmissions?: Array<import('../lib/reportGeneration/sectionOmissions').SectionOmissionRecord>;
    /**
     * Truth violations restated as questions a human can ANSWER (2026-08-05).
     * Generation always completes; these travel with the report so the
     * operator can resolve each one. An answer is written to `sed.amendments`
     * and becomes canonical truth for every future report.
     */
    truthQuestions?: Array<import('../lib/audit/qaSeverityPolicy').TruthQuestion>;
    /** R-72 — output distribution sanity vs corpus baseline. */
    distribution?: Array<{
      stat: string;
      statLabel: string;
      observed: number;
      p10: number;
      p25: number;
      p50: number;
      p75: number;
      p90: number;
      severity: 'block' | 'warn';
      message: string;
      suggestion: string;
    }>;
    /** R-72 — corpus sample metadata for distribution checks. */
    distributionCheckMeta?: {
      reportTypeId: string;
      corpusSampleSize: number;
      insufficientCorpus: boolean;
      minSample: number;
      checkedAt?: string;
    };
    /** R-77 — per-section provider relevance whitelist violations. */
    providerRelevance?: Array<{
      kind: 'IRRELEVANT_PROVIDER' | 'SOLE_NUMERIC_UNSUPPORTED' | 'MISSING_PREFERRED';
      severity: 'block' | 'warn' | 'info';
      reportTypeId: string;
      sectionId: string;
      sectionTitle?: string;
      providerId: string;
      refIndex?: number;
      message: string;
      excerpt?: string;
      lineNumber?: number;
    }>;
    /** R-70 — document claim provenance / char-range anchoring findings. */
    docProvenanceFindings?: Array<{
      kind: string;
      severity: 'block' | 'warn';
      docId?: string;
      fileName?: string;
      lineNumber: number;
      message: string;
      claimText?: string;
      anchor?: {
        docId: string;
        page: number;
        charStart: number;
        charEnd: number;
      };
    }>;
    /** R-75 — per-claim declared vs actual confidence band honesty. */
    caveatHonesty?: {
      findings: Array<{
        claimId: string;
        severity: 'block' | 'warn';
        declaredBand: string;
        actualBand: string;
        bandGap: number;
        excerpt: string;
        message: string;
        sectionId?: string;
        lineStart: number;
        lineEnd: number;
        evidenceBreakdown: {
          tier1Score: number;
          tier2Score: number;
          tier3Score: number;
          multiSourceBonus: number;
          brokenCitePenalty: number;
          provenanceGapPenalty: number;
          refTiers: Array<{ index: number; tier: number; source: string }>;
        };
        suggestedRewrite?: string;
      }>;
      claimsScanned: number;
      checkedAt: string;
      autocorrectApplied?: boolean;
      /** Count of truthful-downgrade rewrites applied before re-verification (2026-08-14). */
      rewritesApplied?: number;
      /** Audit trail of claims the rewrite downgraded (before → after). */
      downgraded?: Array<{
        claimId: string;
        declaredBand: string;
        actualBand: string;
        before: string;
        after: string;
      }>;
      blockCount: number;
      warnCount: number;
    };
    /** R-8 — qualitative claim vs SED/citation audit (batch / user-audit only). */
    qualitativeClaimAudit?: {
      findings: Array<{
        claimId: string;
        claimText: string;
        sectionId: string;
        lineNumber: number;
        refIndices: number[];
        verdict: 'UNSUPPORTED' | 'CONTRADICTED';
        severity: 'block' | 'warn';
        rationale: string;
        sedFieldsConsidered: string[];
        message: string;
      }>;
      claimsScanned: number;
      claimsJudged: number;
      claimsSkipped: number;
      confirmedCount: number;
      checkedAt: string;
      promptVersion: string;
    };
    /** R-131-EPISTEMIC-GROUNDING-B — asserted absence without citation evidence. */
    absenceAssertionAudit?: import('../lib/audit/absenceAssertionDetector').AbsenceAssertionAuditResult;
    /** Source subject, footprint and quantity role checked against the frozen SED. */
    sourceRoleReview?: import('../lib/audit/sourceRoleReview').SourceRoleReviewReceipt;
    /** Bounded advisory evaluations; never an acceptance or source authority. */
    jevReportReview?: import('../services/jevReportReview').JevReportReviewReceipt;
    environmentalCitationRepairs?: {
      version: 'environmental-citation-repairs-v1';
      sedId: string;
      sedVersion: number;
      referencesAtRepair: DataSource[];
      repairs: import('../lib/audit/environmentalScreeningCitations').EnvironmentalCitationRepair[];
    };
    /** Generation-time water corrections against exact frozen scenario rows. */
    waterScreeningRepairs?: {
      version: 'water-screening-repairs-v1';
      sedId: string;
      sedVersion: number;
      referencesAtRepair: DataSource[];
      repairs: import('../lib/reportGeneration/repairWaterScreeningClaims').WaterScreeningRepair[];
    };
    /** Original audit/reference identity before the completed narrative is revalidated. */
    finalRepairPass?: import('../lib/reportGeneration/finalRepairPass').FinalRepairReceipt;
    finalSourceReview?: import('../lib/audit/finalSourceValidation').FinalSourceReviewAudit;
    sourceRoleTruth?: {
      findings: import('../lib/audit/sourceRoleTruthGuard').SourceRoleTruthFinding[];
      sedId: string | null;
      sedVersion: number | null;
      cwa404PermitTypePolicyVersion?: typeof import('../lib/audit/cwa404PermitTypeGuard').CWA404_PERMIT_TYPE_POLICY_VERSION;
      /** R13 regulatory exemption guard version that produced these findings. */
      regulatoryExemptionPolicyVersion?: typeof import('../lib/audit/regulatoryExemptionGuard').REGULATORY_EXEMPTION_POLICY_VERSION;
    };
    /**
     * WATER-TRUTH-1 — subject-site water rates checked against the exact
     * deterministic SED screen. Block rows are demonstrable truth violations:
     * an unsupported numeric value, a modeled endpoint labeled as
     * actual/permitted/measured/requested/design use, an omitted required
     * Water Resource screen, or a guard execution failure.
     */
    waterUseScreening?: {
      findings: Array<import('../lib/audit/waterUseScreeningGuard').WaterUseScreeningFinding>;
      screenModelVersion: string | null;
      screenSourceCatalogVersion: string | null;
    };
    /** M3 — nightly cron full-audit summary snapshot. */
    auditRunSummary?: {
      runAt: string;
      totalFindings: number;
      severityCounts: Record<'block' | 'warn' | 'info', number>;
      detectorStatus: Record<string, string>;
      durationMs: number;
    };
    /** UD4 — lineage summary of user `apply` corrections consumed on this rerun. */
    appliedCorrectionsSummary?: {
      appliedCount: number;
      resolvedCount: number;
      stillFailingCount: number;
      supersededCount?: number;
      items: Array<{
        identity: string;
        section: string;
        category: string;
        finding: string;
        appliedDirective: string;
        status: 'resolved' | 'still-failing' | 'superseded-by-evidence';
        remainingReason?: string;
        supersededByEvidence?: string;
      }>;
    };
    /** Receipts for deterministic source-supported audit corrections applied without a full AI rerun. */
    sourceSupportedAuditCorrections?: Array<{
      correctionId: string;
      fieldPath: string;
      section?: string;
      previousValue: string;
      correctedValue: string;
      evidenceCitation: string;
      userNote?: string;
      appliedAt: string;
      appliedByUid?: string;
      fromRevisionId?: string;
      toRevisionId?: string;
      clearedFindingTexts: string[];
      remainingHoldCount: number;
    }>;
  };

  /**
   * RNH-2 — narrative-hygiene quality warnings. Populated by post-gen
   * guards that detect mismatches between the LLM's prose and the
   * user's stated intent / preferences.
   *
   *  - `rnh-stance-mismatch`: nuclear-specific RECOMMENDED ACTIONS
   *     conflict with `projectIntent.nuclearInterest` — assertive verbs
   *     under `'considering'` / `'undecided'`, or ANY nuclear
   *     recommendation under `'no'` (RNH-2b). The renderer surfaces
   *     these as an amber chip "Recommendations may not reflect your
   *     stance — review action plan" with an `[Open Rerun]` link.
   *  - `meta-instruction-leak` (MEL): the LLM's QA-review iteration
   *     loop emitted a self-directive ("The analysis should be framed
   *     around …", "The narrative should note …") INTO the narrative
   *     body instead of executing the correction. The MEL strip
   *     removes the directive text from the persisted prose and stamps
   *     this warning so the renderer can flag the affected section for
   *     manual review — even after the strip the body may be short
   *     enough that human re-write is warranted.
   *
   * Absent on pre-RNH reports — UI must guard. Sparse: only the
   * warning types that fired land here; an empty array is the
   * "everything clean" signal.
   */
  qualityWarnings?: Array<
    | {
        type: 'rnh-stance-mismatch';
        /** Number of triggering phrases the guard found across all sections. */
        count: number;
        /** Up to 3 representative samples; the full list is bounded by the
         *  guard at gen-time so payload stays small. */
        samples: Array<{ sectionId: string; phrase: string }>;
        /** RNH-2b — which stance lane fired: `'considering'` (assertive verbs
         *  need softening) or `'not-considered'` (nuclear pathway excluded by
         *  intent yet recommended anyway — the Pronghorn failure mode).
         *  Absent on pre-RNH-2b reports — UI must guard. */
        stance?: 'considering' | 'not-considered';
      }
    | {
        type: 'meta-instruction-leak';
        /** Number of meta-instruction phrases detected across all sections. */
        count: number;
        /** Up to 3 representative samples; each includes the family tag so
         *  ops can roll up by family without exposing raw excerpts in the
         *  default chip surface. Full list is bounded at strip-time. */
        samples: Array<{ sectionId: string; family: string; excerpt: string }>;
      }
    | {
        type: 'wpg-workload-drift';
        /** Number of drift phrases the guard found across all sections. */
        count: number;
        /** Up to 3 representative samples. */
        samples: Array<{ sectionId: string; phrase: string }>;
        /** Canonical `WorkloadProfile` id from `effectiveWorkloadProfile(intent)`
         *  — surfaces in the chip so the user knows which term WAS expected. */
        intentProfile: string;
      }
    | {
        type: 'wpg-capacity-drift';
        /** Number of capacity-mention phrases the guard found. */
        count: number;
        /** Up to 3 representative samples, each carrying the matched MW
         *  figure and the sentence context for the renderer chip. */
        samples: Array<{ sectionId: string; phrase: string; mw: number }>;
        /** Resolved Project Intent capacity label (bucket or exact MW). */
        intentPowerLabel: string;
        /** Financial-assumptions capacity figure that triggered the flag. */
        financialCapacityMW: number;
      }
    /**
     * NB-2 — lazy render-time hygiene backfill marker.
     *
     * Stamped by `SiteRagReportView`'s memo when `applyHygieneBackfill`
     * fires on an archived report (one that was generated BEFORE the
     * RNH-5 / MEL / KFH-3 / WPG packs shipped). NEVER persisted to
     * Firestore — the marker lives only on the in-memory clone the
     * view renders. The persisted body stays byte-identical at rest.
     *
     * Carries the ordered list of strip rules that ACTUALLY fired so
     * the admin-only "Cleaned at render" chip can list them in its
     * tooltip (NB-4).
     */
    | {
        type: 'backfill-strip-applied';
        /** Mirrors `applied.length` so the union shape stays uniform
         *  with the other variants (each carries a `count`). Existing
         *  callers narrow on `type` before consuming `samples`; this
         *  variant carries no samples (the strip applied to the body
         *  itself, not to a discrete phrase list). */
        count: number;
        /** Strip rules that actually fired on this read, in pipeline
         *  order. Stable identifiers from `BackfillFlag` in
         *  `narrativeBackfill.ts`. */
        applied: ReadonlyArray<
          | 'rnh-verifier-tags'
          | 'mel-meta-instructions'
          | 'kfh-unresolved-placeholders'
          | 'wpg-workload-drift'
          | 'wpg-capacity-drift'
        >;
      }
    /**
     * CSE-FOLLOWUP-CONFLICT-DETECTION (CI-2) — two or more claims
     * emitted with the same (reportSectionId, field) but different
     * `value`. Stamped by `applyClaimsEmission`'s `onConflicts`
     * callback so the admin-only chip on `SiteRagReportView` can
     * surface the disagreement. Customers don't see this — it's an
     * internal-quality signal, not a reader-level annotation.
     *
     * `entries` carries one row per (sectionId, field) collision
     * with the deduped string forms of the distinct values. The
     * renderer truncates the value list in the chip tooltip.
     */
    | {
        type: 'cse-internal-conflict';
        /** Mirrors `entries.length` so the union shape stays uniform. */
        count: number;
        entries: ReadonlyArray<{
          reportSectionId: string;
          field: string;
          /** Distinct normalised string values observed. Length ≥ 2. */
          values: ReadonlyArray<string>;
        }>;
      }
    /**
     * PSE-3 — `flagSentimentConflicts` post-gen guard fired. The
     * narrative contains assertive prose recommending a BTM tech whose
     * `perTech` sentiment in the SED is clearly oppositional (mean
     * sentiment ≤ -0.2 with non-trivial sample count), and the
     * containing sentence is NOT wrapped in a conditional preamble.
     *
     * Sibling of `rnh-stance-mismatch` — same softening + warning
     * shape, but keyed off the public-sentiment axis rather than the
     * static `projectIntent.nuclearInterest` stance. Stamped by the
     * guard at gen-time; renderer surfaces an amber "Recommendation
     * conflicts with local sentiment" chip with an [Open Rerun]
     * affordance.
     */
    | {
        type: 'pse-sentiment-conflict';
        /** Mirrors `samples.length`. */
        count: number;
        /** Up to 3 representative conflicts. */
        samples: Array<{
          /** BTM tech id whose sentiment is oppositional. */
          tech: string;
          /** Mean sentiment score in [-1, +1]. Negative ⇒ oppositional. */
          sentimentScore: number;
          /** Report section the assertive phrase appeared in. */
          sectionId: string;
          /** The triggering phrase (≤120 chars; bounded at guard time). */
          phrase: string;
        }>;
      }
    /**
     * PSE-4 — alternative-option recommendation engine stamped a
     * sentiment-friendlier alternative onto one or more recommended
     * actions. The narrative recommended a tech with oppositional local
     * sentiment, the engine ran the plausibility filter against the
     * site profile, and a supportive alternative was found and appended
     * to the offending recommended-action sentence.
     *
     * Stamped by `applyAltOptionRecommendations` immediately after
     * `flagSentimentConflicts` in the gen-time hygiene pipeline. The
     * renderer surfaces an info-tone chip with the per-tech swap list;
     * unlike `pse-sentiment-conflict` this one is informational rather
     * than a corrective.
     */
    | {
        type: 'pse-alt-option-recommendation';
        /** Mirrors `samples.length`. */
        count: number;
        /** Per-swap suggestion that was appended. Up to 5 samples
         *  (one per oppositional-tech we surfaced an alt for). */
        samples: Array<{
          /** BTM tech id whose sentiment is oppositional. */
          fromTech: string;
          /** BTM tech id we suggested as the friendlier alternative. */
          toTech: string;
          /** From-tech mean sentiment (negative, in [-1, 0]). */
          fromSentiment: number;
          /** To-tech mean sentiment (positive, in [0, +1]). */
          toSentiment: number;
          /** Positive-share % at to-tech (rounded int, 0-100). */
          positivePct: number;
          /** Negative-share % at from-tech (rounded int, 0-100). */
          negativePct: number;
        }>;
      }
    | {
        type: 'sec-coverage-manifest-drift';
        /** Field keys from manifest not found verbatim in narrative text. */
        count: number;
        samples: string[];
      }
    /**
     * C0-GEN Wave 2 — editorial-narrative grounding rejection. Stamped by the
     * editorial pass (`applyEditorialNarrative`) when a per-section editorial
     * layer FAILED one of the Wave-1 grounding validators (A–E) and was
     * dropped back to its deterministic fallback. NEVER silently suppressed
     * (spec fix #6): every per-section rejection surfaces here so the
     * admin-only chip can list the failing section + reason. Mirrors the
     * `stripUnresolvedPlaceholders` surfacing pattern.
     */
    | {
        /** Optional editorial operation degraded; no truth/semantic hold is cleared. */
        type: 'editorial-capacity-skip' | 'editorial-recoverable-fallback';
        count: number;
        samples: Array<{ sectionId: string; reason: string }>;
      }
    | {
        type: 'editorial-grounding-reject';
        /** Mirrors `samples.length` so the union shape stays uniform. */
        count: number;
        /** Per-section rejections: the section id + the failing-check reason. */
        samples: Array<{
          /** Section id whose editorial layer was rejected. */
          sectionId: string;
          /** The grounding check that failed (A–E) and its human reason. */
          reason: string;
        }>;
      }
    /**
     * RVG — reviewer-voice quality gate (`applyReviewerVoiceQualityGate`)
     * tripped: QA-critic prose ("these figures should be verified before
     * publication", "[Ref X]") was detected in a narrative leaf.
     *
     * Since 2026-09-18 the gate WITHHOLDS the whole leaf (null; '' for the
     * top-level `summary`) and records it in `withheld` — it never persists a
     * display string. A withheld leaf holds the report for revision (it does
     * not block export or publishing — owner decision 2026-09-18; see
     * `lib/report/withheldNarrative.ts`); render and every export show an
     * honest "not assessed" notice at that path.
     *
     * `sections` is the LEGACY shape: records written before 2026-09-18, when
     * the gate replaced the container's gated leaf with the ops sentinel
     * "<Label> unavailable — content failed quality gate. Regenerate report."
     * Those bodies are not rewritten; render/export scrub the sentinel.
     */
    | {
        type: 'reviewer-voice-leak';
        /** Number of gated leaves (legacy: gated containers). */
        count: number;
        /** Leaves withheld by the gate (exact path + leaf-derived label). */
        withheld?: import('../lib/report/withheldNarrative').WithheldNarrativeLeaf[];
        /** Legacy (pre-2026-09-18) container-level records. */
        sections?: Array<{
          sectionId: string;
          matchedPatterns: string[];
          excerpt: string;
        }>;
      }
  >;

  // Detailed Assessment Fields (PDF matching)
  executiveSummary?: string;
  sitingNotes?: string;
  climateSevereWeather?: {
    risks: string[];
    implications: string;
  };
  infrastructureInterconnection?: {
    electricGrid: string;
    transportFiber: string;
    municipalUtilities: {
      system: string;
      totalCapacity: string;
      currentUse: string;
      available: string;
    }[];
  };
  developmentPathway?: {
    phase: string;
    title: string;
    description: string;
    // TRACK-B — OPTIONAL per-phase finance fields (intent-faithful rollout):
    // cumulative declared load MW, COD, power source, planning-default ELCC firm verdict, and
    // the corrected per-tech LCOE. Populated from the PER-PHASE FINANCE prompt
    // block; absent on legacy reports. Strings carry units verbatim.
    capacityMW?: string;
    cod?: string;
    powerSource?: string;
    firmCapacityVerdict?: 'feasible' | 'marginal' | 'infeasible' | string;
    lcoe?: string;
  }[];
  appendix?: {
    title: string;
    content: string;
  }[];

  btmOptions?: BtmOption[];
  gridStudy?: GridStudySection;
  transportStudy?: TransportStudySection;
  connectivityStudy?: ConnectivityStudySection;
  connectionRisks?: DetailedRisk[]; // legacy — kept for backwards compat
  developmentRisks?: DetailedRisk[];

  /**
   * Deterministic strengths register populated by data providers (mirrors
   * `developmentRisks`). Surfaces in the SiteLandingPage Top Strengths
   * column when the LLM conclusion arrays are empty (FS1 fallback chain).
   * Each entry is a short headline + a one-line description.
   */
  developmentStrengths?: { title?: string; description?: string }[];

  // Site Boundary — MP pack canonical multi-parcel snapshot.
  /**
   * Multi-Parcel Site Boundary (MP pack). Snapshot of the site's
   * parcels at report-generation time. Length 1 for single-parcel
   * sites; back-compat aliases below derive from index 0.
   */
  screeningBasis?: import('../lib/reportGeneration/screeningBasis').ScreeningBasis;
  deterministicScreeningValidation?: import('../lib/reportGeneration/deterministicScreeningValidation').DeterministicScreeningValidation;
  studyAreaScope?: import('./siteStudyArea').SiteStudyAreaScope;
  siteBoundaries?: import('./siteBoundary').SiteBoundaryParcel[];
  /** Aggregate stats across all parcels — `computeAggregate(siteBoundaries)`. */
  siteBoundaryAggregate?: import('./siteBoundary').SiteBoundaryAggregate;

  /**
   * @deprecated MP pack: derived alias of `siteBoundaries[0].polygon`.
   * Kept on write paths for back-compat with legacy report consumers.
   */
  siteBoundary?: { lat: number; lng: number }[];
  /** @deprecated MP pack: equals `siteBoundaryAggregate.totalArea` (acres). */
  siteArea?: number;        // acres, computed from polygon
  /** @deprecated MP pack: equals `siteBoundaryAggregate.totalPerimeter` (km). */
  sitePerimeter?: number;   // km
  /** @deprecated MP pack: equals `siteBoundaryAggregate.aggregateCentroid`. */
  siteCentroid?: { lat: number; lng: number };
  siteElevationRange?: {
    sourceQualification?: import('./elevationSourceQualification').ElevationSourceQualification;
    min: number;
    max: number;
    avg: number;
    /** EL3 — population stdev across sampled elevations (m ASL). */
    stdev?: number | null;
    /** EL3 — number of sample points that returned a usable value. */
    pointsSampled?: number;
  };
  /**
   * ELV-3 — provenance tag for `siteElevationRange`. Stamped at
   * `generateReportFromSed` time so the report viewer + downstream
   * exports can render an honest source label ("Refreshed via USGS
   * EPQS centroid" vs "Sampled across 11 polygon vertices").
   *
   * Optional for back-compat — pre-ELV reports leave it undefined and
   * the renderer infers `polygon-sample` from the presence of a real
   * (non-zero) triple. `legacy-unknown` is reserved for the eventual
   * admin backfill (ELV-FOLLOWUP-BACKFILL-MIGRATION).
   */
  siteElevationSource?: 'polygon-sample' | 'usgs-epqs-centroid' | 'google-centroid' | 'legacy-unknown';

  // Data provenance
  references?: DataSource[];
  /** Generation-time proof that numbered plain and reliability-tagged tokens
   * share the same source-preserving compaction. Frozen inside the source hash;
   * absent on historical reports whose tagged numbering is not established. */
  referenceNumberingVersion?: 2;
  /** New diligence evidence frozen at generation time for export parity. */
  siteDiligenceEvidence?: SiteDiligenceReportEvidence;
  /** Private exact model-consumed resource/owner/site/SED frame; excluded from public projections. */
  campusResourceAdvisoryAtGen?: import('../lib/findings/campusResourceAdvisory').FrozenCampusResourceAdvisory;
  /**
   * Regulatory feasibility screen frozen before model dispatch on new
   * feasibility reports (owner decision R5): the private input, the result and
   * their binding to site, SED and persisted intent. Read-time validation
   * only; the immutable document carries the result alone. Absent on legacy
   * reports and in AI-only evidence mode.
   */
  regulatoryFeasibilityScreenAtGen?: import('../lib/regulatoryFeasibility/frozen').FrozenRegulatoryFeasibilityScreen;

  /**
   * Cross-validation findings from the SED snapshot — surfaced under
   * a "Data quality notes" panel in the appendix, separate from
   * `references[]`. These are metadata *about* the data, not sources
   * to cite, so Content-Source Fixes Prompt 6 moved them out of the
   * citable references list to stop the AI being tempted to emit
   * `[Ref N]` against an integrity flag. Only failed checks with
   * non-`info` severity make it here. */
  crossValidationFindings?: import('./siteEvidence').CrossValidationCheck[];

  /**
   * Set by the citation hard-gate (Content-Source Fixes Prompt 15)
   * when the LLM cited [Ref N] tokens beyond `references.length` on
   * the initial attempt AND a strict-prompt retry also failed. The
   * out-of-bounds tokens were REMOVED from the narrative by the
   * citationFinalize rewriter (never a literal `[Ref ?]` — Redhead
   * review wave 3). UI surfaces a non-blocking toast on
   * this signal so the user knows some inline citations weren't
   * model-sourced. The numeric `count` is the de-duped offender
   * count for telemetry. */
  citationsRewritten?: boolean;
  citationsRewrittenCount?: number;

  /**
   * CT6 — structured per-sentence claims emitted by the LLM. Each entry
   * pairs one sentence of narrative text with the lineage-target refs
   * that back it (e.g. `['ref:3', 'doc:abc']`). The HTML renderer
   * surfaces a `[¹]` superscript after each claim's text; DOCX/PDF
   * exports carry the same as footnotes.
   *
   * Optional for back-compat: legacy reports (pre-CT6) leave this
   * undefined and the renderer falls through to the prose-only path
   * (parsing `[Ref N]` tokens from the narrative bodies). The density
   * KPI (`citationDensity`) likewise handles both shapes.
   *
   * Type imported lazily to avoid pulling the citation module into
   * non-citation code paths. See `src/types/citation.ts:ReportClaim`.
   */
  claims?: import('./citation').ReportClaim[];

  /**
   * CT6 — citation-density KPI snapshot computed at gen-time. The
   * report-quality panel surfaces this as a "X% of sentences cite a
   * source" badge, and the admin Citation Quality dashboard (CT9,
   * deferred) flags reports below the floor.
   *
   * Optional: a generation that didn't run the density compute leaves
   * this undefined. Callers should treat undefined as "not measured"
   * rather than "0% density".
   */
  citationDensity?: {
    /** Total sentences counted across the narrative. */
    totalSentences: number;
    /** Sentences with at least one source attachment. */
    citedSentences: number;
    /** `citedSentences / totalSentences`, 0..1. */
    density: number;
    /** The floor used for the `belowFloor` decision (default 0.8). */
    floor: number;
    /** True when `density < floor`. KPI flag. */
    belowFloor: boolean;
    /** Whether the metric came from the structured `claims[]` field
     *  or from a prose-only `[Ref N]` scan (legacy reports). */
    source: 'claims' | 'prose';
  };

  // Data quality & confidence
  dataQuality?: 'verified' | 'estimated' | 'degraded';
  fieldConfidence?: FieldConfidence[];
  sanityChecks?: SanityCheck[];
  sanityFindings?: SanityFinding[];
  /** Durable, versioned record of user-approved factual corrections carried across reruns (V1 -> V2 -> V3). */
  approvedFactualCorrections?: import('../lib/preservedUserDecisions').ApprovedFactualCorrectionsLedgerV1;
  /**
   * True once `migrateSanityResolution` has collapsed any legacy
   * three-state resolutions on `sanityFindings` to the two-state model.
   * Set on first report load (store hydration), persisted back to
   * Firestore so the migration runs exactly once per record.
   */
  sanityResolutionsMigrated?: boolean;
  citationAudit?: CitationAudit;
  /**
   * Report P4 / SED D0.4 — per-report SED ingestion-completeness signal.
   * The raw `sedToText()` output is relevance-ranked + budgeted before the
   * narrative builders see it (replacing the old blind tail-cuts). This
   * records whether the FULL SED reached the generator (`fitsWhole`) or
   * which lower-relevance sections were dropped to fit the budget.
   * `omittedTitles` names ONLY genuine SED `##` sections (computed over the
   * raw SED, not the composed prompt). `droppedChars` is UTF-16 .length, an
   * approximate floor (excludes the appended omitted-index line).
   */
  sedIngestion?: SedIngestionInfo;
  generationTelemetry?: GenerationTelemetry;
  reviewTrace?: ReviewTrace;
  /** Prompt config version that produced this report. See src/services/promptChangelog.md. */
  promptVersion?: string;
  /** Effective scoring thresholds version. May include `+override:<v>` if a tenant override was applied. */
  scoringThresholdsVersion?: string;
  /** Per-type preferences captured at preflight. Persisted alongside the report. */
  typedPreferences?: TypedPreferencesByType;
  /** Free-text reason captured on rerun. e.g. "Utility confirmed substation upgrade Q4 2026". */
  regenerationReason?: string;

  /**
   * Site_Configuration_Consolidation SC2-2 — per-report opt-in override of
   * the site-level project intent. `undefined` / `null` ⇒ inherit from
   * `site.projectIntent` via the `resolveSiteConfig` waterfall. Stamped
   * by the NewReportWizard / FeasibilityRerunPanel when the user toggles
   * "override for this report only".
   *
   * Migration discipline: legacy reports leave this undefined and continue
   * to read `rerunConfig.projectIntent` for their reruns; new reports
   * post-SC2 go through the override + AtGen snapshot path.
   */
  projectIntentOverride?: ProjectIntent;
  /** SC2-2 — per-report opt-in override of the site-level typed-preference
   *  defaults. Sparse map keyed by ReportTypeId. `undefined` ⇒ inherit. */
  typedPreferencesOverride?: TypedPreferencesByType;
  /** SC2-2 — per-report opt-in override of the site-level site context.
   *  `undefined` ⇒ inherit from `site.siteContext` (or whatever the
   *  resolveSiteConfig waterfall returns). */
  siteContextOverride?: import('./siteContext').SiteContext;

  /**
   * SC2-2 — gen-time snapshots of the resolved (override → site → empty)
   * values. STAMPED ONCE at `generateTypedReport` time, BEFORE the LLM
   * call. The display layer + the LLM prompt builders read THESE fields,
   * never the live site doc — this is the reproducibility invariant.
   *
   * If `site.projectIntent` later changes, prior reports' `projectIntentAtGen`
   * remains UNCHANGED — that's how a report stays reproducible after
   * site config drift. Optional only on legacy reports (pre-SC2-2);
   * every new report carries them.
   */
  projectIntentAtGen?: ProjectIntent;
  typedPreferencesAtGen?: TypedPreferencesByType;
  siteContextAtGen?: import('./siteContext').SiteContext;

  /**
   * SOC — discriminator for the `*AtGen` site-overview snapshots above
   * (`projectIntentAtGen`, `siteContextAtGen`, `financialAssumptionsAtGen`).
   *
   *   - `'sed'`        — snapshots came from the SED's SOC channels
   *                      (`sed.fullProjectIntent` / `sed.siteContext` /
   *                      `sed.financialAssumptions`). This proves the SED
   *                      itself was internally consistent with the user's
   *                      declared overview AT SED-COMPILE TIME.
   *   - `'live-store'` — snapshots came from the live Zustand store at
   *                      report-generation time (the historical / legacy
   *                      path, or a SED that predates SOC).
   *
   * The rerun-diff layer surfaces this so users can see whether a
   * historical report's frozen-in snapshots are SED-sourced (the SED
   * is the audit trail) or store-sourced (the SED predated SOC; the
   * live store was the canonical source at gen-time).
   *
   * Optional for back-compat — reports written before SOC leave this
   * undefined; the diff layer treats `undefined` as `'live-store'`
   * (matches historical behaviour).
   *
   * Note: this does NOT change the snapshot READ path. Persisted reports
   * continue to expose `projectIntentAtGen` / `siteContextAtGen` /
   * `financialAssumptionsAtGen` directly off the report (SC2
   * reproducibility invariant — never through a resolver). SOC only
   * adds the discriminator on the WRITE side.
   */
  siteOverviewSnapshotProvenance?: 'sed' | 'live-store';

  /**
   * Financial Assumptions (FA1) — per-report opt-in override of the
   * site-level `financialAssumptions`. `undefined` ⇒ inherit from
   * `site.financialAssumptions` via the resolver.
   *
   * **NO WRITER TODAY (audit 6.1).** The line above used to end "Stamped when
   * the NewReportWizard / Rerun panel picks a non-canonical scenario" — neither
   * does. Four places READ it, always as a fallback behind
   * `financialAssumptionsAtGen`, so that arm is currently unreachable; the
   * resolver also advertises a tier-1 `report-override` source that can never
   * fire. Kept rather than deleted because the per-report override is a
   * designed extension point and removing it changes the report schema, but do
   * not write code that assumes overrides work until a writer exists.
   */
  financialAssumptionsOverride?: import('./financialAssumptions').FinancialAssumptions;
  /**
   * FA1 — gen-time snapshot of the resolved financial assumptions.
   * STAMPED ONCE at `generateTypedReport` time, BEFORE the LLM call.
   * The display layer + `buildFinancialAssumptionsBlock` read THIS,
   * never the live site doc — that's the reproducibility invariant.
   *
   * Legacy reports written before FA1 leave this undefined; readers
   * fall through to "no project-economics block in the LLM prompt".
   */
  financialAssumptionsAtGen?: import('./financialAssumptions').FinancialAssumptions;

  /**
   * Supply_Chain_Management SCM1 — per-report opt-in override of the
   * site-level supply-chain plan. `undefined` ⇒ inherit the canonical
   * `site.supplyChainPlan` via the resolver waterfall. Stamped by the
   * SupplyChainPanel's "Use a different plan for this report" path.
   * Partial — only the fields the user diverged on are stored; the
   * resolver merges in unchanged fields from the site-canonical plan.
   */
  supplyChainPlanOverride?: Partial<import('./supplyChain').SupplyChainPlan>;
  /**
   * SCM1 — gen-time snapshot of the resolved supply-chain plan. STAMPED
   * ONCE at `generateTypedReport` time, BEFORE the LLM call. The display
   * layer + the SCM6 risk derivation + the SCM7 itemized-CAPEX feedback
   * read THIS, never the live site doc — this is the reproducibility
   * invariant. Mirrors `projectIntentAtGen` exactly.
   *
   * Optional only on legacy reports (pre-SCM1); every new report that
   * has a non-empty site plan carries the snapshot.
   */
  supplyChainPlanAtGen?: import('./supplyChain').SupplyChainPlan;
  /**
   * Deterministic Supply Chain authority confidence frozen at generation.
   * Missing suppliers, pricing, or quotes are nonblocking evidence gaps, but
   * they cap the report below a green/low-risk conclusion.
   */
  supplyChainAuthorityAtGen?: {
    schemaVersion: 1;
    status: 'complete' | 'limited';
    warningCodes: Array<
      | 'supply-chain-suppliers-missing'
      | 'supply-chain-pricing-incomplete'
      | 'supply-chain-quotes-missing'
      | 'supply-chain-scope-incomplete'
    >;
    lowRiskEligible: boolean;
    scoreCap: 69 | null;
    uncappedOverallScore: number;
  };

  /**
   * SEC — gen-time snapshot of consolidated Coverage Matrix fields
   * (normalized values + source tier per schema key). Stamped once
   * before the LLM call; prompt builders prefer this over raw claims.
   */
  coverageManifestAtGen?: import('../lib/coverage/evidenceConsolidation').CoverageManifestAtGen;

  /**
   * Report P0 — gen-time snapshot of the deterministic Go/No-Go Decision-Guide rollup
   * (`buildExecutiveDecisionRollup`). The exec-summary's PRIMARY decision band (`execDecisionBand`)
   * is a "live" section that reads the Zustand store, which is COLD in the print/export subtree, so it
   * rendered BLANK in the PDF. The section now prefers THIS snapshot (live viewer AND print read it, so
   * viewer↔PDF are identical), recomputing live only as a legacy fallback when the field is absent.
   * Fully JSON-serializable; stamped only for executive-summary reports.
   */
  executiveDecisionRollupAtGen?: import('../lib/executiveDecisionRollup').DecisionGuideRollup;

  /**
   * Report P0 (pathToGo half) — gen-time snapshot of the deterministic Path-to-GO state for the
   * exec-summary's OTHER primary band (`pathToGo`), which has the identical cold-store blank-PDF defect.
   * Carries `presentReportTypes` alongside `state` because the required-diligence checklist derives
   * present/missing from the live sibling-report set, which is empty in the print subtree — without it
   * the PDF checklist would render every required report as "Outstanding". Both halves are
   * JSON-serializable (DerivedSiteVerdict is all primitives); stamped only for executive-summary reports.
   */
  pathToGoSnapshotAtGen?: {
    state: import('../lib/pathToGo/pathToGoOrchestrator').PathToGoState;
    presentReportTypes: import('../config/reportTypes').ReportTypeId[];
  };

  /**
   * LLM-emitted verdict-lean signal (Sentiment-First Verdict pack SF4).
   * Captured from the narrative response schema in geminiService.ts. The
   * display layer (`getDisplayVerdict` in src/lib/reportVerdict.ts) applies
   * this to the deterministic tier with a ±1 guardrail and a green↔red
   * safety bound. Absent on legacy reports written before SF4.
   *
   *   tierAdjustment: -1 = lean more negative; +1 = lean more positive; 0 = no adjustment.
   *   drivers: 1-4 short bullet strings citing narrative evidence (required when adjustment != 0).
   */
  verdictLean?: {
    tierAdjustment: -1 | 0 | 1;
    drivers?: string[];
  };

  /**
   * Snapshot of the company-archetype context that framed this report.
   * Mirrors SiteEvidenceDocument.company at generation time, with the
   * per-report override (companyArchetypeOverride) already applied. The
   * report viewer renders "Prepared for {label}" from this; the compare
   * view renders the archetype shortLabel pill from this. Optional —
   * legacy reports written before Workflow Prompt B fall through to
   * neutral framing in the UI.
   */
  company?: {
    id?: string;
    name?: string;
    archetype?: import('./company').CompanyArchetype;
    archetypeLabel?: string;
    archetypeNote?: string;
  };
  /**
   * Per-report archetype override captured in the wizard's Reader Profile
   * preflight. When present, the prompt pipeline used this archetype
   * instead of the SED's company.archetype. Stored independently of
   * `company` so a viewer can show "(overridden from {SED archetype})".
   */
  companyArchetypeOverride?: import('./company').CompanyArchetype;

  /**
   * Documents_On_Site Prompt 9 — confidence cap derived from the
   * report's document checklist at generation time. When `cap < 1`,
   * the report viewer surfaces a "Limited evidence base" banner
   * listing the critical document slots that were missing.
   */
  evidenceConfidence?: {
    cap: number;                      // 0..1
    missingCriticalIds: string[];     // ChecklistItem.id values
    missingCriticalLabels: string[];  // human-readable for the banner
  };

  /**
   * Evidence Assist (EA2) — per-checklist-row tier selection chosen by the
   * user in the Documents panel. Maps `ChecklistItem.id` to one of the
   * four tier choices. The next rerun reads this map and routes accordingly
   * (`tier1-doc` keeps the upload path; `tier3-baseline` triggers a Tier-3
   * `buildPublicBaseline` call; `tier4-synthesis` activates the caveat-
   * gated LLM synthesis; `pending` defers the decision to the default
   * strategy — EA5 flips the default to Tier 3 for any row whose registry
   * entry exposes a viable baseline).
   *
   * Absent on legacy reports → callers should treat as all 'pending'.
   */
  evidenceStrategy?: Record<
    string,
    'tier1-doc' | 'tier2-reuse' | 'tier3-baseline' | 'tier4-synthesis' | 'pending'
  >;

  /**
   * Evidence Assist (EA4) — per-section provenance map. Each entry tells
   * the report viewer which tier informed the section, what sources to
   * cite, and (when Tier 2/3/4) what the human-readable caveat is. The
   * `ProvenanceStrip` component reads this and renders the chip strip at
   * the top of each section block; the DOCX/PDF export pulls the same map
   * for its footer-side provenance summary.
   *
   * Computed at gen time by `composeSectionProvenance()` from the active
   * `evidenceStrategy` + the checklist row → section mapping (each
   * `ChecklistItem` declares `sedSectionsHelpful`).
   *
   * Absent on legacy reports → renderer falls back to "no provenance shown"
   * which keeps back-compat.
   */
  sectionProvenance?: Record<
    string,
    {
      tier: 1 | 2 | 3 | 4;
      /** Human-readable source labels for the chip subtitle. */
      sources: string[];
      /** Checklist row ids that informed this section. */
      checklistRowIds: string[];
      /** Hard caveat for Tier 4. Optional otherwise. */
      caveat?: string;
    }
  >;

  /**
   * SNT pack — per-section guard status. Distinct from `sectionProvenance`
   * (which tracks evidence tier). This map tracks whether the AI output
   * for a given field path was QUARANTINED at gen time by an
   * instruction-shape detection guard (`src/lib/instructionShapeGuard.ts`).
   *
   *   - `'degraded-instruction-leak'` — model emitted instruction-shape
   *     text (e.g. "The Siting Notes should cite the FEMA Flood Layer
   *     ..."). The field was swapped for a data-grounded fallback
   *     (`SECTION_FALLBACKS[reportType][fieldPath]`); the original AI
   *     text is preserved on `quarantinedOriginal[fieldPath]` for
   *     transparency.
   *   - `'fallback-data-only'` — model returned empty/null AND no
   *     telemetry-worthy instruction shape was detected; the legacy
   *     deterministic fallback fired.
   *
   * Absent on legacy reports → renderer treats all fields as `'normal'`.
   * The amber quarantine banner (SiteRagReportView) only renders when
   * the entry is `'degraded-instruction-leak'`.
   *
   * Composes with:
   *   - RDX — quarantined entries MUST be skipped by `applyReportToSed`.
   *   - RCO — citation count decrements for quarantined sections.
   *   - UAE-2 — banner's "Author" CTA opens the authored-claim flow
   *             scoped to the same `fieldPath`.
   */
  sectionGuardStatus?: Record<string, 'degraded-instruction-leak' | 'fallback-data-only'>;

  /**
   * SNT pack — preserved original AI text for fields that were
   * quarantined under `sectionGuardStatus`. Surfaced collapsed under
   * "Show original AI output" in the quarantine banner so the reader
   * can audit what the model emitted. Absent for `'normal'` and
   * `'fallback-data-only'` entries.
   */
  quarantinedOriginal?: Record<string, string>;

  /**
   * Documents_On_Site Prompt 12 — snapshot of unresolved evidence
   * conflicts at report-generation time. Drives the "Evidence
   * Conflicts" widget in the Quality panel + the report-top banner
   * when a high-severity conflict was unresolved.
   */
  unresolvedConflicts?: {
    total: number;
    bySeverity: { low: number; medium: number; high: number };
    /** First N for the widget; click-through opens the SED section editor. */
    items: Array<{ sectionId: string; field: string; severity: 'low' | 'medium' | 'high'; explanation: string }>;
  };

  /**
   * Per-report document selection. References docs in `site.documents` by id
   * with AI-classified relevance and a user-included flag. Replaces the
   * `documents[]` array that lived directly on reports.
   */
  documentRefs?: Array<{
    docId: string;
    relevance: number;          // 0..1, AI-classified
    userIncluded: boolean;      // false = explicitly excluded by user
    reasonIfExcluded?: string;
    includedAt?: string;
    includedBy?: string;        // uid
  }>;
  documentSelectionMode?: 'auto' | 'manual' | 'mixed';
  /**
   * DDL-FOLLOWUP-IDREF — canonical pointer into `site.documents` by id.
   *
   * Replaces the legacy `documents: SiteDocument[]` snapshot. Storing
   * ids only means the report's view of a doc (filename, summary,
   * extraction status, importance score, …) stays current with the
   * site without needing a per-report mass-stamp pass. Rename a doc
   * on the site and every historical report's resolved view reflects
   * it on the next render.
   *
   * Resolution: callers must route through
   *   `resolveSiteDocuments(site, report)`
   * (`src/lib/site/resolveSiteDocuments.ts`) — the helper handles the
   * legacy fallback and silently drops ids that no longer resolve.
   *
   * Backward-compat: the legacy `documents` field is still populated
   * at gen time (and is still the source-of-truth for reports written
   * before IDREF shipped). Once a soak period confirms every report
   * in Firestore carries `documentIds`, a follow-up migration will
   * retire the snapshot field.
   *
   * Composability: distinct from `documentRefs` — that array carries
   * the richer classifier metadata (relevance / userIncluded /
   * reasonIfExcluded). `documentIds` is the simpler "live set" view.
   * Today the two arrays are populated in lockstep at gen time;
   * `documentIds === documentRefs.map(r => r.docId)` by construction.
   */
  documentIds?: string[];
  /** @deprecated — use documentRefs against site.documents. Kept for back-compat read. */
  documents?: SiteDocument[];

  // ═══ Environmental Impact Assessment sections ═══
  nepaScreening?: {
    determination: string;
    rationale: string;
    ceqaApplicability: string;
    estimatedTimeline: string;
    keyTriggers: string[];
  };
  ecologicalAssessment?: {
    criticalHabitatAnalysis: string;
    esaSpeciesAssessment: string;
    wetlandsDelineation: string;
    wildlifeCorridors: string;
    section7Requirements: string;
    section404Requirements: string;
  };
  waterResources?: {
    waterStressAnalysis: string;
    waterRightsAssessment: string;
    groundwaterSurfaceWater: string;
    thermalDischarge: string;
    stormwaterManagement: string;
  };
  airQualityEmissions?: {
    baselineAqi: string;
    attainmentStatus: string;
    criteriaPollutants: string;
    ghgImplications: string;
    constructionEmissions: string;
    generalConformity: string;
  };
  floodNaturalHazards?: {
    femaFloodAnalysis: string;
    coastalZoneManagement: string;
    seaLevelRise: string;
    seismicImpact: string;
    climateResiliency: string;
  };
  culturalHistorical?: {
    tribalConsultation: string;
    archaeologicalSensitivity: string;
    shpoConsultation: string;
  };
  landUseZoning?: {
    currentZoning: string;
    adjacentLandUses: string;
    compatibilityAnalysis: string;
  };
  noiseVisualImpact?: {
    constructionNoise: string;
    operationalNoise: string;
    visualScreening: string;
  };
  mitigationPlan?: {
    impactCategory: string;
    measure: string;
    costEstimate: string;
    timeline: string;
    effectiveness: string;
  }[];
  permittingPathway?: {
    permit: string;
    agency: string;
    level: string;
    estimatedTimeline: string;
    dependencies: string[];
    criticalPath: boolean;
  }[];
  environmentalRiskMatrix?: {
    risk: string;
    category: string;
    severity: string;
    likelihood: string;
    mitigation: string;
    residualRisk: string;
  }[];

  // RTU-2 (row 0.99235) — three NEPA-canonical sections that the
  // pre-RTU-2 schema omitted. Authority: NEPA §102(2)(E),
  // 40 CFR §1508.7, 40 CFR §1501.7 + EO 13175. Each is a free-text
  // narrative guarded by SNT-1 (instruction-shape) + SNT-5
  // (data-grounded fallback composer in
  // src/lib/sectionFallbacks/environmental.ts).
  alternativesAnalysis?: string;
  cumulativeImpacts?: string;
  publicParticipationPlan?: string;
  /** R-131-EPISTEMIC-GROUNDING-A — permit/compliance gaps as FindingClaim rows. */
  complianceGaps?: import('../lib/findingClaim').FindingClaim[];

  // ═══ Power Availability & BTM Analysis sections ═══
  powerAnalysis?: {
    substationAssessment: string;
    interconnectionQueue: string;
    capacityAnalysis: string;
    curtailmentRisk: string;
    rateStructure: string;
    renewableIntegration: string;
    redundancyAssessment: string;
    recommendations: string[];
    risks: import('../lib/findingClaim').FindingClaim[];
    /** R-131-EPISTEMIC-GROUNDING-A — commercial agreements as FindingClaim rows. */
    commercialReadiness?: import('../lib/findingClaim').FindingClaim[];
    /**
     * R-FIN-PHASE-D — OPTIONAL capacity-credit (ELCC) deep-dive. Per-phase firm-
     * capacity adequacy (firm-deliverable vs facility load + reserve margin).
     * ELCC values are PLANNING DEFAULTS, not a site-specific ELCC study.
     * Optional so existing generations validate unchanged.
     */
    capacityCreditDeepDive?: {
      summary?: string;
      reserveMarginPct?: string;
      elccCaveat?: string;
      perPhase?: {
        phaseLabel?: string;
        facilityLoadMW?: string;
        requiredFirmMW?: string;
        firmDeliverableMW?: string;
        verdict?: string;
        perSourceElcc?: string;
      }[];
    };
  };
  /** Power-availability v8 — intent-linked strategy framing before grid/BTM analysis. */
  powerStrategyContext?: {
    requirementsSummary: string;
    strategyPosture: string;
    analysisScope: string;
    keyDecisionCriteria: string[];
    /**
     * R-FIN-PHASE-D — OPTIONAL power-basis breakdown (IT → facility → grid →
     * MVA). All sub-fields optional; the whole object may be omitted.
     */
    powerBasisBreakdown?: {
      basis?: string;
      itLoadMW?: string;
      facilityMW?: string;
      apparentMVA?: string;
      generationRequiredMW?: string;
      pue?: string;
      powerFactor?: string;
      notes?: string;
    };
  };
  btmDetailedAnalysis?: {
    technology: string;
    capitalCost: string;
    lcoeRange: string;
    timelineToFirstPower: string;
    landRequirement: string;
    carbonIntensity: string;
    operatingModel: string;
    regulatoryPathway: string;
    siteSpecificFactors: string;
    financialStructure: string;
    recommendation: string;
    viabilityTier?: 'primary' | 'alternative' | 'disqualified' | 'not-applicable';
    intentAlignment?: string;
  }[];
  gridVsBtmComparison?: {
    metric: string;
    gridOption: string;
    btmOption: string;
    advantage: string;
  }[];
  lcoeDetailedAnalysis?: {
    technology: string;
    capitalCostPerMW: string;
    fixedOM: string;
    variableOM: string;
    fuelCost: string;
    capacityFactor: string;
    lcoeResult: string;
    siteAdjustments: string;
  }[];

  // ═══ Supply Chain Risk sections ═══
  supplyChainAnalysis?: SupplyChainAnalysis;

  // ═══ Water screen (feasibility, Phase 3 part B) / Water Resource Assessment (retired) ═══
  // The feasibility report carries the SCREENING subset (supply, groundwater &
  // surface water, rights, drought, recommendations, risks); a retired Water
  // Resource report carries every field. The three below are therefore
  // OPTIONAL: absent on a feasibility water screen, present on legacy water
  // reports. Renderers and emitters already gate on truthiness.
  /** Complete deterministic SED water screen, frozen with the generated report. */
  waterScreening?: string;
  waterScreeningBasis?: {
    sedId: string;
    sedVersion: number;
    economicContentHash: string;
    modelInputHash: string;
    modelVersion: string;
    sourceCatalogVersion: string;
    /** Auxiliary frozen climate identity; never the persisted water-model revision. */
    dryCoolingClimateSummaryHash?: string;
    dryCoolingTmyDatasetHash?: string;
  };
  /**
   * Reader projection of `waterScreening`, built by the same formatter so every
   * figure is the string the saved text prints. Present on reports generated
   * from 2026-09-18; older reports render `waterScreening` line by line. Audit,
   * guard and DOCX/PPTX surfaces read `waterScreening`, never this view.
   */
  waterScreeningView?: WaterScreeningView;
  waterAnalysis?: {
    supplyAvailability: string;
    groundwaterAssessment: string;
    surfaceWaterAssessment: string;
    waterRightsAnalysis: string;
    droughtResilience: string;
    coolingWaterRequirements?: string;
    wastewaterManagement?: string;
    waterConservation?: string;
    recommendations: string[];
    risks: import('../lib/findingClaim').FindingClaim[];
    /**
     * OPTIONAL consumptive-use / withdrawal permitting gate (the prompt also
     * folds this content into `waterRightsAnalysis` + `coolingWaterRequirements`).
     * The doctrine/permit/volume/curtailment gate renders in WaterRightsSection;
     * `dryCoolingPueTradeoff` renders in CoolingByTechSection. Optional +
     * absent-safe (not in the schema `required` set).
     */
    consumptiveUse?: ConsumptiveUseGate;
  };

  // ═══ Test Fit / Site Layout sections ═══
  testFitAnalysis?: {
    siteConstraints: string;
    buildingPlacement: string;
    setbackCompliance: string;
    parkingAccess: string;
    stormwaterDetention: string;
    utilityRouting: string;
    dataHallOrientation: string;
    phaseLayout: string;
    recommendations: string[];
    /** Deterministically replaced from the frozen SED authority at save time. */
    generationFootprint?: {
      chosenTechnology: string;
      generationNameplateMW: string;
      generationPlantAcres: string;
      coolingFootprintAcres: string;
      substationFootprintAcres: string;
      ccsFootprintAcres: string;
      compressionFootprintAcres: string;
      totalGenerationAcres: string;
      parcelAreaAcres: string;
      netBuildableAcres: string;
      footprintMarginAcres: string;
      representedFootprintAcres: string;
      overlappingFootprintAcres: string;
      outsideBoundaryAcres: string;
      acreageFitVerdict: import('./generationImpact').EngineeringVerdict;
      spatialFitVerdict: import('./generationImpact').EngineeringVerdict;
      spatialFitScope: 'generation-layout-only';
      basis: string;
    };
  };

  // RTU-3 (row 0.99235) — two test-fit canonical fields the pre-RTU-3
  // schema omitted. Authority: AGC of America Constructability Review
  // (constructabilityAssessment) + RSMeans / Mortenson / Turner
  // programme benchmarks (costRangeEstimate). Each is a free-text
  // narrative guarded by SNT-1 (instruction-shape) + SNT-5
  // (data-grounded fallback composer in
  // src/lib/sectionFallbacks/testFit.ts).
  constructabilityAssessment?: string;
  costRangeEstimate?: string;
  /** BW5-5 — annual-yield + LCOE synthesis section (conditional). */
  bopEconomics?: string;
  /** R-131-FIN-DECISION-K — pinned pathway comparison snapshot at generation. */
  pathwayComparisonAtGen?: import('./pathwayComparison').PathwayComparisonAtGen;
  /** C3 — BTM sizing ceiling narrative (sizing math, not action items). */
  maxCampusCapacity?: string;

  // ═══ TRACK-B — structured finance/feasibility snapshots persisted at gen ═══
  /**
   * TRACK-B — site-wide firm-capacity (ELCC) adequacy snapshot at generation,
   * from `summarizeIntentFeasibility`. Stage 3 ingestion reads this. New
   * snapshots carry one matched `phaseComparison` from the first worst-verdict
   * phase, not independent minima or the ultimate site load. Old snapshots
   * without it cannot support a phase-specific coverage comparison.
   * Planning-default ELCC only, NOT a site-specific study.
   */
  firmCapacityVerdict?: {
    worstVerdict: 'feasible' | 'marginal' | 'infeasible' | null;
    firmDeliverableMW: number | null;
    reservePct: number;
    assessedCount: number;
    infeasibleCount: number;
    marginalCount: number;
    worstPhaseLabel: string | null;
    /** Optional only for legacy persisted reports. All operands share one assessment. */
    phaseComparison?: {
      source: 'phase-feasibility-v1';
      phaseId: string;
      phaseLabel: string;
      verdict: import('../lib/phaseFeasibility').PhaseFeasibilityVerdict;
      assessmentMessage: string;
      firmDeliverableMW: number;
      requiredMW: number;
      requiredFirmMW: number;
      reservePct: number;
      loadBasis: import('../lib/phaseFeasibility').PhaseFeasibilityLoadBasis;
      loadBasisLabel: string;
      loadRange: import('../lib/phaseFeasibility').PhaseFeasibility['loadRange'];
      sources: Array<Pick<import('../lib/phaseFeasibility').PhaseFeasibilityPerSource,
        'component' | 'capacityBasis' | 'capacityRange'>>;
    };
  };
  /**
   * TRACK-B — corrected per-tech WACC-discounted LCOE for the chosen / compared
   * technologies at generation (USD/MWh). Keyed by `TechnologyId` finance
   * preset id. From `computePathwayLcoe` over the intent-faithful pathways —
   * the single LCOE source of truth, NOT re-derived. Stage 3 ingestion reads
   * this. Each entry carries the nominal + real LCOE so downstream consumers
   * pick the basis they need.
   */
  pathwayLcoeAtGen?: Array<{
    pathwayId: string;
    pathwayName: string;
    technology: string;
    capacityMW: number;
    lcoeNominalPerMwhUsd: number;
    lcoeRealPerMwhUsd: number;
    /**
     * Sim P2.12-S1 — provenance of the annual-energy basis behind this LCOE:
     * `'simulated'` when a real EO annual-yield run supplied the climate-derated
     * annualMwh, `'assumed-cf'` when the capacity-factor proxy was used. Optional
     * for backward-compatibility with pre-P2.12 persisted snapshots.
     */
    yieldSource?: 'simulated' | 'assumed-cf';
    currencyYear?: number;
    currencyBasis?: 'real' | 'nominal';
    subsidyStatus?: 'unsubsidized' | 'subsidized';
    firmingBasis?: '24/7-load-sized' | 'plant-level-model';
    serviceScope?: 'served-load' | 'busbar';
  }>;

  // ═══ Public Sentiment Report sections (PSR) ═══
  executive?: string;
  stakeholderMap?: string;
  perBtmSentiment?: string;
  themeDeepDives?: string;
  trendAnalysis?: string;
  comparableSiteBenchmark?: string;
  mitigationPlaybook?: string;
  /** PSR appendix narrative (distinct from feasibility `appendix[]`). */
  sentimentAppendix?: string;
  /** PSR-2 SENTIMENT INDEX 0-100 verdict — not a feasibility category score. */
  sentimentIndex?: {
    score: number;
    band: 'supportive' | 'mixed' | 'opposed' | string;
    topDrivers?: string[];
  };
  /** Deterministic PSE snapshot stamped at generation — cover KPIs + extended data.
   *  New reports may also carry the additive `psr-meta-v2` projection
   *  (`src/lib/sentiment/publicSentimentMetaV2Contract.ts`); read it only via
   *  `readPublicSentimentMetaV2`, which treats persisted `null` as absent. */
  publicSentimentMeta?: Partial<import('../lib/sentiment/publicSentimentMetaV2Contract').PublicSentimentMetaV2> & {
    sourceLeads?: import('./siteEvidence').PublicSentimentSourceLead[];
    assessment?: import('../lib/sentiment/publicSentimentScope').SentimentAssessment;
    queryContext?: import('../lib/sentiment/publicSentimentScope').SentimentQueryContext;
    change?: { addedObservationCount: number; comparable: boolean; supportIndexDelta?: number };
    entryCount: number;
    sourcesAttempted: number;
    opposedTechCount: number;
    status?: 'ok' | 'partial' | 'unavailable';
    /** Per-geographic-layer rollup (site / state / country): entry count +
     *  mean sentiment per layer, so the report presents the discourse at
     *  each scope rather than collapsing them. Omitted on legacy reports
     *  generated before the three-layer scan. */
    perScope?: Array<{
      scope: 'site' | 'state' | 'country';
      count: number;
      meanSentiment: number;
      meanConfidence?: number;
    }>;
  };
  /** D2/R-76 — set when degraded fallback cannot populate distinct PSR sections. */
  publicSentimentStatus?: 'unavailable';
  publicSentimentUnavailableReason?: string;

  /** R-131-EPISTEMIC-GROUNDING-D — default contract-review negation scope for this report. */
  contractReviewEpistemicScope?: import('../lib/contractReviewEpistemic').ContractReviewEpistemicScope;
  /** R-131-EPISTEMIC-GROUNDING-D — per-section contract-review scope overrides (e.g. customBriefQ_*). */
  contractReviewEpistemicScopesBySection?: Record<
    string,
    import('../lib/contractReviewEpistemic').ContractReviewEpistemicScope
  >;

  // ═══ Custom Brief (user-defined diligence) ═══
  customBriefName?: string;
  customBriefObjective?: string;
  customBriefContextNotes?: string;
  customBriefCounterparties?: string;
  customBriefKeyDates?: string;
  customBriefAnswers?: CustomBriefAnswer[];
  /** Structured exec summary (v2); legacy reports use `executiveSummary` string. */
  customBriefExecutiveSummary?: CustomBriefExecutiveSummary;
  customBriefProjectContext?: CustomBriefProjectContext;
  customBriefConclusion?: CustomBriefConclusion;

  // ═══ Executive Summary synthesis report ═══
  executiveSummarySourcesUsed?: ExecutiveSummarySourceUsed[];
  btmViabilityAssessment?: string;
  crossReportSynthesis?: CrossReportDomainBlock[];
  /** R-131-EPISTEMIC-GROUNDING-C — critical risks carry epistemic evidence state. */
  executiveSummaryCriticalRisks?: {
    risk: string;
    severity: 'high' | 'medium' | 'low';
    mitigation?: string;
    evidenceState?: import('../lib/findingClaim').FindingEvidenceState;
    openQuestion?: string;
    evidence?: import('../lib/findingClaim').FindingClaimEvidence;
  }[];

  // ═══ Financial Viability Report (FIN-1, wf w7nuyquwd) ═══
  /**
   * Compact deterministic stamp persisted on create / recompute (NO LLM) so
   * cross-report aggregators (the rollup worst-source floor, BTM map, overview
   * cards via getDisplayVerdict) can read the financial headline + verdict
   * WITHOUT re-running the project-finance engine. Built by
   * `buildFinancialAnalysis` from `projectFinance.recompute` +
   * `computeAllPathwayCompareMetrics` + `runComparisonMonteCarlo` +
   * `computeSensitivity`. The report body is rendered LIVE from this struct
   * (read-only renderers; no Firestore writes in report view).
   */
  financialAnalysis?: FinancialAnalysisStamp;

  // ═══ Interconnection Feasibility sections ═══
  interconnectionAnalysis?: {
    fiberAvailability: string;
    latencyModeling: string;
    carrierDiversity: string;
    ixProximity: string;
    submarineCable: string;
    networkResilience: string;
    edgeDeployment: string;
    recommendations: string[];
    risks: string[];
  };

  // Go/No-Go Conclusion
  conclusion?: {
    recommendation: ConclusionRecommendation;
    overallScore: number;
    summary?: string;
    topStrengths: string[];
    topRisks: string[];
    topConstraints?: string[];
    /**
     * LLM-curated headline opportunities — same shape as `topStrengths`
     * but written under the "opportunities" framing some report types
     * prefer (e.g. environmental → "Top opportunities"). FS1 fallback
     * chain uses this when `topStrengths` is empty.
     */
    keyOpportunities?: string[];
    environmentalVerdict?: string;
    keyConditions: string[];
    nextActions: string[];
    /** Structured BTM path recommendation synthesized in conclusion. */
    btmDecisionSummary?: string;
    insightModifier?: number;
    modifierJustification?: string;
  };

  /** Preserved user-saved decisions stamped onto the report snapshot. */
  preservedUserDecisions?: Array<{
    parameter?: string;
    value?: string;
    status?: string;
    source?: string;
    appliedAt?: string;
  }>;

  /** Top-level BTM decision summary (feasibility, custom-brief, public-sentiment). */
  btmDecisionSummary?: string;

  // Sensitivity analysis — how much each category drives the conclusion
  scoreDrivers?: {
    category: string;
    currentScore: number;
    weight: number;
    impact: number;       // points contributed to overall score
    ifImproved: number;   // overall score if this category scored 85 (green)
    ifWorsened: number;   // overall score if this category scored 25 (red)
  }[];

  // User-added custom sections (title + content, reorderable)
  customSections?: { id: string; title: string; content: string }[];

  /**
   * Per-figure alt text + caption overrides, keyed by document id.
   * The DOCX/PDF renderers consume this in gatherFigureImages —
   * present overrides win over the auto-derived "stem of filename"
   * fallback. Useful when the filename is opaque (`IMG_2451.jpg`)
   * but the figure has a clear meaning the editor wants to surface.
   *
   *   alt: screen-reader-only text. Drives the docx ImageRun.altText
   *        and the in-product <img alt=…>.
   *   caption: visible "Figure N: …" line under the embedded image.
   *
   * Both fields are optional; an entry without either is treated
   * as if absent.
   */
  figureCaptions?: Record<string, { alt?: string; caption?: string }>;

  // Heading overrides — maps original heading key to user-renamed text
  headingOverrides?: Record<string, string>;

  // Edit history for learning
  editHistory?: EditRecord[];

  /**
   * PH-A2 #8: previously typed required, but absent on Firestore-fallback
   * records (`store.repairIncompleteReports` already filters on
   * `!r.datasets`; metadata-only saves in `siteRagService` write only
   * the headline fields when GCS save fails). Consumers must guard
   * (`report.datasets?.climate?.avgTemp`) — TS will enforce.
   */
  datasets?: {
    climate: ClimateData;
    grid: GridData;
    land: LandData;
    infrastructure: InfrastructureData;
    seismic?: SeismicData;
    solar?: SolarData;
    wind?: WindData;
    environmental?: EnvironmentalData;
    electricity?: ElectricityData;
    airQuality?: AirQualityData;
    connectivity?: ConnectivityData;
    parcel?: ParcelData;
    nrcReactors?: {
      nearestReactorName: string;
      nearestReactorDistKm: number;
      /**
       * @deprecated R-29: misleading — this is daily power percentage alias (0-100%),
       * NOT MW nameplate. Kept for back-compat. New readers use
       * `dailyPower` + `nearestReactorNameplateMw`.
       */
      nearestReactorPower: number;
      /** R-29: daily power percentage alias 0-100%, -1 = unknown. */
      nearestReactorCapacityFactorPct?: number;
      dailyPower?: NrcDailyPowerSummary;
      powerStatusFeed?: { latestReportDate: string | null; invalidRows: number; unmappedUnits: string[]; provenance?: import('../lib/nrcNationalFeed').NrcNationalFeed['provenance'] };
      /** R-29: site-aggregated static catalog nameplate capacity in MW. */
      nearestReactorNameplateMw?: number;
      reactorsWithin200km: number;
      reactorList: {
        name: string;
        distKm: number;
        /** @deprecated R-29: daily power percentage alias, not capacity factor or MW. */
        power: number;
        capacityFactorPct?: number;
        dailyPower?: NrcDailyPowerSummary;
        nameplateMw?: number;
        units?: number;
      }[];
      nuclearInfraMaturity: 'High' | 'Moderate' | 'Low';
    };
    siteResearch?: import('../services/dataProviders/siteResearch').SiteResearchData;
  };

  createdAt: string;
  /** Last durable post-generation mutation (editorial edits/operator answers). */
  updatedAt?: string;

  // ═══ Versioning ═══
  version?: number;               // Major version: 1, 2, 3 (reruns increment)
  minorVersion?: number;          // Minor version: 0, 1, 2 (editorial edits increment)
  parentReportId?: string;        // Links to previous version's report ID
  versionNote?: string;           // User note explaining what changed in this version
  rerunConfig?: {                 // Preserved config for re-running this report
    projectIntent?: ProjectIntent;
    selectedProviders?: string[];
    siteBoundary?: { lat: number; lng: number }[];
    siteArea?: number;
    customWeights?: Record<string, number>;
    /** Per-type wizard answers (customerCategory, projectSizeMW, etc.) so a
     *  rerun can prefill the form instead of resetting to defaults. */
    typedPreferences?: TypedPreferencesByType;
    /** User's curated document selection at generation time. Preserved so a
     *  rerun starts from the same evidence set instead of falling back to a
     *  category-driven auto-pick. */
    documentRefs?: Array<{
      docId: string;
      relevance: number;
      userIncluded: boolean;
      reasonIfExcluded?: string;
      includedAt?: string;
      includedBy?: string;
    }>;
    /** Reader profile override that framed the original prompt. Without
     *  this snapshot a rerun would silently re-derive the override from the
     *  current company default and produce different framing than the
     *  parent. */
    companyArchetypeOverride?: import('./company').CompanyArchetype;
  };

  // ═══ Site Reference ═══
  siteId?: string;               // Links report to parent SiteEntity

  // ═══ Company Sharing ═══
  isCompanyShared?: boolean;
  companyId?: string;

  /**
   * #owner-path-sweep (part-2) — TRANSIENT in-memory annotation stamped
   * by `fetchRagReports` from the collectionGroup doc-ref path segment
   * when the report lives under ANOTHER user's uid (company-shared report
   * whose Firestore doc lives at `users/{ownerUid}/reports/{id}`).
   *
   * NEVER persisted — `stripTransientReportAnnotations` must be called
   * on every write payload. Own-user reports never carry this field;
   * `reportOwnerUid` falls back to the current uid in that case so
   * every writer is byte-identical to the pre-sweep path for own reports.
   */
  _ownerUid?: string;

  /**
   * Render-only V2 branding derived from a hash-verified immutable
   * PresentationSnapshot. Never persisted and never resolved from session,
   * tenant, template, or site state by an immutable artifact renderer.
   */
  _frozenBranding?: import('../lib/reportBranding').ResolvedReportBranding;

  // ═══ Layout ═══
  templateId?: string;
  /**
   * Cover layout stamped on the report (gen-time assignment / Studio
   * pick). For template-linked reports this is a SNAPSHOT that the live
   * template's `coverLayout` normally wins over (see resolveCoverLayout).
   * Kept OPTIONAL so legacy reports stay byte-stable.
   */
  coverLayout?: import('./reportTemplate').CoverLayout;
  /**
   * Gen-time cover-layout SNAPSHOT that survives the
   * template-linked-layout strip. When a report links a template whose
   * row cannot be resolved this session (still hydrating, private, or
   * cross-org), `resolveCoverLayout` falls back to this snapshot instead
   * of defaulting to Hero — so an assigned Masthead/Classic cover is not
   * silently lost. Stamped when a template is assigned (the strip moves
   * the about-to-be-deleted `coverLayout` here). Legacy reports without
   * the field fall through to today's behaviour.
   */
  coverLayoutAtGen?: import('./reportTemplate').CoverLayout;
  /**
   * TSE-2 (C12) — snapshot of `ReportTemplate.version` at generation
   * time. Lets the Apply-to-existing-reports flow show "Update from v3
   * → v4?" diffs and lets a viewer detect "this template has been
   * updated since the report rendered". Legacy reports without this
   * field are treated as v1.
   */
  templateVersionUsed?: number;
  sectionOrder?: string[];
  sectionVisibility?: Record<string, boolean>;
  /** AI-generated bodies for template custom sections (keyed by responseField or id). */
  sectionContent?: Record<string, string>;
  sectionColumnLayouts?: Record<string, 1 | 2 | 3>;

  /**
   * TX-2 (TPN-FOLLOWUP-PER-REPORT-PAGE-MAP-OVERRIDE, 2026-05-19) —
   * optional per-report page-map overrides. Today's `resolvePageMap`
   * projection uses template-wide settings; this field lets a single
   * report opt out of the template's page-break decisions for a
   * specific section (e.g. "this report wants the Risk Matrix to
   * start on a pinned page even though the template uses auto").
   *
   * Shape: `{ [sectionId]: { startMode?, pagePin? } }`. Both fields
   * inside the override are optional; missing fields leave the
   * template-stamped value unchanged. Absent keys leave the template
   * projection untouched.
   *
   * Reproducibility guarantee (HARD-RULE 9): archived reports without
   * `pageMapOverrides` stay byte-identical — `resolvePageMap` checks
   * for `undefined`/empty and short-circuits to the existing code
   * path. The override is consumed by the resolver via
   * `ResolvePageMapOptions.pageMapOverrides`.
   *
   * Pre-TX-2 reports omit the field; UI must guard.
   */
  pageMapOverrides?: Record<string, import('../lib/templatePages/resolvePageMap').PageMapOverride>;

  /**
   * (report-ai-design-pass · Phase 1) — optional AI LAYOUT/DESIGN plan: a
   * bounded set of DESIGN DECISIONS (cover treatment, per-section columns / flow
   * / page-breaks / exhibit placement / editorial emphasis) emitted by a
   * rule-constrained Gemini pass AFTER content is grounded. It carries NO prose,
   * numbers, or citations (the type is structurally incapable of mutating
   * content) and is read as an OVERRIDE FLOOR — it wins over blind engine
   * defaults but yields to explicit user/template choices.
   *
   * Reproducibility guarantee: archived reports without `layoutPlan` stay
   * byte-identical — the consuming resolvers (Phase 3) short-circuit to today's
   * values when the field is absent, and the pass itself is default-OFF behind a
   * runtime flag (Phase 2). Rides the existing full-object GCS write with zero
   * save-code change.
   */
  layoutPlan?: import('./reportLayoutPlan').ReportLayoutPlan;

  /**
   * Immutable V2 document/artifact pointer. The continuous Report View reads
   * the semantic body; any PDF pointer here is the exact validated byte stream
   * used only for artifact preview, print and download. Optional for all
   * legacy reports.
   */
  reportRevision?: import('./reportContractsV2').ReportRevisionReferenceV1;
  reportRevisionHistory?: Array<import('./reportContractsV2').ReportRevisionReferenceV1>;
  screeningRecommendationSetV1?: import('../lib/findings/screeningArchitectureRecommendations').ScreeningRecommendationSetV1;
  /** Independent printable delivery outcome; missing on legacy on-demand PDFs. */
  pdfDelivery?: import('./reportPdfDelivery').ReportPdfDelivery;
  /**
   * Immutable web-first content identity for generated products outside the
   * GO decision-analysis domain (currently Custom Brief).
   */
  reportContentRevision?: import('./reportContent').ReportContentRevisionReferenceV1;
  reportContentRevisionId?: string;
  reportContentInputHash?: string;
  reportContentHash?: string;
  reportContentReadiness?: unknown;
  reportContentState?: string;
  /** Exact source input on the immutable Brief body only. The plan resolves
   * live saved authority from this snapshot; metadata never substitutes for it. */
  reportContentInputAtGen?: import('./reportContent').ReportContentInputV1;
  /**
   * Presentation-neutral, immutable decision-analysis snapshot and its small
   * current-pointer/readiness envelope. New analysis-backed revisions carry
   * both; legacy reports remain readable without them.
   */
  decisionAnalysis?: import('./decisionAnalysis').DecisionAnalysisSnapshotV1;
  analysisRevision?: import('./decisionAnalysis').AnalysisRevisionReferenceV1;
  analysisRevisionId?: string;
  analysisInputHash?: string;
  analysisContentHash?: string;
  analysisReadiness?: import('./decisionAnalysis').AnalysisReadinessDecisionV1;
  analysisState?: string;
  analysisRevisions?: Array<import('./decisionAnalysis').AnalysisRevisionReferenceV1> | Record<string, unknown>;

  // ═══ Evidence-First Pipeline ═══
  sedId?: string;
  sedVersion?: number;
  reportRevisionId?: string;
  storagePath?: string;
  appliedCorrectionsSummary?: {
    sourceReportId?: string;
    evaluatedAt?: string;
    requestedCount?: number;
    appliedCount?: number;
    resolvedCount: number;
    stillFailingCount?: number;
    persistingCount?: number;
    supersededCount?: number;
    items: Array<{
      identity: string;
      section: string;
      category?: string;
      finding: string;
      directive?: string;
      appliedDirective?: string;
      status: 'resolved' | 'still-failing' | 'persisting' | 'superseded-by-evidence' | 'superseded';
      remainingReason?: string;
      supersededByEvidence?: string;
      changedSourceFields?: string[];
      conflictReason?: string;
    }>;
  };
  /** Snapshot of SiteEntity.sedVersion at generation time. Lets the report
   *  show "generated against SED v3" even if the site SED has since rebuilt
   *  to v4. */
  sedVersionUsed?: number;
  /** Versioned canonical SED source dependency projection at generation.
   * New reports compare these material fields against the current full SED;
   * absent legacy manifests retain their original conservative currency gate. */
  sedMaterialDependenciesAtGen?: import('./decisionAnalysis').ReportSedMaterialDependencyManifestV1;
  /** Exact immutable Generation & Impact scenario used by this report. */
  generationImpactScenarioAtGen?: import('./generationImpact').GenerationImpactScenarioSnapshot;
  /** Purpose-specific deterministic projection derived from that frozen snapshot. */
  generationImpactProjection?: import('./generationImpact').GenerationImpactReportProjection;
  /**
   * Compact Generation & Impact authority tuple used by an on-site Test Fit.
   * Legacy and explicitly grid-only reports omit it. Consumers compare this
   * immutable identity to the live site/SED tuple before treating the report
   * as current decision evidence.
   */
  testFitAuthorityRef?: import('./generationImpact').TestFitAuthorityRef;
  /**
   * Pinned Datacrate Model version that produced this report's
   * domain context. `null` on legacy reports generated before DAM
   * existed; non-null on every report post-cutover. Paired with
   * `damReportTypeUsed` so the exact (reportType, version) tuple is
   * recoverable from `loadDamVersion`.
   */
  damVersionUsed?: number | null;
  /** The DAM `reportType` this report's domain context was loaded
   *  from. Matches `reportType` in normal use; pinned here so a future
   *  multi-template flow (one DAM serving multiple report types) can
   *  still recover the exact source. */
  damReportTypeUsed?: import('./datacrateModel').ReportType | null;
  /**
   * PL2 — the `reportType` that `applyDocAwareConfidence` was applied
   * with at gen time. Differs from `reportType` only on legacy reports
   * produced before PL2 shipped (where doc-aware confidence ran at
   * bootstrap with a hard-coded 'feasibility' default). Present on
   * every post-PL2 report so the Quality / Provenance panels can
   * surface "doc-aware confidence scored against {reportType} relevance".
   */
  docAwareReportType?: import('../config/reportTypes').ReportTypeId;
  sedText?: string;
  sedCompleteness?: number;
  sedIntegrity?: number;
  sedGaps?: string[];
  /**
   * RV pack, RV6 — full DataGap[] snapshot taken at generation time
   * from the SED that produced this report. Carries severity +
   * provider id + recommendation, which the "What this report does
   * not know" appendix subsection enumerates. Optional for back-compat
   * with reports generated before RV6 shipped — the renderer treats
   * absent ⇒ empty and falls back to the legacy `sedGaps: string[]`
   * shape when populated.
   */
  dataGaps?: import('./siteEvidence').DataGap[];
  sedAnomalies?: string[];
  /**
   * Site_Context SC2 — snapshot of `site.siteContext` at generation
   * time. Lets a prior report stay reproducible even after the user
   * later edits the site's context. Optional — undefined for reports
   * generated before SC1 shipped or for sites that never had a
   * site-context entered. Consumers should treat absent ⇒ empty.
   */
  siteContextSnapshot?: import('./siteContext').SiteContext;
  /** Durable post-generation operator answers overlaid on immutable findings. */
  openQuestionResponses?: import('../lib/findingClaim').OpenQuestionResponse[];
  factCheck?: {
    completion?: import('../services/reportFactCheck').FactCheckCompletion;
    verified: number;
    unverified: number;
    contradictions: string[];
    score: number;
  };
  structuredFindings?: {
    title: string;
    detail: string;
    source: string;
    impact: string;
    pageReference?: number;
  }[];
  /** Project Intent / Overview narrative prose (replaces key-findings bullets in section). */
  projectIntentOverview?: string;
  /**
   * C0-GEN — Editorial-narrative layer (Option B).
   *
   * OPTIONAL, generated, NON-AUTHORITATIVE re-voicing of already-verified
   * section content into flowing monograph prose. Behind the
   * `EDITORIAL_NARRATIVE` / `VITE_EDITORIAL_NARRATIVE` flag (default OFF);
   * absent on every flag-OFF / legacy report. The render NEVER depends on
   * this field existing — every monograph block resolves through
   * `resolveEditorialLayer(report, sectionId)`, which falls back to a
   * deterministic Option-A re-compose (`composeEditorialFallback`) that
   * returns the SAME verified strings the legacy renderers display. So a
   * report without this key renders byte-identically to today.
   *
   * Stored on the persisted report (GCS + Firestore) so view + print/export
   * read byte-identically from the archived snapshot.
   *
   * WAVE 1 (this commit): schema + flag + resolver + deterministic fallback
   * + grounding validators only. No generator writes this field yet, so it
   * is ALWAYS absent — the resolver always returns the fallback.
   */
  editorialNarrative?: {
    /** ISO timestamp the editorial pass ran. */
    generatedAt: string;
    /** Model id that produced the layer (telemetry / reproducibility). */
    modelUsed: string;
    /**
     * Per-section editorial layers, keyed by section id plus a `'cover'`
     * pseudo-section. A section absent from this map resolves to its
     * deterministic fallback.
     */
    sections: Record<string, EditorialSectionLayer>;
  };
  /** Context-aware key findings rendered in Feasibility Profile (and exec-summary fallback). */
  contextualKeyFindings?: ContextualKeyFinding[];
  qualityReview?: {
    score: number;          // 0-100 overall
    accuracy: number;       // 0-100 data correctness
    citations: number;      // 0-100 citation quality
    completeness: number;   // 0-100 section coverage
    issueCount: number;
    verdict: 'pass' | 'needs-revision';
    /**
     * REVIEW-OBS-1 (2026-07-03) — the reviewer's issue texts, bounded to 10.
     * Previously only `issueCount` survived, so a needs-revision hold said
     * "5 issue(s)" with no way to see WHAT the reviewer objected to (the
     * live Redhead regen). Each entry: section + issue summary (correction
     * text omitted — it was already applied or rejected by the loop).
     */
    issues?: Array<{ section: string; issue: string }>;
    /** REVIEW-OBS-2 — count of pre-repair issues dropped because the repair passes resolved them. */
    staleIssuesDropped?: number;
  };
}

export interface SiteRagState {
  reports: SiteRagReport[];
  activeReportId: string | null;
  comparisonIds: string[];
  isLoading: boolean;
  progress: number;
  progressStage: string;
  error: string | null;
  isDemoMode: boolean;

  // Site-centric state
  sites: import('./site').SiteEntity[];
  activeSiteId: string | null;
  isSitesLoaded: boolean;
  /**
   * Flips to `true` when fetchRagReports has resolved at least once. Lives
   * next to `isSitesLoaded` so dashboard / comparison surfaces can gate
   * their empty-state copy on real data, not on a transient `[]`.
   */
  isReportsLoaded: boolean;
  /** Authenticated library rows are available; semantic body readiness stays separate. */
  isReportMetadataLoaded?: boolean;
  /**
   * Sites whose current-head report bodies were read this session (or whose
   * read attempt settled). The list pass marks the open site once its heads
   * are in and every listed site once the background warm-up finishes;
   * `ensureSiteReportBodies` marks a site it read on demand. The site page
   * and the portfolio staleness checks treat a site as judgeable only when it
   * is here or has nothing left to read. Rebuilt per session — never restored
   * from a persisted workspace blob.
   */
  hydratedReportSiteIds?: string[];
  /**
   * Concurrent report-generation runs keyed by run id.
   *
   * Used to allow multiple in-flight generations at once while keeping
   * legacy `isLoading/progress/progressStage` fields backward-compatible
   * for existing UI surfaces.
   */
  activeGenerations?: Record<string, {
    id: string;
    /** Firestore job id when async reload-safe generation is enabled. */
    jobId?: string;
    siteId: string;
    reportTypeId: string;
    startedAt: string;
    progress: number;
    progressStage: string;
    /** Server-owned approximate runtime baseline used by the live countdown. */
    estimatedDurationMs?: number;
    // The server liveness beat (`heartbeatAt` on the job doc) is deliberately
    // NOT a field of the run. The runner refreshes it on EVERY write and on a
    // 30s ticker, so carrying it here rebuilt this record — and everything
    // memoised on it — every 30s for a value only the stall indicators read.
    // It lives in the root store's `generationHeartbeats`, keyed by this run's
    // `id`; subscribe with `useGenerationHeartbeat(run.id)`.
    /**
     * Public Sentiment only: the run is held client-side (no job, no POST yet)
     * until a running site-evidence build finishes, then dispatched
     * automatically. Its `progressStage` is `SENTIMENT_EVIDENCE_WAIT_STAGE`.
     * In-memory only — closing the page drops it without recording an outcome.
     */
    waitingForSiteEvidence?: boolean;
  }>;
  /**
   * Settled generation outcomes keyed by run id. Used by wizard/report
   * UI surfaces to read the status of a specific run even when other
   * runs are active concurrently.
   */
  generationOutcomes?: Record<string, {
    id: string;
    jobId?: string;
    siteId: string;
    reportTypeId: string;
    finishedAt: string;
    /** Terminal job state. Optional only for outcomes written by older clients. */
    status?: 'succeeded' | 'failed' | 'cancelled';
    error?: string;
    errorCode?: string;
    terminalReason?: string;
    blockedByProviderCoverage?: boolean;
    reportId?: string;
    /** R-65 — structured intent/SED mismatch when dispatch was blocked. */
    intentSedMismatch?: import('../lib/audit/intentSedReconciler').IntentSedReconciliationResult;
    /** R-71 — structured provider coverage block when dispatch was blocked. */
    providerCoverage?: import('../lib/audit/providerCoverageGate').ProviderCoverageReport;
    /** R-3 — citation density gate block payload. */
    citationDensity?: import('../lib/audit/citationDensityGate').CitationDensitySnapshot;
    /** R-3 — regen-with-focus already consumed for this outcome. */
    citationDensityRegenUsed?: boolean;
    /** R-7 — site state missing when conflict detection was required. */
    siteStateRequired?: boolean;
  }>;
  /**
   * Public Sentiment runs NOT dispatched because the site's evidence needs a
   * rebuild (or kept changing). Keyed by the held run id; at most one per site.
   * A hold is deliberately NOT a generation outcome: it never renders as
   * "Failed" / "Generation Failed". Cleared by dismissal or a new attempt.
   */
  sentimentEvidenceHolds?: Record<string, import('../lib/sentiment/sentimentEvidenceReadiness').SentimentEvidenceHold>;
  /** R-71 — last provider coverage gate result when generation was blocked. */
  lastProviderCoverageBlock?: import('../lib/audit/providerCoverageGate').ProviderCoverageReport | null;
  /** R-3 — last citation density gate block (wizard banner). */
  lastCitationDensityBlock?: import('../lib/audit/citationDensityGate').CitationDensitySnapshot | null;
  /** R-3 — regen-with-focus used for the last density block. */
  lastCitationDensityRegenUsed?: boolean;
  /** R-71 — per-site last coverage gate by report type (wizard/audit cache). */
  lastCoverageGate?: Record<string, Partial<Record<import('../config/reportTypes').ReportTypeId, import('../lib/audit/providerCoverageGate').ProviderCoverageReport>>>;
  /** R-65 — last intent/SED reconciliation block (wizard banner). */
  lastIntentSedMismatch?: import('../lib/audit/intentSedReconciler').IntentSedReconciliationResult | null;
}
